import { ConfigService } from '@produce8/pegasus';
import * as Sentry from '@sentry/browser';
import { DEFAULT_USER_COUNT_VALIDATION_VALUE } from '../src/common/constants/validation.constants';
import {
  GuardvalidateUserCount,
  UserCountInDateRangeProps,
} from '../src/common/decorators/validation-guard.decorator';
import { WithSentryClassDecorator } from '../src/common/decorators/with-sentry-class.decorator';
import { StaticEnvironmentKeysEnum } from '../src/common/enum/static-environment/static-environment-keys.enum';
import { InternalServerErrorException } from '../src/common/exceptions/exceptions';
import { DiLogManager } from '../src/common/port/log/di-log.manager.port';
import { LogManager } from '../src/common/port/log/log.manager.port';
import { AnalyticsActivityPort } from '../src/core/application/ports/analytics-activity/analytics-activity.port';
import { AnalyticsEventsPort } from '../src/core/application/ports/analytics-events/analytics-events.port';
import { OrganizationAnalyticsEventsPort } from '../src/core/application/ports/analytics-events/organization-analytics-events.port';
import { TeamAnalyticsEventsPort } from '../src/core/application/ports/analytics-events/team-analytics-events.port';
import { ChallengeTargetResultPort } from '../src/core/application/ports/challenge-target-result/challenge-target-result.port';
import { OrgChallengeTargetResultPort } from '../src/core/application/ports/challenge-target-result/org-challenge-target-result.port';
import { OrgAnalyticsActivityPort } from '../src/core/application/ports/org-analytics-activity/org-analytics-activity.port';
import { OrgSentimentReportPort } from '../src/core/application/ports/org-sentiment-report/org-sentiment-report.port';
import { SentimentReportPort } from '../src/core/application/ports/sentiment/sentiment-report.port';
import { TeamAnalyticsActivityPort } from '../src/core/application/ports/team-analytics-activity/team-analytics-activity.port';
import { OrgWorkingDaysPort } from '../src/core/application/ports/working-days/org-working-days.port';
import { OrganizationWorkingSessionsPort } from '../src/core/application/ports/working-sessions/organization-working-sessions.port';
import { WorkingSessionsPort } from '../src/core/application/ports/working-sessions/working-sessions.port';
import { GetAccountTotalAppCategoriesSummaryApplicationService } from '../src/core/application/services/app-category-metrics/account-total-app-categories-summary.application-service';
import { GetAccountAppCategoriesSummaryApplicationService } from '../src/core/application/services/app-category-metrics/get-account-app-categories-summary.application-service';
import { GetAccountAppCategoriesTimeSeriesApplicationService } from '../src/core/application/services/app-category-metrics/get-account-app-categories-time-series.application-service';
import { GetSelfAppCategoryChartAllTimeAverageApplicationService } from '../src/core/application/services/app-category-metrics/get-self-app-category-chart-all-time-average.application-service';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryApplicationService } from '../src/core/application/services/app-category-metrics/get-self-app-category-score-time-series-metrics-summary.application-service';
import { GetSelfAppCategoryTimeSeriesApplicationService } from '../src/core/application/services/app-category-metrics/get-self-app-category-time-series.application-service';
import { GetSelfTopAppCategoriesApplicationService } from '../src/core/application/services/app-category-metrics/get-self-top-app-categories.application-service';
import { GetSelfTopAppCategoryAppsApplicationService } from '../src/core/application/services/app-category-metrics/get-self-top-app-category-apps.application-service';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesApplicationService } from '../src/core/application/services/app-category-metrics/get-workspace-category-metrics-usage-per-individual-time-series.application-service';
import { GetWorkspaceCategoryMetricsUsageTotalsApplicationService } from '../src/core/application/services/app-category-metrics/get-workspace-category-metrics-usage-totals.application-service';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesApplicationService } from '../src/core/application/services/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.application-service';
import { GetCalendarAppChartAllTimeAveragesService } from '../src/core/application/services/calendar/all-time-averages/get-calendar-app-chart-all-time-averages.service';
import { GetCalendarChartAllTimeAveragesService } from '../src/core/application/services/calendar/all-time-averages/get-calendar-chart-all-time-averages.service';
import { GetSelfCalendarScoreTimeSeriesMetricsSummaryService } from '../src/core/application/services/calendar/score/metrics/get-self-calendar-score-time-series-metrics-summary.service';
import { GetSelfCalendarAppTimeSeriesWeekdayService } from '../src/core/application/services/calendar/score/time-series/get-self-calendar-app-time-series-weekday.service';
import { GetSelfCalendarAppTimeSeriesService } from '../src/core/application/services/calendar/score/time-series/get-self-calendar-app-time-series.service';
import { GetSelfCalendarScoreTimeSeriesWeekdayService } from '../src/core/application/services/calendar/score/time-series/get-self-calendar-score-time-series-weekday.service';
import { GetSelfCalendarScoreTimeSeriesService } from '../src/core/application/services/calendar/score/time-series/get-self-calendar-score-time-series.service';
import { GetCalendarAppTimeSeriesMetricsSummaryService } from '../src/core/application/services/calendar/time-series/get-calendar-app-time-series-metrics-summary.service';
import { GetAccountMetricsInsightDigitalIntensityAverageApplicationService } from '../src/core/application/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-average.application-service';
import { GetAccountMetricsInsightDigitalIntensityChartDataApplicationService } from '../src/core/application/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-chart-data.application-service';
import { GetAccountMetricsInsightDigitalIntensityTotalApplicationService } from '../src/core/application/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-total.application-service';
import { ListAccountMetricsInsightDigitalIntensitySummaryApplicationService } from '../src/core/application/services/challenge-insights/digital-intensity/list-account-metrics-insight-digital-intensity-summary.application-service';
import { GetAccountMetricsInsightDigitalWorkingHoursAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-digital-working-hours-average.application-service';
import { GetAccountMetricsInsightDigitalWorkingHoursChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-digital-working-hours-chart-data.application-service';
import { GetAccountMetricsInsightDigitalWorkingHoursTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-digital-working-hours-total.application-service';
import { GetAccountMetricsInsightFocusV1AverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-focus-v1-average.application-service';
import { GetAccountMetricsInsightFocusV1ChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-focus-v1-chart-data.application-service';
import { GetAccountMetricsInsightFocusV1TotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-focus-v1-total.application-service';
import { GetAccountMetricsInsightSentimentAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-sentiment-average.application-service';
import { GetAccountMetricsInsightSentimentChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-sentiment-chart-data.application-service';
import { GetAccountMetricsInsightSentimentTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-sentiment-total.application-service';
import { GetAccountMetricsInsightTimeInAppsAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-apps-average.application-service';
import { GetAccountMetricsInsightTimeInAppsChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-apps-chart-data.application-service';
import { GetAccountMetricsInsightTimeInAppsTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-apps-total.application-service';
import { GetAccountMetricsInsightTimeInCallsAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-calls-average.application-service';
import { GetAccountMetricsInsightTimeInCallsChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-calls-chart-data.application-service';
import { GetAccountMetricsInsightTimeInCallsTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-calls-total.application-service';
import { GetAccountMetricsInsightTimeInCategoriesAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-categories-average.application-service';
import { GetAccountMetricsInsightTimeInCategoriesChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-categories-chart-data.application-service';
import { GetAccountMetricsInsightTimeInCategoriesTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-categories-total.application-service';
import { GetAccountMetricsInsightTimeInMeetingsAverageApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-meetings-average.application-service';
import { GetAccountMetricsInsightTimeInMeetingsChartDataApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-meetings-chart-data.application-service';
import { GetAccountMetricsInsightTimeInMeetingsTotalApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insight-time-in-meetings-total.application-service';
import { GetAccountMetricsInsightsDigitalIntensitySummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-digital-intensity-summary.application-service';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.application-service';
import { GetAccountMetricsInsightsFocusV1SummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-focus-v1-summary.application-service';
import { GetAccountMetricsInsightsSentimentSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-sentiment-summary.application-service';
import { GetAccountMetricsInsightsTimeInAppSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-time-in-app-summary.application-service';
import { GetAccountMetricsInsightsTimeInCallsSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-time-in-calls-summary.application-service';
import { GetAccountMetricsInsightsTimeInCategorySummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-time-in-category-summary.application-service';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.application-service';
import { GetChallengeInsightProgressChallengeTargetSuccessApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-challenge-target-success.application-service';
import { GetChallengeInsightProgressDigitalIntensitySummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-digital-intensity-summary.application-service';
import { GetChallengeInsightProgressDigitalWorkingHoursSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-digital-working-hours-summary.application-service';
import { GetChallengeInsightProgressFocusV1SummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-focus-v1-summary.application-service';
import { GetChallengeInsightProgressSelfReportSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-self-report-summary.application-service';
import { GetChallengeInsightProgressSentimentSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-summary-summary.application-service';
import { GetChallengeInsightProgressTimeInAppSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-time-in-app-summary.application-service';
import { GetChallengeInsightProgressTimeInCallsSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-time-in-calls-summary.application-service';
import { GetChallengeInsightProgressTimeInCategorySummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-time-in-category-summary.application-service';
import { GetChallengeInsightProgressTimeInMeetingsSummaryApplicationService } from '../src/core/application/services/challenge-insights/get-challenge-insight-progress-time-in-meetings-summary.application-service';
import { GetSelfMetricsInsightsChallengeTargetStreakApplicationService } from '../src/core/application/services/challenge-insights/get-self-metrics-insights-challenge-target-streak.application-service';
import { ListAccountMetricsInsightDigitalWorkingHoursSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-digital-working-hours-summary.application-service';
import { ListAccountMetricsInsightFocusV1SummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-focus-v1-summary.application-service';
import { ListAccountMetricsInsightSentimentSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-sentiment-summary.application-service';
import { ListAccountMetricsInsightTimeInAppSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-time-in-app-summary.application-service';
import { ListAccountMetricsInsightTimeInCallsSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-time-in-calls-summary.application-service';
import { ListAccountMetricsInsightTimeInCategoriesSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-time-in-categories-summary.application-service';
import { ListAccountMetricsInsightTimeInMeetingsSummaryApplicationService } from '../src/core/application/services/challenge-insights/list-account-metrics-insight-time-in-meetings-summary.application-service';
import { GetAccountMetricsInsightSelfReportAverageApplicationService } from '../src/core/application/services/challenge-insights/self-report/get-account-metrics-insight-self-report-average.application-service';
import { GetAccountMetricsInsightSelfReportChartDataApplicationService } from '../src/core/application/services/challenge-insights/self-report/get-account-metrics-insight-self-report-chart-data.application-service';
import { GetAccountMetricsInsightSelfReportTotalApplicationService } from '../src/core/application/services/challenge-insights/self-report/get-account-metrics-insight-self-report-total.application-service';
import { GetSelfCalendarTimeInAppGoalService } from '../src/core/application/services/goals/get-self-calendar-time-in-app-goal.service';
import { GetSelfCallTimeInAppGoalService } from '../src/core/application/services/goals/get-self-call-time-in-app-goal.service';
import { GetSelfDigitalIntensityScoreGoalService } from '../src/core/application/services/goals/get-self-digital-intensity-score-goal.service';
import { GetSelfDigitalWorkingHoursScoreGoalService } from '../src/core/application/services/goals/get-self-digital-working-hours-score-goal.service';
import { GetSelfFocusScoreGoalService } from '../src/core/application/services/goals/get-self-focus-score-goal.service';
import { GetSelfInteractionsGoalService } from '../src/core/application/services/goals/get-self-interactions-goal.service';
import { GetSelfScreentimeScoreGoalService } from '../src/core/application/services/goals/get-self-screentime-score-goal.service';
import { GetSelfTimeInAppGoalService } from '../src/core/application/services/goals/get-self-time-in-app-goal.service';
import { GetSelfTimeInCallsScoreGoalService } from '../src/core/application/services/goals/get-self-time-in-calls-score-goal.service';
import { GetSelfTimeInMeetingsScoreGoalApplicationService } from '../src/core/application/services/goals/get-self-time-in-meetings-score-goal.application-service';
import { GetAccountAppCategoryMetricsPerAppApplicationService } from '../src/core/application/services/org-metrics/apps/get-account-app-category-metrics-per-app.application-service';
import { GetAccountAverageCallsPerPersonSummaryApplicationService } from '../src/core/application/services/org-metrics/calls/get-account-average-calls-per-person-summary.application-service';
import { GetAccountAverageCallsPerWorkspaceTimeseriesApplicationService } from '../src/core/application/services/org-metrics/calls/get-account-average-calls-per-workspace-time-series.application-service';
import { GetAccountCallEventsTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/calls/get-account-call-events-time-series.application-service';
import { GetAccountCallsPerWorkspaceSummaryApplicationService } from '../src/core/application/services/org-metrics/calls/get-account-calls-per-workspace-summary.application-service';
import { GetAccountCallsSummaryApplicationService } from '../src/core/application/services/org-metrics/calls/get-account-calls-summary.application-service';
import { GetAccountAppTotalsPerCategorySummaryApplicationService } from '../src/core/application/services/org-metrics/category/get-account-app-totals-per-category-summary.application-service';
import { GetAccountAverageMeetingsPerPersonSummaryApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-average-meetings-per-person-summary.application-service';
import { GetAccountMeetingEventsSummaryApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meeting-events-summary.application-service';
import { GetAccountMeetingEventsTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meeting-events-time-series.application-service';
import { GetAccountMeetingHoursBySizeSummaryApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meeting-hours-by-size-summary.application-service';
import { GetAccountMeetingHoursBySizeTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.application-service';
import { GetAccountMeetingsPerWorkspaceSummaryApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meetings-per-workspace-summary.application-service';
import { GetAccountMeetingsPerWorkspaceTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/meetings/get-account-meetings-per-workspace-time-series.application-service';
import { GetAccountAverageWorkingHoursPerPersonRangesApplicationService } from '../src/core/application/services/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.application-service';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.application-service';
import { GetAccountWorkingHoursAveragePerPersonDistributionApplicationService } from '../src/core/application/services/org-metrics/working-hours/get-account-working-hours-average-per-person-distribution.application-service';
import { GetAccountWorkingHoursAverageSummaryApplicationService } from '../src/core/application/services/org-metrics/working-hours/get-account-working-hours-average-summary.application-service';
import { GetAccountWorkingHoursAverageTimeSeriesApplicationService } from '../src/core/application/services/org-metrics/working-hours/get-account-working-hours-average-time-series.application-service';
import { GetSelfAppAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-app-averages-summary.application-service';
import { GetSelfAppCalendarEventsAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-app-calendar-events-averages-summary.application-service';
import { GetSelfCalendarScoreApplicationService } from '../src/core/application/services/scores/get-self-calendar-score.application-service';
import { GetSelfDigitalIntensityScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-digital-intensity-score-averages-summary.application-service';
import { GetSelfDigitalIntensityScoreService } from '../src/core/application/services/scores/get-self-digital-intensity-score.service';
import { GetSelfDigitalWorkingHoursScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-digital-working-hours-score-averages-summary.application-service';
import { GetSelfDigitalWorkingHoursScoreService } from '../src/core/application/services/scores/get-self-digital-working-hours-score.service';
import { GetSelfFocusScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-focus-score-averages-summary.application-service';
import { GetSelfFocusScoreService } from '../src/core/application/services/scores/get-self-focus-score.service';
import { GetSelfScreentimeScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-screentime-score-averages-summary.application-services';
import { GetSelfScreentimeScoreService } from '../src/core/application/services/scores/get-self-screentime-score.service';
import { GetSelfTimeInCallsScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-time-in-calls-score-averages-summary.application-service';
import { GetSelfTimeInCallsScoreService } from '../src/core/application/services/scores/get-self-time-in-calls-score.service';
import { GetSelfTimeInMeetingsScoreAveragesSummaryApplicationService } from '../src/core/application/services/scores/get-self-time-in-meetings-score-averages-summary.application-service';
import { GetAccountSentimentReportAverageSummaryApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-average-summary.application-service';
import { GetAccountSentimentReportAverageTimeSeriesApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-average-time-series.application-service';
import { GetAccountSentimentReportAverageTotalsApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-average-totals.application-service';
import { GetAccountSentimentReportPercentilesAverageApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-percentiles-average.application-service';
import { GetAccountSentimentReportPercentilesSummaryApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-percentiles-summary.application-service';
import { GetAccountSentimentReportTimeSeriesApplicationService } from '../src/core/application/services/sentiment/get-account-sentiment-report-time-series.application-service';
import { GetSelfSentimentReportTimeSeriesApplicationService } from '../src/core/application/services/sentiment/get-self-sentiment-report-time-series.application-service';
import { GetSelfSentimentReportApplicationService } from '../src/core/application/services/sentiment/get-self-sentiment-report.application-service';
import { GetTeamMetricsAppsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-apps-summary-average-whole-team.application-service';
import { GetTeamMetricsAppsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-apps-summary-total-individual.application-service';
import { GetTeamMetricsAppsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-apps-summary-total-whole-team.application-service';
import { GetTeamMetricsCalendarAppTimeSeriesTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-individual.application-service';
import { GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-whole-team.application-service';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.application-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-average-whole-team.application-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-individual.application-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.application-service';
import { GetTeamMetricsCalendarMeetingsSummaryIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-meetings-summary-individual.application-service';
import { GetTeamMetricsCalendarMeetingsSummaryApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.application-service';
import { GetTeamMetricsCalendarViewsSummaryApplicationService } from '../src/core/application/services/team-metrics/calendar/get-team-metrics-calendar-views-summary.application-service';
import { GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-average-whole-team.application-service';
import { GetTeamMetricsCalendarScoreTimeSeriesIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-individual.application-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-average-whole-team.application-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-total-individual.application-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-total-whole-team.application-service';
import { GetTeamMetricsCalendarScoreTimeSeriesApplicationService } from '../src/core/application/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series.application-service';
import { GetTeamMetricsCallsEventsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calls/get-team-metrics-calls-events-summary-average-whole-team.application-service';
import { GetTeamMetricsCallsEventsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/calls/get-team-metrics-calls-events-summary-total-individual.application-service';
import { GetTeamMetricsCallsEventsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/calls/get-team-metrics-calls-events-summary-total-whole-team.application-service';
import { GetCategoryMetricsAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/category/get-category-metrics-average-whole-team.application-service';
import { GetCategoryMetricsTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/category/get-category-metrics-total-individual.application-service';
import { GetCategoryMetricsTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/category/get-category-metrics-total-whole-team.application-service';
import { GetSelfAppCategoryAveragesSummaryApplicationService } from '../src/core/application/services/team-metrics/category/get-self-app-category-averages-summary.application-service';
import { GetAppMetricsAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/get-app-metrics-average-whole-team.application-service';
import { GetAppMetricsTimeSeriesAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/get-app-metrics-time-series-average-whole-team.application-service';
import { GetAppMetricsTimeSeriesTotalIndividualService } from '../src/core/application/services/team-metrics/get-app-metrics-time-series-total-individual.service';
import { GetAppMetricsTimeSeriesTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/get-app-metrics-time-series-total-whole-team.application-service';
import { GetAppMetricsTotalIndividualService } from '../src/core/application/services/team-metrics/get-app-metrics-total-individual.service';
import { GetAppMetricsTotalWholeTeamService } from '../src/core/application/services/team-metrics/get-app-metrics-total-whole-team.service';
import { GetTeamMetricsMeetingsEventSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-average-whole-team.application-service';
import { GetTeamMetricsMeetingsEventSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-individual.application-service';
import { GetTeamMetricsMeetingsEventSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-whole-team.application-service';
import { GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-average-whole-team.application-service';
import { GetTeamSubscriptionMetricsAppsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-individual.application-service';
import { GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-whole-team.application-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-average-whole-team.application-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-individual.application-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-whole-team.application-service';
import { GetAppMetricsAverageSummaryWholeTeamApplicationService } from '../src/core/application/services/team-metrics/summary/get-app-metrics-average-summary-whole-team.application-service';
import { GetAppMetricsTotalSummaryIndividualService } from '../src/core/application/services/team-metrics/summary/get-app-metrics-total-summary-individual.service';
import { GetAppMetricsTotalSummaryWholeTeamService } from '../src/core/application/services/team-metrics/summary/get-app-metrics-total-summary-whole-team.service';
import { GetTeamMetricsAppViewsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-app-views-summary-average-whole-team.application-service';
import { GetTeamMetricsAppViewsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-app-views-summary-total-individual.application-service';
import { GetTeamMetricsAppViewsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-app-views-summary-total-whole-team.application-service';
import { GetTeamMetricsMeetingsSummaryAverageWholeTeamApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-meetings-summary-average-whole-team.application-service';
import { GetTeamMetricsMeetingsSummaryTotalIndividualApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-meetings-summary-total-individual.application-service';
import { GetTeamMetricsMeetingsSummaryTotalWholeTeamApplicationService } from '../src/core/application/services/team-metrics/summary/get-team-metrics-meetings-summary-total-whole-team.application-service';
import { GetAccountAppCategoriesPerWorkspaceSummaryApplicationService } from '../src/core/application/services/top-apps/get-account-app-categories-per-workspace-summary.application-service';
import { GetAccountAppMetricsPerWorkspaceSummaryApplicationService } from '../src/core/application/services/top-apps/get-account-app-metrics-per-workspace-summary.application-service';
import { GetAccountTopAppsMetricsSummaryApplicationService } from '../src/core/application/services/top-apps/get-account-top-apps-metrics-summary.application-service';
import { GetSelfWorkingSessionsApplicationService } from '../src/core/application/services/working-sessions/get-self-working-sessions.application-service';
import { GetAccountAppCategoriesSummaryDomainService } from '../src/core/domain/services/app-category-metrics/get-account-app-categories-summary.domain-service';
import { GetAccountAppCategoriesTimeSeriesDomainService } from '../src/core/domain/services/app-category-metrics/get-account-app-categories-time-series.domain-service';
import { GetAccountTotalAppCategoriesSummaryDomainService } from '../src/core/domain/services/app-category-metrics/get-account-total-app-categories-summary.domain-service';
import { GetSelfAppCategoryTimeSeriesDomainService } from '../src/core/domain/services/app-category-metrics/get-self-app-category-time-series.domain-service';
import { GetSelfTopAppCategoriesDomainService } from '../src/core/domain/services/app-category-metrics/get-self-top-app-categories.domain-service';
import { GetSelfTopAppCategoryAppsDomainService } from '../src/core/domain/services/app-category-metrics/get-self-top-app-category-apps.domain-service';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesDomainService } from '../src/core/domain/services/app-category-metrics/get-workspace-category-metrics-per-individual-time-series.domain-service';
import { GetWorkspaceCategoryMetricsUsageTotalsDomainService } from '../src/core/domain/services/app-category-metrics/get-workspace-category-metrics-usage-totals.domain-service';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesDomainService } from '../src/core/domain/services/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.domain-service';
import { GetAccountMetricsInsightDigitalWorkingHoursAverageDomainService } from '../src/core/domain/services/challenge-insights/averages/get-account-metrics-insight-digital-working-hour-average.domain-service';
import { GetAccountMetricsInsightDigitalWorkingHoursTotalDomainService } from '../src/core/domain/services/challenge-insights/averages/get-account-metrics-insight-digital-working-hour-total.domain-service';
import { GetAccountMetricsInsightDigitalWorkingHoursChartDataDomainService } from '../src/core/domain/services/challenge-insights/averages/get-account-metrics-insight-digital-working-hours-chart-data.domain-service';
import { ListAccountMetricsInsightDigitalWorkingHoursSummaryDomainService } from '../src/core/domain/services/challenge-insights/averages/list-account-metrics-insight-digital-working-hours-summary.domain-service';
import { GetAccountMetricsInsightDigitalIntensityAverageDomainService } from '../src/core/domain/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-average.domain-service';
import { GetAccountMetricsInsightDigitalIntensityChartDataDomainService } from '../src/core/domain/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-chart-data.domain-service';
import { GetAccountMetricsInsightDigitalIntensityTotalDomainService } from '../src/core/domain/services/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-total.domain-service';
import { ListAccountMetricsInsightDigitalIntensitySummaryDomainService } from '../src/core/domain/services/challenge-insights/digital-intensity/list-account-metrics-insight-digital-intensity-summary.domain-service';
import { GetAccountMetricsInsightFocusV1AverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-focus-v1-average.domain-service';
import { GetAccountMetricsInsightFocusV1ChartDataDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-focus-v1-chart-data.domain-service';
import { GetAccountMetricsInsightFocusV1TotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-focus-v1-total.domain-service';
import { GetAccountMetricsInsightSentimentAverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-sentiment-average.domain-service';
import { GetAccountMetricsInsightSentimentChartDataDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-sentiment-chart-data.domain-service';
import { GetAccountMetricsInsightSentimentTotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-sentiment-total.domain-service';
import { GetAccountMetricsInsightTimeInAppsAverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-apps-average.domain-service';
import { GetAccountMetricsInsightTimeInAppsTotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-apps-total.domain-service';
import { GetAccountMetricsInsightTimeInCallsAverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-calls-average.domain-service';
import { GetAccountMetricsInsightTimeInCallsChartDataDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-calls-chart-data.domain-service';
import { GetAccountMetricsInsightTimeInCallsTotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-calls-total.domain-service';
import { GetAccountMetricsInsightTimeInCategoriesAverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-categories-average.domain-service';
import { GetAccountMetricsInsightTimeInCategoriesTotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-categories-total.domain-service';
import { GetAccountMetricsInsightTimeInMeetingsAverageDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-meetings-average.domain-service';
import { GetAccountMetricsInsightTimeInMeetingsChartDataDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-meetings-chart-data.domain-service';
import { GetAccountMetricsInsightTimeInMeetingsTotalDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insight-time-in-meetings-total.domain-service';
import { GetAccountMetricsInsightsDigitalIntensitySummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-digital-intensity-summary.domain-service';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.domain-service';
import { GetAccountMetricsInsightsFocusV1SummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-focus-v1-summary.domain-service';
import { GetAccountMetricsInsightsSentimentSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-sentiment-summary.domain-service';
import { GetAccountMetricsInsightsTimeInAppSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-time-in-app-summary.domain-service';
import { GetAccountMetricsInsightTimeInCallsSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-time-in-calls-summary.domain-service';
import { GetAccountMetricsInsightsTimeInCategorySummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-time-in-category-summary.domain-service';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.domain-service';
import { GetChallengeInsightProgressChallengeTargetSuccessDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-challenge-target-success.domain-service';
import { GetChallengeInsightProgressDigitalIntensitySummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-digital-intensity-summary.domain-service';
import { GetChallengeInsightProgressDigitalWorkingHoursSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-digital-working-hours-summary.domain-service';
import { GetChallengeInsightProgressFocusV1SummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-focus-v1-summary.domain-service';
import { GetChallengeInsightProgressSentimentSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-sentiment-summary.domain-service';
import { GetChallengeInsightProgressTimeInAppSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-time-in-app-summary.domain-service';
import { GetChallengeInsightProgressTimeInCallsSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-time-in-calls-summary.domain-service';
import { GetChallengeInsightProgressTimeInCategorySummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-time-in-category-summary.domain-service';
import { GetChallengeInsightProgressTimeInMeetingsSummaryDomainService } from '../src/core/domain/services/challenge-insights/get-challenge-insight-progress-time-in-meetings-summary.domain-service';
import { GetSelfMetricsInsightsChallengeTargetStreakDomainService } from '../src/core/domain/services/challenge-insights/get-self-metrics-insights-challenge-target-streak.domain-service';
import { ListAccountMetricsInsightFocusV1SummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-focus-v1-summary.domain-service';
import { ListAccountMetricsInsightSentimentSummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-sentiment-summary.domain-service';
import { ListAccountMetricsInsightTimeInAppsSummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-time-in-apps-summary.domain-service';
import { ListAccountMetricsInsightTimeInCallsSummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-time-in-calls-summary.domain-service';
import { ListAccountMetricsInsightTimeInCategorySummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-time-in-category-summary.domain-service';
import { ListAccountMetricsInsightTimeInMeetingsSummaryDomainService } from '../src/core/domain/services/challenge-insights/list-account-metrics-insight-time-in-meetings-summary.domain-service';
import { GetAccountMetricsInsightSelfReportChartDataDomainService } from '../src/core/domain/services/challenge-insights/self-report/get-account-metrics-insight-digital-intensity-chart-data.domain-service';
import { GetAccountMetricsInsightSelfReportAverageDomainService } from '../src/core/domain/services/challenge-insights/self-report/get-account-metrics-insight-self-report-average.domain-service';
import { GetAccountMetricsInsightSelfReportTotalDomainService } from '../src/core/domain/services/challenge-insights/self-report/get-account-metrics-insight-self-report-total.domain-service';
import { GetChallengeInsightProgressSelfReportSummaryDomainService } from '../src/core/domain/services/challenge-insights/self-report/get-challenge-insight-progress-self-report-summary.domain-service';
import { GetAccountMetricsInsightTimeInAppsChartDataDomainService } from '../src/core/domain/services/challenge-insights/time-in-apps/get-account-metrics-insight-time-in-apps-chart-data.domain-service';
import { GetSelfTimeInMeetingsScoreGoalDomainService } from '../src/core/domain/services/goals/get-self-time-in-meetings-goal.domain-service';
import { GetAccountAppCategoryMetricsPerAppDomainService } from '../src/core/domain/services/org-metrics/apps/get-account-app-category-metrics-per-app.domain-service';
import { ExpandAccountCallEventsTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/calls/expand-account-events-calls-time-series.domain-service';
import { GetAccountAverageCallsPerPersonSummaryDomainService } from '../src/core/domain/services/org-metrics/calls/get-account-average-calls-per-person-summary.domain-service';
import { GetAccountAverageCallsPerWorkspaceTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/calls/get-account-average-calls-per-workspace-time-series.domain-service';
import { GetAccountCallsPerWorkspaceSummaryDomainService } from '../src/core/domain/services/org-metrics/calls/get-account-calls-per-workspace.summary.domain-service';
import { GetAccountCallsSummaryDomainService } from '../src/core/domain/services/org-metrics/calls/get-account-calls-summary.domain-service';
import { GetAccountEventsCallsTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/calls/get-account-events-calls-time-series.domain-service';
import { GetAccountAppTotalsPerCategorySummaryDomainService } from '../src/core/domain/services/org-metrics/category/get-account-app-totals-per-category-summary.domain-service';
import { GetAccountAverageMeetingsPerPersonSummaryDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-average-meetings-per-person-summary.domain-service';
import { GetAccountEventsMeetingsTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-events-meetings-time-series.domain-service';
import { GetAccountMeetingEventsSummaryDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-meeting-events-summary.domain-service';
import { GetAccountMeetingHoursBySizeSummaryDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-meeting-hours-by-size-summary.domain-service';
import { GetAccountMeetingHoursBySizeTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.domain-service';
import { GetAccountMeetingsPerWorkspaceSummaryDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-meetings-per-workspace-summary.domain-service';
import { GetAccountMeetingsPerWorkspaceTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/meetings/get-account-meetings-per-workspace-time-series.domain-service';
import { GetAccountAverageWorkingHoursPerPersonRangesDomainService } from '../src/core/domain/services/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.domain-service';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.domain-service';
import { GetAccountWorkingHoursAveragePerPersonDistributionDomainService } from '../src/core/domain/services/org-metrics/working-hours/get-account-working-hours-average-per-person-distribution.domain-service';
import { GetAccountWorkingHoursAverageSummaryDomainService } from '../src/core/domain/services/org-metrics/working-hours/get-account-working-hours-average-summary.domain-service';
import { GetAccountWorkingHoursAverageTimeSeriesDomainService } from '../src/core/domain/services/org-metrics/working-hours/get-account-working-hours-average-time-series.domain-service';
import { GetSelfAppAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-app-averages-summary.domain-service';
import { GetSelfAppCalendarEventsAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-app-calendar-events-averages-summary.domain-service';
import { GetSelfCalendarScoreDomainService } from '../src/core/domain/services/scores/get-self-calendar-score.domain-service';
import { GetSelfDigitalIntensityScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-digital-intensity-score-averages-summary.domain-service';
import { GetSelfDigitalWorkingHoursScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-digital-working-hours-score-averages-summary.domain-service';
import { GetSelfFocusScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-foucs-score-averages-summary.domain-service';
import { GetSelfScreentimeScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-screentime-score-averages-summary.domain-service';
import { GetSelfTimeInCallsScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-time-in-calls-score-averages-summary.domain-service';
import { GetSelfTimeInMeetingsScoreAveragesSummaryDomainService } from '../src/core/domain/services/scores/get-self-time-in-meetings-score-averages-summary.domain-service';
import { GetAccountSentimentReportAverageSummaryDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-average-summary.domain-service';
import { GetAccountSentimentReportAverageTimeSeriesDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-average-time-series.domain-service';
import { GetAccountSentimentReportAverageTotalsDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-average-totals.domain-service';
import { GetAccountSentimentReportPercentilesAverageDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-percentiles-average.domain-service';
import { GetAccountSentimentReportPercentilesSummaryDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-percentiles-summary.domain-service';
import { GetAccountSentimentReportTimeSeriesDomainService } from '../src/core/domain/services/sentiment/get-account-sentiment-report-time-series.domain-service';
import { GetSelfSentimentReportTimeSeriesDomainService } from '../src/core/domain/services/sentiment/get-self-sentiment-report-time-series.domain-service';
import { GetTeamMetricsAppsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-apps-summary-average-whole-team.domain-service';
import { GetTeamMetricsAppsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-apps-summary-total-individual.domain-service';
import { GetTeamMetricsAppsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-apps-summary-total-whole-team.domain-service';
import { GetTeamMetricsCalendarAppTimeSeriesTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-individual.domain-service';
import { GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-whole-team.domain-service';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.domain-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-average-whole-team.domain-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-individual.domain-service';
import { GetTeamMetricsCalendarChartAllTimeAveragesDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.domain-service';
import { GetTeamMetricsCalendarMeetingsSummaryIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-meetings-summary-individual.domain-service';
import { GetTeamMetricsCalendarMeetingsSummaryDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.domain-service';
import { GetTeamMetricsCalendarViewsSummaryDomainService } from '../src/core/domain/services/team-metrics/calendar/get-team-metrics-calendar-views-summary.domain-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-average-whole-team.domain-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-total-individual.domain-service';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-total-whole-team.domain-service';
import { GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series-average-whole-team.domain-service';
import { GetTeamMetricsCalendarScoreTimeSeriesIndividualDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series-individual.domain-service';
import { GetTeamMetricsCalendarScoreTimeSeriesDomainService } from '../src/core/domain/services/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series.domain-service';
import { GetTeamMetricsCallsEventsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calls/get-team-metrics-calls-events-summary-average-whole-team.domain-service';
import { GetTeamMetricsCallsEventsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/calls/get-team-metrics-calls-events-summary-total-individual.domain-service';
import { GetTeamMetricsCallsEventsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/calls/get-team-metrics-calls-events-summary-total-whole-team.domain-service';
import { GetCategoryMetricsAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/category/get-category-metrics-average-whole-team.domain-service';
import { GetCategoryMetricsTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/category/get-category-metrics-total-individual.domain-service';
import { GetCategoryMetricsTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/category/get-category-metrics-total-whole-team.domain-service';
import { GetSelfAppCategoryAveragesSummaryDomainService } from '../src/core/domain/services/team-metrics/category/get-self-app-category-averages-summary.domain-service';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryDomainService } from '../src/core/domain/services/team-metrics/category/get-self-app-category-score-time-series-metrics-summary.domain-service';
import { GetAppMetricsAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/get-app-metrics-average-whole-team.domain-service';
import { GetAppMetricsTimeSeriesAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/get-app-metrics-time-series-average-whole-team.domain-service';
import { GetAppMetricsTimeSeriesTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/get-app-metrics-time-series-total-whole-team.domain-service';
import { GetTeamMetricsMeetingsEventSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-average-whole-team.domain-service';
import { GetTeamMetricsMeetingsEventSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-individual.domain-service';
import { GetTeamMetricsMeetingsEventSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-whole-team.domain-service';
import { GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-average-whole-team.domain-service';
import { GetTeamSubscriptionMetricsAppsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-individual.domain-service';
import { GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-whole-team.domain-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-average-whole-team.domain-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-individual.domain-service';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-whole-team.domain-service';
import { GetAppMetricsAverageSummaryWholeTeamDomainService } from '../src/core/domain/services/team-metrics/summary/get-app-metrics-average-summary-whole-team.domain-service';
import { GetTeamMetricsAppViewsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-app-views-summary-average-whole-team.domain-service';
import { GetTeamMetricsAppViewsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-app-views-summary-total-individual.domain-service';
import { GetTeamMetricsAppViewsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-app-views-summary-total-whole-team.domain-service';
import { GetTeamMetricsMeetingsSummaryAverageWholeTeamDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-meetings-summary-average-whole-team.domain-service';
import { GetTeamMetricsMeetingsSummaryTotalIndividualDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-meetings-summary-total-individual.domain-service';
import { GetTeamMetricsMeetingsSummaryTotalWholeTeamDomainService } from '../src/core/domain/services/team-metrics/summary/get-team-metrics-meetings-summary-total-whole-team.domain-service';
import { GetAccountAppCategoriesPerWorkspaceSummaryDomainService } from '../src/core/domain/services/top-apps/get-account-app-categories-per-workspace-summary.domain-service';
import { GetAccountAppMetricsPerWorkspaceSummaryDomainService } from '../src/core/domain/services/top-apps/get-account-apps-metrics-per-workspace-summary.domain-service';
import { GetAccountTopAppsMetricsSummaryDomainService } from '../src/core/domain/services/top-apps/get-account-top-apps-metrics-summary.domain-service';
import { CubeAdapter } from '../src/infrastructure/adapters/cube/cube.adapter';
import { DiCubeAdapter } from '../src/infrastructure/adapters/cube/di-cube.adapter';
import { StaticEnvironmentAdapter } from '../src/infrastructure/adapters/environment/static-environment.adapter';
import { AccountAppCategoriesSummaryAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/account-app-categories-summary.aggregate-root.mapper';
import { AccountAppCategoriesTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/account-app-categories-time-series.aggregate-root.mapper';
import { AccountTotalAppCategoriesSummaryAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/account-total-app-categories-summary.aggregate-root.mapper';
import { GetSelfAppCategoryChartAllTimeAverageAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/get-self-app-category-chart-all-time-average.aggregate-root.mapper';
import { SelfAppCategoryScoreTimeSeriesMetricsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/get-self-app-category-score-time-series-metrics-summary.aggregate-root.mapper';
import { SelfAppCategoryTimeSeriesEntityMapper } from '../src/infrastructure/mappers/app-category-metrics/self-app-category-time-series.entity.mapper';
import { SelfTopAppCategoriesAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/self-top-app-categories.aggregate-root.mapper';
import { SelfTopAppCategoryAppsAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/self-top-app-category-apps.aggregate-root.mapper';
import { WorkspaceCategoryMetricsUsagePerIndividualTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/workspace-category-metrics-usage-per-individual-time-series.aggregate-root.mapper';
import { WorkspaceCategoryMetricsUsageTotalsAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/workspace-category-metrics-usage-totals.aggregate-root.mapper';
import { WorkspaceCategoryMetricsUsageWholeTeamTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/app-category-metrics/workspace-category-metrics-usage-whole-team-time-series.aggregate-root.mapper';
import { CalendarChartAllTimeAveragesAggregateRootMapper } from '../src/infrastructure/mappers/calendar/all-time-averages/calendar-chart-all-time-averages-aggregate-root.mapper';
import { CalendarChartAllTimeAveragesMapper } from '../src/infrastructure/mappers/calendar/all-time-averages/calendar-chart-all-time-averages.mapper';
import { SelfCalendarScoreTimeSeriesMetricsSummaryMapper } from '../src/infrastructure/mappers/calendar/score/metrics/self-calendar-score-time-series-metrics-summary.mapper';
import { SelfCalendarScoreTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/calendar/score/time-series/self-calendar-score-time-series-aggregate-root.mapper';
import { SelfCalendarScoreTimeSeriesMapper } from '../src/infrastructure/mappers/calendar/score/time-series/self-calendar-score-time-series.mapper';
import { CalendarAppTimeSeriesMetricsSummaryMapper } from '../src/infrastructure/mappers/calendar/time-series/get-calendar-score-time-series-summary-entity.mapper';
import { CalendarTimeSeriesMetricsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/calendar/time-series/get-calendar-time-series-metrics-summary-aggregate-root.mapper';
import { AccountMetricsInsightsDigitalIntensitySummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-digital-intensity-summary.entity.mapper';
import { AccountMetricsInsightsDigitalWorkingHoursSummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-digital-working-hours-summary.entity.mapper';
import { AccountMetricsInsightsFocusV1SummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-focus-v1-summary.entity.mapper';
import { AccountMetricsInsightsSentimentSummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-sentiment-summary.entity.mapper';
import { AccountMetricsInsightsTimeInAppSummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-time-in-app-summary.entity.mapper';
import { AccountMetricsInsightsTimeInCallsSummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-time-in-calls-summary.entity.mapper';
import { AccountMetricsInsightsTimeInCategorySummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-time-in-category-summary.entity.mapper';
import { AccountMetricsInsightsTimeInMeetingSummaryEntityMapper } from '../src/infrastructure/mappers/challenge-insights/account-metrics-insights-time-in-meetings-summary.entity.mapper';
import { SelfChallengeTargetStreakMapper } from '../src/infrastructure/mappers/challenge-target-result/self-challenge-target-streak.mapper';
import { SelfCalendarTimeInAppGoalMapper } from '../src/infrastructure/mappers/goals/self-calendar-time-in-app-goal.mapper';
import { SelfCallTimeInAppGoalMapper } from '../src/infrastructure/mappers/goals/self-call-time-in-app-goal.mapper';
import { SelfDigitalIntensityScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-digital-intensity-score-goal.mapper';
import { SelfDigitalWorkingHoursScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-digital-working-hours-score-goal.mapper';
import { SelfFocusScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-focus-score-goal.mapper';
import { SelfInteractionsGoalMapper } from '../src/infrastructure/mappers/goals/self-interactions-goal.mapper';
import { SelfScreentimeScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-screentime-score-goal.mapper';
import { SelfTimeInAppGoalMapper } from '../src/infrastructure/mappers/goals/self-time-in-app-goal.mapper';
import { SelfTimeInCallsScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-time-in-calls-score-goal.mapper';
import { SelfTimeInMeetingsScoreGoalMapper } from '../src/infrastructure/mappers/goals/self-time-in-meetings-score-goal.mapper';
import { AccountAppCategoryMetricsPerAppAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/apps/get-account-app-category-metrics-per-app.aggregate-root.mapper';
import { AccountAverageCallsPerPersonSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/calls/account-average-calls-per-person-summary.aggregate-root.mapper';
import { AccountAverageCallsPerWorkspaceTimeseriesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/calls/account-average-calls-per-workspace-time-series.aggregate-root.mapper';
import { AccountCallsPerWorkspaceSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/calls/account-calls-per-workspace-summary.aggregate-root.mapper';
import { AccountCallsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/calls/account-calls-summary.aggregate-root.mapper';
import { AccountCallsTimeSeriesItemAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/calls/account-calls-time-series-Item.aggregate-root.mapper';
import { AccountAppTotalsPerCategorySummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/category/account-app-totals-per-category-summary.aggregate-root.mapper';
import { AccountAverageMeetingsPerPersonSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-average-meetings-per-person-summary.aggregate-root.mapper';
import { AccountMeetingHoursBySizeSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meeting-hours-by-size-summary.aggregate-root.mapper';
import { AccountMeetingSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meeting-summary.aggregate-root.mapper';
import { AccountMeetingTimeSeriesItemAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meeting-time-series-Item.aggregate-root.mapper';
import { AccountMeetingsPerWorkspaceSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meetings-per-workspace-summary.aggregate-root.mapper';
import { AccountMeetingsPerWorkspaceTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meetings-per-workspace-time-series.aggregate-root.mapper';
import { AccountAverageWorkingHoursPerPersonRangesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/working-hours/account-average-working-hours-per-person-distribution-range.aggregate-root.mapper';
import { AccountAverageWorkingHoursPerPersonDistributionRangeMapper } from '../src/infrastructure/mappers/org-metrics/working-hours/account-average-working-hours-per-person-distribution-range.entity.mapper';
import { AccountAverageWorkingHoursPerPersonTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/working-hours/account-average-working-hours-per-person-time-series.aggregate-root.mapper';
import { AccountAverageWorkingHoursSummaryAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/working-hours/account-average-working-hours-summary.aggregate-root.mapper';
import { AccountAverageWorkingHoursTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/working-hours/account-average-working-hours-time-series.aggregate-root.mapper';
import { SelfAppAverageSummaryEntityMapper } from '../src/infrastructure/mappers/scores/self-app-average-summary.mapper';
import { SelfAppCalendarEventsAverageSummaryEntityMapper } from '../src/infrastructure/mappers/scores/self-app-calendar-events-average-summary.mapper';
import { SelfAverageDigitalIntensityScoreMapper } from '../src/infrastructure/mappers/scores/self-average-digital-intensity-score.mapper';
import { SelfAverageDigitalWorkingHoursScoreMapper } from '../src/infrastructure/mappers/scores/self-average-digital-working-hours-scores.mapper';
import { SelfAverageFocusScoreMapper } from '../src/infrastructure/mappers/scores/self-average-focus-score.mapper';
import { SelfAverageScreentimeScoreMapper } from '../src/infrastructure/mappers/scores/self-average-screentime-score.mapper';
import { SelfAverageTimeInCallsScoreMapper } from '../src/infrastructure/mappers/scores/self-average-time-in-calls-score.mapper';
import { SelfCalendarScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-calendar-score-aggregate-root.mapper';
import { SelfDigitalIntensityScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-digital-intensity-score-aggregate-root.mapper';
import { SelfDigitalIntensityScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-digital-intensity-score-averages-summary.mapper';
import { SelfDigitalIntensityScoreMapper } from '../src/infrastructure/mappers/scores/self-digital-intensity-score.mapper';
import { SelfDigitalWorkingHoursScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-digital-working-hours-score-aggregate-root.mapper';
import { SelfDigitalWorkingHoursScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-digital-working-hours-score-averages-summary.mapper';
import { SelfDigitalWorkingHoursScoreMapper } from '../src/infrastructure/mappers/scores/self-digital-working-hours-score.mapper';
import { SelfFocusScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-focus-score-aggregate-root.mapper';
import { SelfFocusScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-focus-score-averages-summary.mapper';
import { SelfFocusScoreMapper } from '../src/infrastructure/mappers/scores/self-focus-score.mapper';
import { SelfScreentimeScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-screentime-score-aggregate-root.mapper';
import { SelfScreentimeScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-screentime-score-averages-summary.mapper';
import { SelfScreentimeScoreMapper } from '../src/infrastructure/mappers/scores/self-screentime-score.mapper';
import { SelfTimeInCallsScoreAggregateRootMapper } from '../src/infrastructure/mappers/scores/self-time-in-calls-score-aggregate-root.mapper';
import { SelfTimeInCallsScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-time-in-calls-score-averages-summary.mapper';
import { SelfTimeInCallsScoreMapper } from '../src/infrastructure/mappers/scores/self-time-in-calls-score.mapper';
import { SelfTimeInMeetingsScoreAveragesSummaryMapper } from '../src/infrastructure/mappers/scores/self-time-in-meetings-score-averages-summary.mapper';
import { AccountSentimentReportAverageSummaryAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-average-summary-aggregate-root.mapper';
import { AccountSentimentReportAverageTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-average-time-series-aggregate-root.mapper';
import { AccountSentimentReportAverageTotalsAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-average-totals-aggregate-root.mapper';
import { AccountSentimentReportPercentilesAverageAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-percentiles-average-aggregate-root.mapper';
import { AccountSentimentReportPercentilesSummaryAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-percentiles-summary-aggregate-root.mapper';
import { AccountSentimentReportTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/sentiment/account-sentiment-report-time-series-aggregate-root.mapper';
import { SelfSentimentReportTimeSeriesMapper } from '../src/infrastructure/mappers/sentiment/self-sentiment-report-time-series.mapper';
import { SelfSentimentReportMapper } from '../src/infrastructure/mappers/sentiment/self-sentiment-report.mapper';
import { AppMetricsAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-average-app-time-whole-team.aggregate-root.mapper';
import { AppMetricsTimeSeriesAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-time-series-average-whole-team.aggregate-root.mapper';
import { AppMetricsTimeSeriesTotalIndividualMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-time-series-total-individual.mapper';
import { AppMetricsTimeSeriesTotalWholeTeamMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-time-series-total-whole-team.mapper';
import { AppMetricsTotalIndividualMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-total-individual.mapper';
import { AppMetricsTotalWholeTeamMapper } from '../src/infrastructure/mappers/team-metrics/app-metrics-total-whole-team.mapper';
import { TeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/score/metrics/team-calendar-score-time-series-metrics-summary-average-whole-team.aggregate-root.mapper';
import { TeamCalendarScoreTimeSeriesMetricsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/score/metrics/team-calendar-score-time-series-metrics-summary.aggregate-root.mapper';
import { TeamMetricsCalendarScoreTimeSeriesAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/score/metrics/team-metrics-calendar-score-time-series-average-whole-team.aggregate-root.mapper';
import { TeamMetricsCalendarScoreTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/score/metrics/team-metrics-calendar-score-time-series.aggregate-root.mapper';
import { TeamMetricsAppsSummaryAverageWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-app-summary-average-whole-team-parent.aggregate-root.mapper';
import { TeamMetricsAppsSummaryTotalIndividualParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-app-summary-total-individual-parent.aggregate-root.mapper';
import { TeamMetricsAppsSummaryTotalWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-app-summary-total-whole-team-parent.aggregate-root.mapper';
import { TeamMetricsCalendarAppTimeSeriesTotalIndividualAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-app-time-series-total-individual.aggregate-root.mapper';
import { TeamMetricsCalendarAppTimeSeriesTotalWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-app-time-series-total-whole-team.aggregate-root.mapper';
import { TeamMetricsCalendarAppTrendsTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-app-trends-time-series.aggregate-root.mapper';
import { TeamMetricsCalendarChartAllTimeAveragesAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-chart-all-time-averages-aggregate-root.mapper';
import { TeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-chart-all-time-averages-average-whole-team-aggregate-root.mapper';
import { TeamMetricsCalendarMeetingsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-meetings-summary.aggregate-root.mapper';
import { TeamMetricsCalendarViewsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calendar/team-metrics-calendar-views-summary.aggregate-root.mapper';
import { TeamMetricsCallsEventsSummaryAverageWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calls/team-metrics-calls-events-summary-average-whole-team-parent.aggregate-root.mapper';
import { TeamMetricsCallsEventsSummaryTotalIndividualParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calls/team-metrics-calls-events-summary-total-individual-parent.aggregate-root.mapper';
import { TeamMetricsCallsEventsSummaryTotalWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/calls/team-metrics-calls-events-summary-total-whole-team-parent.aggregate-root.mapper';
import { CategoryMetricsAverageWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/category/category-metrics-average-whole-team-parent.aggregate-root.mapper';
import { CategoryMetricsTotalIndividualParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/category/category-metrics-total-individual-parent.aggregate-root.mapper';
import { CategoryMetricsTotalWholeTeamParentAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/category/category-metrics-total-whole-team-parent.aggregate-root.mapper';
import { SelfAppCategoryAveragesSummaryEntityMapper } from '../src/infrastructure/mappers/team-metrics/category/self-app-category-average-summary.mapper';
import { TeamMetricsMeetingsEventSummaryAverageWholeTeamEntityMapper } from '../src/infrastructure/mappers/team-metrics/meetings/team-metrics-meetings-event-summary-average-whole-team.entity.mapper';
import { TeamMetricsMeetingsEventSummaryTotalIndividualEntityMapper } from '../src/infrastructure/mappers/team-metrics/meetings/team-metrics-meetings-event-summary-total-individual.entity.mapper';
import { TeamMetricsMeetingsEventSummaryTotalWholeTeamEntityMapper } from '../src/infrastructure/mappers/team-metrics/meetings/team-metrics-meetings-event-summary-total-whole-team.entity.mapper';
import { TeamSubscriptionMetricsAppsSummaryAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-app-summary-average-whole-team.aggregate-root.mapper';
import { TeamSubscriptionMetricsAppsSummaryTotalIndividualAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-app-summary-total-individual.aggregate-root.mapper';
import { TeamSubscriptionMetricsAppsSummaryTotalWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-app-summary-total-whole-team.aggregate-root.mapper';
import { TeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-meetings-summary-average-whole-team.aggregate-root.mapper';
import { TeamSubscriptionMetricsMeetingsSummaryTotalIndividualAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-meetings-summary-total-individual.aggregate-root.mapper';
import { TeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/subscription/team-subscription-metrics-meetings-summary-total-whole-team.aggregate-root.mapper';
import { AppMetricsAverageSummaryWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/app-metrics-average-summary-whole-team.aggregate-root.mapper';
import { AppMetricsTotalSummaryIndividualMapper } from '../src/infrastructure/mappers/team-metrics/summary/app-metrics-total-summary-individual.mapper';
import { AppMetricsTotalSummaryWholeTeamMapper } from '../src/infrastructure/mappers/team-metrics/summary/app-metrics-total-summary-whole-team.mapper';
import { TeamMetricsAppViewsSummaryAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-app-views-summary-average-whole-team.aggregate-root.mapper';
import { TeamMetricsAppViewsSummaryTotalIndividualAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-app-views-summary-total-individual.aggregate-root.mapper';
import { TeamMetricsAppViewsSummaryTotalWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-app-views-summary-total-whole-team.aggregate-root.mapper';
import { TeamMetricsMeetingsSummaryAverageWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-meetings-summary-average-whole-team.aggregate-root.mapper';
import { TeamMetricsMeetingsSummaryTotalIndividualAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-meetings-summary-total-individual.aggregate-root.mapper';
import { TeamMetricsMeetingsSummaryTotalWholeTeamAggregateRootMapper } from '../src/infrastructure/mappers/team-metrics/summary/team-metrics-meetings-summary-total-whole-team.aggregate-root.mapper';
import { AccountAppCategoriesPerWorkspaceSummaryAggregateRootMapper } from '../src/infrastructure/mappers/top-apps/account-app-categories-per-workspace-summary.aggregate-root.mapper';
import { AccountAppMetricsPerWorkspaceSummaryAggregateRootMapper } from '../src/infrastructure/mappers/top-apps/account-app-metrics-per-workspace-summary.aggregate-root.mapper';
import { AccountTopAppsMetricsSummaryAggregateRootMapper } from '../src/infrastructure/mappers/top-apps/account-top-apps-metrics-summary.aggregate-root.mapper';
import { SelfWorkingSessionsEntityMapper } from '../src/infrastructure/mappers/working-sessions/self-working-sessions.entity.mapper';
import { ExpandAccountCallsEventsTimeSeriesCommand } from '../src/interface/commands/org-metrics/calls/expand-account-call-events-time-series.command';
import { GetAccountAppCategoriesSummaryController } from '../src/interface/controllers/app-category-metrics/get-account-app-categories-summary.controller';
import { GetAccountAppCategoriesTimeSeriesController } from '../src/interface/controllers/app-category-metrics/get-account-app-categories-time-series.controller';
import { GetAccountTotalAppCategoriesSummaryController } from '../src/interface/controllers/app-category-metrics/get-account-total-app-categories-summary.controller';
import { GetSelfAppCategoryChartAllTimeAverageController } from '../src/interface/controllers/app-category-metrics/get-self-app-category-chart-all-time-average.controller';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryController } from '../src/interface/controllers/app-category-metrics/get-self-app-category-score-time-series-metrics-summary.controller';
import { GetSelfAppCategoryTimeSeriesController } from '../src/interface/controllers/app-category-metrics/get-self-app-category-time-series.controller';
import { GetSelfTopAppCategoriesController } from '../src/interface/controllers/app-category-metrics/get-self-top-app-categories.controller';
import { GetSelfTopAppCategoryAppsController } from '../src/interface/controllers/app-category-metrics/get-self-top-app-category-apps.controller';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesController } from '../src/interface/controllers/app-category-metrics/get-workspace-category-metrics-usage-per-individual-time-series.controller';
import { GetWorkspaceCategoryMetricsUsageTotalsController } from '../src/interface/controllers/app-category-metrics/get-workspace-category-metrics-usage-totals.controller';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesController } from '../src/interface/controllers/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.controller';
import { GetCalendarAppChartAllTimeAveragesController } from '../src/interface/controllers/calendar/all-time-averages/get-calendar-app-chart-all-time-averages.controller';
import { GetCalendarChartAllTimeAveragesController } from '../src/interface/controllers/calendar/all-time-averages/get-calendar-chart-all-time-averages.controller';
import { GetSelfCalendarScoreTimeSeriesMetricsSummaryController } from '../src/interface/controllers/calendar/score/metrics/get-calendar-score-time-series-metrics-summary.controller';
import { GetSelfCalendarAppTimeSeriesController } from '../src/interface/controllers/calendar/score/time-series/get-calendar-app-time-series.controller';
import { GetSelfCalendarScoreTimeSeriesController } from '../src/interface/controllers/calendar/score/time-series/get-calendar-score-time-series.controller';
import { GetCalendarAppTimeSeriesMetricsSummaryController } from '../src/interface/controllers/calendar/time-series/get-calendar-app-time-series-metrics-summary.controller';
import { GetAccountMetricsInsightsAveragesCompositeController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-averages.composite-controller';
import { GetAccountMetricsInsightsChartDataCompositeController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-chart-data.composite-controller';
import { GetAccountMetricsInsightsDigitalIntensitySummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-digital-intensity-summary.controller';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.controller';
import { GetAccountMetricsInsightsFocusV1SummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-focus-v1-summary.controller';
import { GetAccountMetricsInsightsSentimentSummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-sentiment-summary.controller';
import { GetAccountMetricsInsightsTimeInAppSummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-time-in-app-summary.controller';
import { GetAccountMetricsInsightsTimeInCallsSummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-time-in-calls-summary.controller';
import { GetAccountMetricsInsightsTimeInCategorySummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-time-in-category-summary.controller';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.controller';
import { GetAccountMetricsInsightsTotalsCompositeController } from '../src/interface/controllers/challenge-insights/get-account-metrics-insights-totals.composite-controller';
import { GetChallengeInsightProgressSummaryCompositeController } from '../src/interface/controllers/challenge-insights/get-challenge-insight-progress-summary.composite-controller';
import { GetSelfMetricsInsightsChallengeTargetStreakController } from '../src/interface/controllers/challenge-insights/get-self-metrics-insights-challenge-target-streak.controller';
import { ListAccountMetricsInsightsSummariesCompositeController } from '../src/interface/controllers/challenge-insights/list-account-metrics-insights-summaries.composite-controller';
import { GetSelfGoalSummariesCompositeController } from '../src/interface/controllers/goals/get-self-goal-summaries.composite-controller';
import { GetAccountAppCategoryMetricsPerAppController } from '../src/interface/controllers/org-metrics/apps/get-account-app-category-metrics-per-app.controller';
import { ExpandAccountCallsEventsTimeSeriesController } from '../src/interface/controllers/org-metrics/calls/expand-account-calls-events-time-series.controller';
import { GetAccountAverageCallsPerPersonSummaryController } from '../src/interface/controllers/org-metrics/calls/get-account-average-calls-per-person-summary.controller';
import { GetAccountAverageCallsPerWorkspaceTimeseriesController } from '../src/interface/controllers/org-metrics/calls/get-account-average-calls-per-workspace-time-series.controller';
import { GetAccountCallEventsTimeSeriesController } from '../src/interface/controllers/org-metrics/calls/get-account-calls-events-time-series.controller';
import { GetAccountCallsPerWorkspaceSummaryController } from '../src/interface/controllers/org-metrics/calls/get-account-calls-per-workspace-summary.controller';
import { GetAccountCallsSummaryController } from '../src/interface/controllers/org-metrics/calls/get-account-calls-summary.controller';
import { GetAccountAppTotalsPerCategorySummaryController } from '../src/interface/controllers/org-metrics/category/get-account-app-totals-per-category-summary.controller';
import { GetAccountAverageMeetingsPerPersonSummaryController } from '../src/interface/controllers/org-metrics/meetings/get-account-average-meetings-per-person-summary.controller';
import { GetAccountMeetingEventsSummaryController } from '../src/interface/controllers/org-metrics/meetings/get-account-meeting-events-summary.controller';
import { GetAccountMeetingEventsTimeSeriesController } from '../src/interface/controllers/org-metrics/meetings/get-account-meeting-events-time-series.controller';
import { GetAccountMeetingHoursBySizeSummaryController } from '../src/interface/controllers/org-metrics/meetings/get-account-meeting-hours-by-size-summary.controller';
import { GetAccountMeetingHoursBySizeTimeSeriesController } from '../src/interface/controllers/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.controller';
import { GetAccountMeetingsPerWorkspaceSummaryController } from '../src/interface/controllers/org-metrics/meetings/get-account-meetings-per-workspace-summary.controller';
import { GetAccountMeetingsPerWorkspaceTimeSeriesController } from '../src/interface/controllers/org-metrics/meetings/get-account-meetings-per-workspace-time-series.controller';
import { GetAccountAverageWorkingHoursPerPersonDistributionController } from '../src/interface/controllers/org-metrics/working-hours/get-account-average-working-hours-per-person-distribution.controller';
import { GetAccountAverageWorkingHoursPerPersonRangesController } from '../src/interface/controllers/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.controller';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesController } from '../src/interface/controllers/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.controller';
import { GetAccountAverageWorkingHoursSummaryController } from '../src/interface/controllers/org-metrics/working-hours/get-account-average-working-hours-summary.controller';
import { GetAccountAverageWorkingHoursTimeSeriesController } from '../src/interface/controllers/org-metrics/working-hours/get-account-average-working-hours-time-series.controller';
import { GetSelfAllScoresCompositeController } from '../src/interface/controllers/scores/get-all-scores.composite-controller';
import { GetSelfAppAveragesSummaryCompositeController } from '../src/interface/controllers/scores/get-self-app-averages-summary.composite-controller';
import { GetSelfScoreAveragesSummaryCompositeController } from '../src/interface/controllers/scores/get-self-score-averages-summary.composite-controller';
import { GetAccountSentimentReportAverageSummaryController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-average-summary.controller';
import { GetAccountSentimentReportAverageTimeSeriesController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-average-time-series.controller';
import { GetAccountSentimentReportAverageTotalsController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-average-totals.controller';
import { GetAccountSentimentReportPercentilesAverageController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-percentiles-average.controller';
import { GetAccountSentimentReportPercentilesSummaryController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-percentiles-summary.controller';
import { GetAccountSentimentReportTimeSeriesController } from '../src/interface/controllers/sentiment/get-account-sentiment-report-time-series.controller';
import { GetSelfSentimentReportTimeSeriesController } from '../src/interface/controllers/sentiment/get-self-sentiment-report-time-series.controller';
import { GetSelfSentimentReportController } from '../src/interface/controllers/sentiment/get-self-sentiment-report.controller';
import { GetTeamMetricsAppsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-apps-summary-average-whole-team.controller';
import { GetTeamMetricsAppsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-apps-summary-total-individual.controller';
import { GetTeamMetricsAppsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-apps-summary-total-whole-team.controller';
import { GetTeamMetricsCalendarAppTimeSeriesTotalIndividualController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-individual.controller';
import { GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-whole-team.controller';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.controller';
import { GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-average-whole-team.controller';
import { GetTeamMetricsCalendarChartAllTimeAveragesIndividualController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-individual.controller';
import { GetTeamMetricsCalendarChartAllTimeAveragesController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.controller';
import { GetTeamMetricsCalendarMeetingsSummaryIndividualController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-meetings-summary-individual.controller';
import { GetTeamMetricsCalendarMeetingsSummaryController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.controller';
import { GetTeamMetricsCalendarViewsSummaryController } from '../src/interface/controllers/team-metrics/calendar/get-team-metrics-calendar-views-summary.controller';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-average-whole-team.controller';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-total-individual.controller';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-whole-team.controller';
import { GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series-average-whole-team.controller';
import { GetTeamMetricsCalendarScoreTimeSeriesIndividualController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series-individual.controller';
import { GetTeamMetricsCalendarScoreTimeSeriesController } from '../src/interface/controllers/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series.controller';
import { GetTeamMetricsCallsEventsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/calls/get-team-metrics-calls-events-summary-average-whole-team.controller';
import { GetTeamMetricsCallsEventsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/calls/get-team-metrics-calls-events-summary-total-individual.controller';
import { GetTeamMetricsCallsEventsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/calls/get-team-metrics-calls-events-summary-total-whole-team.controller';
import { GetCategoryMetricsAverageWholeTeamController } from '../src/interface/controllers/team-metrics/category/get-category-metrics-average-whole-team.controller';
import { GetCategoryMetricsTotalIndividualController } from '../src/interface/controllers/team-metrics/category/get-category-metrics-total-individual.controller';
import { GetCategoryMetricsTotalWholeTeamController } from '../src/interface/controllers/team-metrics/category/get-category-metrics-total-whole-team.controller';
import { GetSelfAppCategoryAveragesSummaryController } from '../src/interface/controllers/team-metrics/category/get-self-app-category-averages-summary.controller';
import { GetAppMetricsAverageWholeTeamController } from '../src/interface/controllers/team-metrics/get-app-metrics-average-whole-team.controller';
import { GetAppMetricsTimeSeriesAverageWholeTeamController } from '../src/interface/controllers/team-metrics/get-app-metrics-time-series-average-whole-team.controller';
import { GetAppMetricsTimeSeriesTotalIndividualController } from '../src/interface/controllers/team-metrics/get-app-metrics-time-series-total-individual.controller';
import { GetAppMetricsTimeSeriesTotalWholeTeamController } from '../src/interface/controllers/team-metrics/get-app-metrics-time-series-total-whole-team.controller';
import { GetAppMetricsTotalIndividualController } from '../src/interface/controllers/team-metrics/get-app-metrics-total-individual.controller';
import { GetAppMetricsTotalWholeTeamController } from '../src/interface/controllers/team-metrics/get-app-metrics-total-whole-team.controller';
import { GetTeamMetricsMeetingsEventSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/meetings/get-team-metrics-meetings-event-summary-average-whole-team.controller';
import { GetTeamMetricsMeetingsEventSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-individual.controller';
import { GetTeamMetricsMeetingsEventSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-whole-team.controller';
import { GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-apps-summary-average-whole-team.controller';
import { GetTeamSubscriptionMetricsAppsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-individual.controller';
import { GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-whole-team.controller';
import { GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-average-whole-team.controller';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-individual.controller';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-whole-team.controller';
import { GetAppMetricsAverageSummaryWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-app-metrics-average-summary-whole-team.controller';
import { GetAppMetricsTotalSummaryIndividualController } from '../src/interface/controllers/team-metrics/summary/get-app-metrics-total-summary-individual.controller';
import { GetAppMetricsTotalSummaryWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-app-metrics-total-summary-whole-team.controller';
import { GetTeamMetricsAppViewsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-app-views-summary-average-whole-team.controller';
import { GetTeamMetricsAppViewsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-app-views-summary-total-individual.controller';
import { GetTeamMetricsAppViewsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-app-views-summary-total-whole-team.controller';
import { GetTeamMetricsMeetingsSummaryAverageWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-meetings-summary-average-whole-team.controller';
import { GetTeamMetricsMeetingsSummaryTotalIndividualController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-meetings-summary-total-individual.controller';
import { GetTeamMetricsMeetingsSummaryTotalWholeTeamController } from '../src/interface/controllers/team-metrics/summary/get-team-metrics-meetings-summary-total-whole-team.controller';
import { GetAccountAppCategoriesPerWorkspaceSummaryController } from '../src/interface/controllers/top-apps/get-account-app-categories-per-workspace-summary.controller';
import { GetAccountAppMetricsPerWorkspaceSummaryController } from '../src/interface/controllers/top-apps/get-account-app-metrics-per-workspace-summary.controller';
import { GetAccountTopAppsMetricsSummaryController } from '../src/interface/controllers/top-apps/get-account-top-apps-metrics-summary.controller';
import { GetSelfWorkingSessionsController } from '../src/interface/controllers/working-sessions/get-self-working-sessions.controller';
import { GetAccountAppCategoriesSummaryRequestDto } from '../src/interface/dtos/app-category-metrics/get-account-app-categories-summary.request.dto';
import { GetAccountAppCategoriesSummaryResponseDto } from '../src/interface/dtos/app-category-metrics/get-account-app-categories-summary.response.dto';
import { GetAccountAppCategoriesTimeSeriesRequestDto } from '../src/interface/dtos/app-category-metrics/get-account-app-categories-time-series.request.dto';
import { GetAccountAppCategoriesTimeSeriesResponseDto } from '../src/interface/dtos/app-category-metrics/get-account-app-categories-time-series.response.dto';
import { GetAccountTotalAppCategoriesSummaryRequestDto } from '../src/interface/dtos/app-category-metrics/get-account-total-app-categories-summary.request.dto';
import { GetAccountTotalAppCategoriesSummaryResponseDto } from '../src/interface/dtos/app-category-metrics/get-account-total-app-categories.response.dto';
import { GetSelfAppCategoryChartAllTimeAverageRequestDto } from '../src/interface/dtos/app-category-metrics/get-self-app-category-chart-all-time-average.request.dto';
import { GetSelfAppCategoryChartAllTimeAverageResponseDto } from '../src/interface/dtos/app-category-metrics/get-self-app-category-chart-all-time-average.response.dto';
import { GetSelfAppCategoryTimeSeriesRequestDto } from '../src/interface/dtos/app-category-metrics/get-self-app-category-time-series.request.dto';
import { GetSelfAppCategoryTimeSeriesResponseDto } from '../src/interface/dtos/app-category-metrics/get-self-app-category-time-series.response.dto';
import { GetSelfTopAppCategoriesRequestDto } from '../src/interface/dtos/app-category-metrics/get-self-top-app-categories.request.dto';
import { GetSelfTopAppCategoriesResponseDto } from '../src/interface/dtos/app-category-metrics/get-self-top-app-categories.response.dto';
import { GetSelfTopAppCategoryAppsRequestDto } from '../src/interface/dtos/app-category-metrics/get-self-top-app-category-apps.request.dto';
import { GetSelfTopAppCategoryAppsResponseDto } from '../src/interface/dtos/app-category-metrics/get-self-top-app-category-apps.response.dto';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesRequestDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-per-individual-time-series.request.dto';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesResponseDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-per-individual-time-series.response.dto';
import { GetWorkspaceCategoryMetricsUsageTotalsRequestDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-totals.request.dto';
import { GetWorkspaceCategoryMetricsUsageTotalsResponseDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-totals.response.dto';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesRequestDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.request.dto';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesResponseDto } from '../src/interface/dtos/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.response.dto';
import { GetCalendarChartAllTimeAveragesRequestDto } from '../src/interface/dtos/calendar/all-time-averages/get-calendar-chart-all-time-averages.request.dto';
import { GetCalendarChartAllTimeAveragesResponseDto } from '../src/interface/dtos/calendar/all-time-averages/get-calendar-chart-all-time-averages.response.dto';
import { GetCalendarAppChartAllTimeAveragesRequestDto } from '../src/interface/dtos/calendar/all-time-averages/get-calendar-chart-app-all-time-averages.request.dto';
import { GetSelfCalendarScoreTimeSeriesMetricsSummaryRequestDto } from '../src/interface/dtos/calendar/score/metrics/get-calendar-score-time-series-metrics-summary.request.dto';
import { GetSelfCalendarScoreTimeSeriesMetricsSummaryResponseDto } from '../src/interface/dtos/calendar/score/metrics/get-calendar-score-time-series-metrics-summary.response.dto';
import { GetSelfCalendarAppTimeSeriesRequestDto } from '../src/interface/dtos/calendar/score/time-series/get-calendar-app-time-series.request.dto';
import { GetSelfCalendarScoreTimeSeriesRequestDto } from '../src/interface/dtos/calendar/score/time-series/get-calendar-score-time-series.request.dto';
import { GetSelfCalendarScoreTimeSeriesResponseDto } from '../src/interface/dtos/calendar/score/time-series/get-calendar-score-time-series.response.dto';
import { GetCalendarAppTimeSeriesMetricsRequestDto } from '../src/interface/dtos/calendar/time-series/get-calendar-time-series-metrics.request.dto';
import { GetSelfCalendarAppTimeSeriesMetricsSummaryResponseDto } from '../src/interface/dtos/calendar/time-series/get-calendar-time-series-metrics.response.dto';
import { GetAccountMetricsInsightsAveragesRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-averages.request.dto';
import { GetAccountMetricsInsightsAveragesResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-averages.response.dto';
import { GetAccountMetricsInsightsChartDataRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-chart-data.request.dto';
import { GetAccountMetricsInsightsChartDataResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-chart-data.response.dto';
import { GetAccountMetricsInsightsDigitalIntensitySummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-digital-intensity-summary.request.dto';
import { GetAccountMetricsInsightsDigitalIntensitySummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-digital-intensity-summary.response.dto';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.request.dto';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.response.dto';
import { GetAccountMetricsInsightsFocusV1SummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-focus-v1-summary.request.dto';
import { GetAccountMetricsInsightsFocusV1SummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-focus-v1-summary.response.dto';
import { GetAccountMetricsInsightsSentimentSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-sentiment-summary.request.dto';
import { GetAccountMetricsInsightsSentimentSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-sentiment-summary.response.dto';
import { GetAccountMetricsInsightsTimeInAppSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-app-summary.request.dto';
import { GetAccountMetricsInsightsTimeInAppSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-app-summary.response.dto';
import { GetAccountMetricsInsightsTimeInCallsSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-calls-summary.request.dto';
import { GetAccountMetricsInsightsTimeInCallsSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-calls-summary.response.dto';
import { GetAccountMetricsInsightsTimeInCategorySummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-category-summary.request.dto';
import { GetAccountMetricsInsightsTimeInCategorySummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-category-summary.response.dto';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.request.dto';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.response.dto';
import { GetAccountMetricsInsightsTotalsRequestDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-totals.request.dto';
import { GetAccountMetricsInsightsTotalsResponseDto } from '../src/interface/dtos/challenge-insights/get-account-metrics-insights-totals.response.dto';
import { GetChallengeInsightProgressSummaryRequestDto } from '../src/interface/dtos/challenge-insights/get-challenge-insight-progress-summary.request.dto';
import { GetChallengeInsightProgressSummaryResponseDto } from '../src/interface/dtos/challenge-insights/get-challenge-insight-progress-summary.response.dto';
import { GetSelfMetricsInsightsChallengeTargetStreakRequestDto } from '../src/interface/dtos/challenge-insights/get-self-metrics-insights-challenge-target-sreak.request.dto';
import { GetSelfMetricsInsightsChallengeTargetStreakResponseDto } from '../src/interface/dtos/challenge-insights/get-self-metrics-insights-challenge-target-sreak.response.dto';
import { ListAccountMetricsInsightsSummariesRequestDto } from '../src/interface/dtos/challenge-insights/list-account-metrics-insights-summaries.request.dto';
import { ListAccountMetricsInsightsSummariesResponseDto } from '../src/interface/dtos/challenge-insights/list-account-metrics-insights-summaries.response.dto';
import { GetSelfGoalSummariesRequestDto } from '../src/interface/dtos/goals/get-self-goal-summaries.request.dto';
import { GetSelfGoalSummariesResponseDto } from '../src/interface/dtos/goals/get-self-goal-summaries.response.dto';
import { GetAccountAppCategoryMetricsPerAppRequestDto } from '../src/interface/dtos/org-metrics/apps/get-account-app-category-metrics-per-app.request.dto';
import { GetAccountAppCategoryMetricsPerAppResponseDto } from '../src/interface/dtos/org-metrics/apps/get-account-app-category-metrics-per-app.response.dto';
import { ExpandAccountCallEventsTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/calls/expand-account-call-events-time-series.request.dto';
import { GetAccountAverageCallsPerPersonSummaryRequestDto } from '../src/interface/dtos/org-metrics/calls/get-account-average-calls-per-person-summary.request.dto';
import { GetAccountAverageCallsPerPersonSummaryResponseDto } from '../src/interface/dtos/org-metrics/calls/get-account-average-calls-per-person-summary.response.dto';
import { GetAccountAverageCallsPerWorkspaceTimeseriesRequestDto } from '../src/interface/dtos/org-metrics/calls/get-account-average-calls-per-workspace-time-series.request.dto';
import { GetAccountAverageCallsPerWorkspaceTimeseriesResponseDto } from '../src/interface/dtos/org-metrics/calls/get-account-average-calls-per-workspace-time-series.response.dto';
import { GetAccountCallEventsTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/calls/get-account-call-events-time-series.request.dto';
import { GetAccountCallEventsTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/calls/get-account-call-events-time-series.response.dto';
import { GetAccountCallsPerWorkspaceSummaryRequestDto } from '../src/interface/dtos/org-metrics/calls/get-account-calls-per-workspace-summary-request.dto';
import { GetAccountCallsPerWorkspaceSummaryResponseDto } from '../src/interface/dtos/org-metrics/calls/get-account-calls-per-workspace-summary-response.dto';
import { GetAccountCallsSummaryRequestDto } from '../src/interface/dtos/org-metrics/calls/get-account-calls-summary-request.dto';
import { GetAccountCallsSummaryResponseDto } from '../src/interface/dtos/org-metrics/calls/get-account-calls-summary-response.dto';
import { GetAccountAppTotalsPerCategorySummaryRequestDto } from '../src/interface/dtos/org-metrics/category/get-account-app-totals-per-category-summary.request.dto';
import { GetAccountAppTotalsPerCategorySummaryResponseDto } from '../src/interface/dtos/org-metrics/category/get-account-app-totals-per-category-summary.response.dto';
import { GetAccountMeetingEventsTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/meetings/account-meeting-events-time-series.request.dto';
import { GetAccountMeetingEventsTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/meetings/account-meeting-events-time-series.response.dto';
import { GetAccountMeetingEventsSummaryRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-events-summary.request.dto';
import { GetAccountMeetingEventsSummaryResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-events-summary.response.dto';
import { GetAccountMeetingHoursBySizeSummaryRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-hours-by-size-summary.request.dto';
import { GetAccountMeetingHoursBySizeSummaryResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-hours-by-size-summary.response.dto';
import { GetAccountMeetingHoursBySizeTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.request.dto';
import { GetAccountMeetingHoursBySizeTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.response.dto';
import { GetAccountMeetingsAveragePerPersonSummaryRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-average-per-person-summary.request.dto';
import { GetAccountMeetingsAveragePerPersonSummaryResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-average-per-person-summary.response.dto';
import { GetAccountMeetingsPerWorkspaceSummaryRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-per-workspace-summary.request.dto';
import { GetAccountMeetingsPerWorkspaceSummaryResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-per-workspace-summary.response.dto';
import { GetAccountMeetingsPerWorkspaceTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-per-workspace-time-series.request.dto';
import { GetAccountMeetingsPerWorkspaceTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/meetings/get-account-meetings-per-workspace-time-series.response.dto';
import { GetAccountAverageWorkingHoursPerPersonDistributionRequestDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-distribution.request.dto';
import { GetAccountAverageWorkingHoursPerPersonDistributionResponseDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-distribution.response.dto';
import { GetAccountAverageWorkingHoursPerPersonRangesRequestDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.request.dto';
import { GetAccountAverageWorkingHoursPerPersonRangesResponseDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.response.dto';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.request.dto';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.response.dto';
import { GetAccountAverageWorkingHoursSummaryRequestDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-summary.request.dto';
import { GetAccountAverageWorkingHoursSummaryResponseDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-summary.response.dto';
import { GetAccountAverageWorkingHoursTimeSeriesRequestDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-time-series.request.dto';
import { GetAccountAverageWorkingHoursTimeSeriesResponseDto } from '../src/interface/dtos/org-metrics/working-hours/get-account-average-working-hours-time-series.response.dto';
import { GetSelfAppAveragesSummaryRequestDto } from '../src/interface/dtos/scores/get-self-app-averages-summary.request.dto';
import { GetSelfAppAveragesSummaryResponseDto } from '../src/interface/dtos/scores/get-self-app-averages-summary.response.dto';
import { GetSelfAllScoresRequestDto } from '../src/interface/dtos/scores/self-get-all-scores.request.dto';
import { SelfGetAllScoresResponseDto } from '../src/interface/dtos/scores/self-get-all-scores.response.dto';
import { GetSelfScoreAveragesSummaryRequestDto } from '../src/interface/dtos/scores/self-get-score-averages-summary.request.dto';
import { GetSelfScoreAveragesSummaryResponseDto } from '../src/interface/dtos/scores/self-get-score-averages-summary.response.dto';
import { GetAccountSentimentReportAverageSummaryRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-average-summary.request.dto';
import { GetAccountSentimentReportAverageSummaryResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-average-summary.response.dto';
import { GetAccountSentimentReportAverageTimeSeriesRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-average-time-series.request.dto';
import { GetAccountSentimentReportAverageTimeSeriesResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-average-time-series.response.dto';
import { GetAccountSentimentReportPercentilesAverageRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-percentiles-average.request.dto';
import { GetAccountSentimentReportPercentilesAverageResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-percentiles-average.response.dto';
import { GetAccountSentimentReportPercentilesSummaryRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-percentiles-summary.request.dto';
import { GetAccountSentimentReportPercentilesSummaryResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-percentiles-summary.response.dto';
import { GetAccountSentimentReportTimeSeriesRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-time-series.request.dto';
import { GetAccountSentimentReportTimeSeriesResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-time-series.response.dto';
import { GetAccountSentimentReportAverageTotalsRequestDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-totals-average.request.dto';
import { GetAccountSentimentReportAverageTotalsResponseDto } from '../src/interface/dtos/sentiment/get-account-sentiment-report-totals-average.response.dto';
import { GetSelfSentimentReportTimeSeriesRequestDto } from '../src/interface/dtos/sentiment/get-self-sentiment-report-time-series.request.dto';
import { GetSelfSentimentReportTimeSeriesResponseDto } from '../src/interface/dtos/sentiment/get-self-sentiment-report-time-series.response.dto';
import { GetSelfSentimentReportRequestDto } from '../src/interface/dtos/sentiment/get-self-sentiment-report.request.dto';
import { GetSelfSentimentReportResponseDto } from '../src/interface/dtos/sentiment/get-self-sentiment-report.response.dto';
import { GetTeamMetricsAppsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-apps-summary-average-whole-team.request.dto';
import { GetTeamMetricsAppsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-apps-summary-total-individual.request.dto';
import { GetTeamMetricsAppsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-apps-summary-total-whole-team.request.dto';
import { GetTeamMetricsAppsSummaryResponseDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-apps-summary.response.dto';
import { GetTeamMetricsCalendarAppTimeSeriesTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-individual.request.dto';
import { GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-app-time-series-total-whole-team.request.dto';
import {
  GetTeamMetricsCalendarAppTimeSeriesResponseDto,
  GetTeamMetricsCalendarAppTimeSeriesWholeTeamResponseDto,
} from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-app-time-series.response.dto';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.request.dto';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesResponseDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.response.dto';
import { GetTeamMetricsCalendarChartAllTimeAveragesIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-individual.request.dto';
import { GetTeamMetricsCalendarChartAllTimeAveragesRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.request.dto';
import { GetTeamMetricsCalendarChartAllTimeAveragesResponseDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.response.dto';
import { GetTeamMetricsCalendarMeetingsSummaryIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-meetings-summary-individual.request.dto';
import { GetTeamMetricsCalendarMeetingsSummaryRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.request.dto';
import { GetTeamMetricsCalendarMeetingsSummaryResponseDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.response.dto';
import { GetTeamMetricsCalendarViewsSummaryRequestDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-views-summary.request.dto';
import { GetTeamMetricsCalendarViewsSummaryResponseDto } from '../src/interface/dtos/team-metrics/calendar/get-team-metrics-calendar-views-summary.response.dto';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary-individual.request.dto';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryRequestDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary.request.dto';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-calendar-score-time-series-metrics-summary.response.dto';
import { GetTeamMetricsCalendarScoreTimeSeriesIndividualRequestDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series-individual.request.dto';
import { GetTeamMetricsCalendarScoreTimeSeriesRequestDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series.request.dto';
import { GetTeamMetricsCalendarScoreTimeSeriesResponseDto } from '../src/interface/dtos/team-metrics/calendar/score/metrics/get-team-metrics-calendar-score-time-series.response.dto';
import { GetTeamMetricsCallsEventsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/calls/get-team-metrics-calls-events-summary-average-whole-team.request.dto';
import { GetTeamMetricsCallsEventsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/calls/get-team-metrics-calls-events-summary-total-individual.request.dto';
import { GetTeamMetricsCallsEventsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/calls/get-team-metrics-calls-events-summary-total-whole-team.request.dto';
import { GetTeamMetricsCallsEventsSummaryResponseDto } from '../src/interface/dtos/team-metrics/calls/get-team-metrics-calls-events-summary.response.dto';
import { GetCategoryMetricsAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/category/get-category-metrics-average-whole-team.request.dto';
import { GetCategoryMetricsTotalsIndividualRequestDto } from '../src/interface/dtos/team-metrics/category/get-category-metrics-totals-individual.request.dto';
import { GetCategoryMetricsTotalsWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/category/get-category-metrics-totals-whole-team.request.dto';
import { GetCategoryMetricsTotalsResponseDto } from '../src/interface/dtos/team-metrics/category/get-category-metrics-totals.response.dto';
import { GetSelfAppCategoryAveragesSummaryRequestDto } from '../src/interface/dtos/team-metrics/category/get-self-app-category-averages-summary.request.dto';
import { GetSelfAppCategoryAveragesSummaryResponseDto } from '../src/interface/dtos/team-metrics/category/get-self-app-category-averages-summary.response.dto';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryRequestDto } from '../src/interface/dtos/team-metrics/category/get-self-app-category-score-time-series-metrics-summary.request.dto';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryResponseDto } from '../src/interface/dtos/team-metrics/category/get-self-app-category-score-time-series-metrics-summary.response.dto';
import { GetAppMetricsAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-average-whole-team.request.dto';
import { GetAppMetricsAverageWholeTeamResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-average-whole-team.response.dto';
import { GetAppMetricsTimeSeriesAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-average-whole-team.request.dto';
import { GetAppMetricsTimeSeriesAverageWholeTeamResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-average-whole-team.response.dto';
import { GetAppMetricsTimeSeriesTotalsIndividualRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-totals-individual.request.dto';
import { GetAppMetricsTimeSeriesTotalsIndividualResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-totals-individual.response.dto';
import { GetAppMetricsTimeSeriesTotalsWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-totals-whole-team.request.dto';
import { GetAppMetricsTimeSeriesTotalsWholeTeamResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-time-series-totals-whole-team.response.dto';
import { GetAppMetricsTotalsIndividualRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-totals-individual.request.dto';
import { GetAppMetricsTotalsIndividualResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-totals-individual.response.dto';
import { GetAppMetricsTotalsWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/get-app-metrics-totals-whole-team.request.dto';
import { GetAppMetricsTotalsWholeTeamResponseDto } from '../src/interface/dtos/team-metrics/get-app-metrics-totals-whole-team.response.dto';
import { GetTeamMetricsMeetingsEventSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/meetings/get-team-metrics-meetings-event-summary-average-whole-team.request.dto';
import { GetTeamMetricsMeetingsEventSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-individual.request.dto';
import { GetTeamMetricsMeetingsEventSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-whole-team.request.dto';
import { GetTeamMetricsMeetingsEventSummaryResponseDto } from '../src/interface/dtos/team-metrics/meetings/get-team-metrics-meetings-event-summary.response.dto';
import { GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-apps-summary-average-whole-team.request.dto';
import { GetTeamSubscriptionMetricsAppsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-individual.request.dto';
import { GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-whole-team.request.dto';
import { GetTeamSubscriptionMetricsAppsSummaryResponseDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-apps-summary.response.dto';
import { GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-average-whole-team.request.dto';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-individual.request.dto';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-whole-team.request.dto';
import { GetTeamSubscriptionMetricsMeetingsSummaryResponseDto } from '../src/interface/dtos/team-metrics/subscription/get-team-subscription-metrics-meetings-summary.response.dto';
import { GetAppMetricsAverageSummaryWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-average-summary-whole-team.request.dto';
import { GetAppMetricsAverageSummaryWholeTeamResponseArray } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-average-summary-whole-team.response.dto';
import { GetAppMetricsTotalSummaryIndividualRequestDto } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-total-summary-individual.request.dto';
import { GetAppMetricsTotalSummaryIndividualResponseArray } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-total-summary-individual.response.dto';
import { GetAppMetricsTotalSummaryWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-total-summary-whole-team.request.dto';
import { GetAppMetricsTotalSummaryWholeTeamResponseArray } from '../src/interface/dtos/team-metrics/summary/get-app-metrics-total-summary-whole-team.response.dto';
import { GetTeamMetricsAppViewsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-app-views-summary-average-whole-team.request.dto';
import { GetTeamMetricsAppViewsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-app-views-summary-total-individual.request.dto';
import { GetTeamMetricsAppViewsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-app-views-summary-total-whole-team.request.dto';
import { GetTeamMetricsAppViewsSummaryResponseDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-app-views-summary.response.dto';
import { GetTeamMetricsMeetingsSummaryAverageWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-meetings-summary-average-whole-team.request.dto';
import { GetTeamMetricsMeetingsSummaryTotalIndividualRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-meetings-summary-total-individual.request.dto';
import { GetTeamMetricsMeetingsSummaryTotalWholeTeamRequestDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-meetings-summary-total-whole-team.request.dto';
import { GetTeamMetricsMeetingsSummaryResponseDto } from '../src/interface/dtos/team-metrics/summary/get-team-metrics-meetings-summary.response.dto';
import { GetAccountAppCategoriesPerWorkspaceSummaryRequestDto } from '../src/interface/dtos/top-apps/get-account-app-categories-per-workspace-summary.request.dto';
import { GetAccountAppCategoriesPerWorkspaceSummaryResponseDto } from '../src/interface/dtos/top-apps/get-account-app-categories-per-workspace-summary.response.dto';
import { GetAccountAppMetricsPerWorkspaceSummaryRequestDto } from '../src/interface/dtos/top-apps/get-account-app-metrics-per-workspace-summary.request.dto';
import { GetAccountAppMetricsPerWorkspaceSummaryResponseDto } from '../src/interface/dtos/top-apps/get-account-app-metrics-per-workspace-summary.response.dto';
import { GetAccountTopAppsMetricsSummaryRequestDto } from '../src/interface/dtos/top-apps/get-account-top-apps-metrics-summary.request.dto';
import { GetAccountTopAppsMetricsSummaryResponseDto } from '../src/interface/dtos/top-apps/get-account-top-apps-metrics-summary.response.dto';
import { GetSelfWorkingSessionsRequestDto } from '../src/interface/dtos/working-sessions/get-self-working-sessions.request.dto';
import { GetSelfWorkingSessionsResponseDto } from '../src/interface/dtos/working-sessions/get-self-working-sessions.response.dto';
import { GetAccountMetricsInsightsAveragesPresenter } from '../src/interface/presenters/challenge-insights/get-account-metrics-insights-averages.presenter';
import { GetAccountMetricsInsightsChartDataPresenter } from '../src/interface/presenters/challenge-insights/get-account-metrics-insights-chart-data.presenter';
import { GetChallengeInsightProgressSummaryPresenter } from '../src/interface/presenters/challenge-insights/get-challenge-insight-progress-summary.presenter';
import { ListAccountMetricsInsightsSummariesPresenter } from '../src/interface/presenters/challenge-insights/list-account-metrics-insights-summaries.presenter';
import { GetSelfGoalSummaryPresenter } from '../src/interface/presenters/goals/get-self-goal-summary.presenter';
import { GetSelfAllScoresPresenter } from '../src/interface/presenters/scores/get-self-all-scores.presenter';
import { GetSelfAppAveragesSummaryPresenter } from '../src/interface/presenters/scores/get-self-app-averages-summary.presenter';
import { GetSelfScoreAveragesSummaryPresenter } from '../src/interface/presenters/scores/get-self-score-averages-summary.presenter';
import { GetAccountAppCategoriesSummaryQuery } from '../src/interface/queries/app-category-metrics/get-account-app-categories-summary.query';
import { GetAccountAppCategoriesTimeSeriesQuery } from '../src/interface/queries/app-category-metrics/get-account-app-categories-time-series.query';
import { GetAccountTotalAppCategoriesSummaryQuery } from '../src/interface/queries/app-category-metrics/get-account-total-app-categories-summary.query';
import { GetSelfAppCategoryAveragesSummaryQuery } from '../src/interface/queries/app-category-metrics/get-self-app-category-averages-summary.query';
import { GetSelfAppCategoryChartAllTimeAveragesQuery } from '../src/interface/queries/app-category-metrics/get-self-app-category-chart-all-time-average.query';
import { GetSelfAppCategoryScoreTimeSeriesMetricsSummaryQuery } from '../src/interface/queries/app-category-metrics/get-self-app-category-score-time-series-metrics-summary.query';
import { GetSelfAppCategoryTimeSeriesQuery } from '../src/interface/queries/app-category-metrics/get-self-app-category-time-series-summary.query';
import { GetSelfTopAppCategoriesQuery } from '../src/interface/queries/app-category-metrics/get-self-top-app-categories.query';
import { GetSelfTopAppCategoryAppsQuery } from '../src/interface/queries/app-category-metrics/get-self-top-app-category-apps.query';
import { GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesQuery } from '../src/interface/queries/app-category-metrics/get-workspace-category-metrics-usage-per-individual-time-series.query';
import { GetWorkspaceCategoryMetricsUsageTotalsQuery } from '../src/interface/queries/app-category-metrics/get-workspace-category-metrics-usage-totals.query';
import { GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesQuery } from '../src/interface/queries/app-category-metrics/get-workspace-category-metrics-usage-whole-team-time-series.query';
import { GetCalendarAppChartAllTimeAveragesQuery } from '../src/interface/queries/calendar/all-time-averages/get-calendar-app-chart-all-time-averages.query';
import { GetCalendarChartAllTimeAveragesQuery } from '../src/interface/queries/calendar/all-time-averages/get-calendar-chart-all-time-averages.query';
import { GetSelfCalendarScoreTimeSeriesMetricsSummaryQuery } from '../src/interface/queries/calendar/score/metrics/get-calendar-score-time-series-metrics-summary.query';
import { GetSelfCalendarAppTimeSeriesWeekdayQuery } from '../src/interface/queries/calendar/score/time-series/get-self-calendar-app-time-series-weekday.query';
import { GetSelfCalendarAppTimeSeriesQuery } from '../src/interface/queries/calendar/score/time-series/get-self-calendar-app-time-series.query';
import { GetSelfCalendarScoreTimeSeriesWeekdayQuery } from '../src/interface/queries/calendar/score/time-series/get-self-calendar-score-time-series-weekday.query';
import { GetSelfCalendarScoreTimeSeriesQuery } from '../src/interface/queries/calendar/score/time-series/get-self-calendar-score-time-series.query';
import { GetCalendarAppTimeSeriesMetricsSummaryQuery } from '../src/interface/queries/calendar/time-series/get-calendar-score-time-series-metrics-summary.query';
import { GetAccountMetricsInsightDigitalIntensityAverageQuery } from '../src/interface/queries/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-average.query';
import { GetAccountMetricsInsightDigitalIntensityChartDataQuery } from '../src/interface/queries/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-chart-data.query';
import { GetAccountMetricsInsightDigitalIntensityTotalQuery } from '../src/interface/queries/challenge-insights/digital-intensity/get-account-metrics-insight-digital-intensity-total.query';
import { ListAccountMetricsInsightDigitalIntensitySummaryQuery } from '../src/interface/queries/challenge-insights/digital-intensity/list-account-metrics-insight-digital-intensity-hours-summary.query';
import { GetAccountMetricsInsightDigitalWorkingHoursAverageQuery } from '../src/interface/queries/challenge-insights/digital-working-hours/get-account-metrics-insight-digital-working-hours-average.query';
import { GetAccountMetricsInsightDigitalWorkingHoursChartDataQuery } from '../src/interface/queries/challenge-insights/digital-working-hours/get-account-metrics-insight-digital-working-hours-chart-data.query';
import { GetAccountMetricsInsightDigitalWorkingHoursTotalQuery } from '../src/interface/queries/challenge-insights/digital-working-hours/get-account-metrics-insight-digital-working-hours-total.query';
import { ListAccountMetricsInsightDigitalWorkingHoursSummaryQuery } from '../src/interface/queries/challenge-insights/digital-working-hours/list-account-metrics-insight-digital-working-hours-summary.query';
import { GetAccountMetricsInsightFocusV1AverageQuery } from '../src/interface/queries/challenge-insights/focus-v1/get-account-metrics-insight-focus-v1-average.query';
import { GetAccountMetricsInsightFocusV1ChartDataQuery } from '../src/interface/queries/challenge-insights/focus-v1/get-account-metrics-insight-focus-v1-chart-data.query';
import { GetAccountMetricsInsightFocusV1TotalQuery } from '../src/interface/queries/challenge-insights/focus-v1/get-account-metrics-insight-focus-v1-total.query';
import { ListAccountMetricsInsightFocusV1SummaryQuery } from '../src/interface/queries/challenge-insights/focus-v1/list-account-metrics-insight-focus-v1-summary.query';
import { GetAccountMetricsInsightsDigitalIntensitySummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-digital-intensity-summary.query';
import { GetAccountMetricsInsightsDigitalWorkingHoursSummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-digital-working-hours-summary.query';
import { GetAccountMetricsInsightsFocusV1SummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-focus-v1-summary.query';
import { GetAccountMetricsInsightsSentimentSummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-sentiment-summary.query';
import { GetAccountMetricsInsightsTimeInAppSummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-time-in-app-summary.query';
import { GetAccountMetricsInsightsTimeInCallsSummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-time-in-calls-summary.query';
import { GetAccountMetricsInsightsTimeInCategorySummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-time-in-category-summary.query';
import { GetAccountMetricsInsightsTimeInMeetingsSummaryQuery } from '../src/interface/queries/challenge-insights/get-account-metrics-insights-time-in-meetings-summary.query';
import { GetChallengeInsightProgressChallengeTargetSuccessQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-challenge-target-success.query';
import { GetChallengeInsightProgressDigitalIntensitySummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-digtial-intensity-summary.query';
import { GetChallengeInsightProgressDigitalWorkingHoursSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-digtial-working-hours-summary.query';
import { GetChallengeInsightProgressFocusV1SummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-focus-v1-summary.query';
import { GetChallengeInsightProgressSentimentSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-sentiment-summary.query';
import { GetChallengeInsightProgressTimeInAppSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-time-in-app-summary.query';
import { GetChallengeInsightProgressTimeInCallsSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-time-in-calls-summary.query';
import { GetChallengeInsightProgressTimeInCategorySummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-time-in-category-summary.query';
import { GetChallengeInsightProgressTimeInMeetingsSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-progress-time-in-meetings-summary.query';
import { GetChallengeInsightProgressSelfReportSummaryQuery } from '../src/interface/queries/challenge-insights/get-challenge-insight-self-report-summary.query';
import { GetSelfMetricsInsightsChallengeTargetStreakQuery } from '../src/interface/queries/challenge-insights/get-self-metrics-insights-challenge-target-streak.query';
import { GetAccountMetricsInsightSelfReportAverageQuery } from '../src/interface/queries/challenge-insights/self-report/get-account-metrics-insight-self-report-average.query';
import { GetAccountMetricsInsightSelfReportChartDataQuery } from '../src/interface/queries/challenge-insights/self-report/get-account-metrics-insight-self-report-chart-data.query';
import { GetAccountMetricsInsightSelfReportTotalQuery } from '../src/interface/queries/challenge-insights/self-report/get-account-metrics-insight-self-report-total.query';
import { GetAccountMetricsInsightSentimentAverageQuery } from '../src/interface/queries/challenge-insights/sentiment/get-account-metrics-insight-sentiment-average.query';
import { GetAccountMetricsInsightSentimentChartDataQuery } from '../src/interface/queries/challenge-insights/sentiment/get-account-metrics-insight-sentiment-chart-data.query';
import { GetAccountMetricsInsightSentimentTotalQuery } from '../src/interface/queries/challenge-insights/sentiment/get-account-metrics-insight-sentiment-total.query';
import { ListAccountMetricsInsightSentimentSummaryQuery } from '../src/interface/queries/challenge-insights/sentiment/list-account-metrics-insight-sentiment-summary.query';
import { GetAccountMetricsInsightTimeInAppsAverageQuery } from '../src/interface/queries/challenge-insights/time-in-apps/get-account-metrics-insight-time-in-apps-average.query';
import { GetAccountMetricsInsightTimeInAppsChartDataQuery } from '../src/interface/queries/challenge-insights/time-in-apps/get-account-metrics-insight-time-in-apps-chart-data.query';
import { GetAccountMetricsInsightTimeInAppsTotalQuery } from '../src/interface/queries/challenge-insights/time-in-apps/get-account-metrics-insight-time-in-apps-total.query';
import { GetAccountMetricsInsightTimeInCallsAverageQuery } from '../src/interface/queries/challenge-insights/time-in-calls/get-account-metrics-insight-time-in-calls-average.query';
import { GetAccountMetricsInsightTimeInCallsChartDataQuery } from '../src/interface/queries/challenge-insights/time-in-calls/get-account-metrics-insight-time-in-calls-chart-data.query';
import { GetAccountMetricsInsightTimeInCallsTotalQuery } from '../src/interface/queries/challenge-insights/time-in-calls/get-account-metrics-insight-time-in-calls-total.query';
import { ListAccountMetricsInsightTimeInCallsSummaryQuery } from '../src/interface/queries/challenge-insights/time-in-calls/list-account-metrics-insight-time-in-calls-summary.query';
import { GetAccountMetricsInsightTimeInCategoriesAverageQuery } from '../src/interface/queries/challenge-insights/time-in-categories/get-account-metrics-insight-time-in-categories-average.query';
import { GetAccountMetricsInsightTimeInCategoriesChartDataQuery } from '../src/interface/queries/challenge-insights/time-in-categories/get-account-metrics-insight-time-in-categories-chart-data.query';
import { GetAccountMetricsInsightTimeInCategoriesTotalQuery } from '../src/interface/queries/challenge-insights/time-in-categories/get-account-metrics-insight-time-in-categories-total.query';
import { ListAccountMetricsInsightTimeInCategoriesQuery } from '../src/interface/queries/challenge-insights/time-in-categories/list-account-metrics-insight-time-in-categories.query';
import { GetAccountMetricsInsightTimeInMeetingsAverageQuery } from '../src/interface/queries/challenge-insights/time-in-meetings/get-account-metrics-insight-time-in-meetings-average.query';
import { GetAccountMetricsInsightTimeInMeetingsChartDataQuery } from '../src/interface/queries/challenge-insights/time-in-meetings/get-account-metrics-insight-time-in-meetings-chart-data.query';
import { GetAccountMetricsInsightTimeInMeetingsTotalQuery } from '../src/interface/queries/challenge-insights/time-in-meetings/get-account-metrics-insight-time-in-meetings-total.query';
import { ListAccountMetricsInsightTimeInMeetingsSummaryQuery } from '../src/interface/queries/challenge-insights/time-in-meetings/list-account-metrics-insight-time-in-meetings-summary.query';
import { GetSelfCalendarTimeInAppGoalQuery } from '../src/interface/queries/goals/get-self-calendar-time-in-app.query';
import { GetSelfCallTimeInAppGoalQuery } from '../src/interface/queries/goals/get-self-call-time-in-app-goal.query';
import { GetSelfDigitalIntensityScoreGoalQuery } from '../src/interface/queries/goals/get-self-digital-intensity-score-goal.query';
import { GetSelfDigitalWorkingHoursScoreGoalQuery } from '../src/interface/queries/goals/get-self-digital-working-hours.query';
import { GetSelfFocusScoreGoalQuery } from '../src/interface/queries/goals/get-self-focus-score-goal.query';
import { GetSelfInteractionsGoalQuery } from '../src/interface/queries/goals/get-self-interactions-goal.query';
import { GetSelfScreentimeScoreGoalQuery } from '../src/interface/queries/goals/get-self-screentime-score-goal.query';
import { GetSelfTimeInAppGoalQuery } from '../src/interface/queries/goals/get-self-time-in-app-goal.query';
import { GetSelfTimeInCallsScoreGoalQuery } from '../src/interface/queries/goals/get-self-time-in-calls-score.query';
import { GetSelfTimeInMeetingsScoreGoalQuery } from '../src/interface/queries/goals/get-self-time-in-meetings-score-goal.query';
import { GetAccountAppCategoryMetricsPerAppQuery } from '../src/interface/queries/org-metrics/apps/get-account-app-category-metrics-per-app.query';
import { ListAccountMetricsInsightTimeInAppQuery } from '../src/interface/queries/org-metrics/apps/list-account-metrics-insight-time-in-app.query';
import { GetAccountAverageCallsPerPersonSummaryQuery } from '../src/interface/queries/org-metrics/calls/get-account-average-calls-per-person-summary.query';
import { GetAccountAverageCallsPerWorkspaceTimeseriesQuery } from '../src/interface/queries/org-metrics/calls/get-account-average-calls-per-workspace-time-series.query';
import { GetAccountCallEventsTimeSeriesQuery } from '../src/interface/queries/org-metrics/calls/get-account-call-events-time-series.query';
import { GetAccountCallsPerWorkspaceSummaryQuery } from '../src/interface/queries/org-metrics/calls/get-account-calls-per-workspace-summary.query';
import { GetAccountCallsSummaryQuery } from '../src/interface/queries/org-metrics/calls/get-account-calls-summary.query';
import { GetAccountAppTotalsPerCategorySummaryQuery } from '../src/interface/queries/org-metrics/category/get-account-app-totals-per-category-summary.query';
import { GetAccountAverageMeetingsPerPersonSummaryQuery } from '../src/interface/queries/org-metrics/meetings/get-account-average-meetings-per-person-summary.query';
import { GetAccountMeetingEventsSummaryQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meeting-events-summary.query';
import { GetAccountMeetingEventsTimeSeriesQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meeting-events-time-series.query';
import { GetAccountMeetingHoursBySizeSummaryQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meeting-hours-by-size-summary.query';
import { GetAccountMeetingHoursBySizeTimeSeriesQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meeting-hours-by-size-time-series.query';
import { GetAccountMeetingsPerWorkspaceSummaryQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meetings-per-workspace-summary.query';
import { GetAccountMeetingsPerWorkspaceTimeSeriesQuery } from '../src/interface/queries/org-metrics/meetings/get-account-meetings-per-workspace-time-series.query';
import { GetAccountAverageWorkingHoursPerPersonDistributionQuery } from '../src/interface/queries/org-metrics/working-hours/get-account-average-working-hours-per-person-distribution.query';
import { GetAccountAverageWorkingHoursPerPersonRangesQuery } from '../src/interface/queries/org-metrics/working-hours/get-account-average-working-hours-per-person-ranges.query';
import { GetAccountAverageWorkingHoursPerPersonTimeSeriesQuery } from '../src/interface/queries/org-metrics/working-hours/get-account-average-working-hours-per-person-time-series.query';
import { GetAccountAverageWorkingHoursSummaryQuery } from '../src/interface/queries/org-metrics/working-hours/get-account-average-working-hours-summary.query';
import { GetAccountAverageWorkingHoursTimeSeriesQuery } from '../src/interface/queries/org-metrics/working-hours/get-account-average-working-hours-time-series.query';
import { GetSelfAppAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-app-averages-summary.query';
import { GetSelfAppCalendarEventsAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-app-calendar-events-averages-summary.query';
import { GetSelfCalendarScoreQuery } from '../src/interface/queries/scores/get-self-calendar-score.query';
import { GetSelfDigitalIntensityScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-digital-intensity-score-averages-summary.query';
import { GetSelfDigitalIntensityScoreQuery } from '../src/interface/queries/scores/get-self-digital-intensity-score.query';
import { GetSelfDigitalWorkingHoursScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-digital-working-hours-score-averages-summary.query';
import { GetSelfDigitalWorkingHoursScoreQuery } from '../src/interface/queries/scores/get-self-digital-working-hours-score.query';
import { GetSelfFocusScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-focus-score-averages-summary.query';
import { GetSelfFocusScoreQuery } from '../src/interface/queries/scores/get-self-focus-score.query';
import { GetSelfScreentimeScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-screentime-score-averages-summary.query';
import { GetSelfScreentimeScoreQuery } from '../src/interface/queries/scores/get-self-screentime-score.query';
import { GetSelfTimeInCallsScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-time-in-calls-score-averages-summary.query';
import { GetSelfTimeInCallsScoreQuery } from '../src/interface/queries/scores/get-self-time-in-calls-score.query';
import { GetSelfTimeInMeetingsScoreAveragesSummaryQuery } from '../src/interface/queries/scores/get-self-time-in-meetings-score-averages-summary.query';
import { GetAccountSentimentReportAverageSummaryQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-average-summary.query';
import { GetAccountSentimentReportAverageTimeSeriesQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-average-time-series.query';
import { GetAccountSentimentReportAverageTotalsMetricsQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-average-totals.query';
import { GetAccountSentimentReportPercentilesAverageMetricsQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-percentiles-average.query';
import { GetAccountSentimentReportPercentilesSummaryQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-percentiles-summary.query';
import { GetAccountSentimentReportTimeSeriesQuery } from '../src/interface/queries/sentiment/get-account-sentiment-report-time-series.query';
import { GetSelfSentimentReportTimeSeriesQuery } from '../src/interface/queries/sentiment/get-self-sentiment-report-time-series.query';
import { GetSelfSentimentReportQuery } from '../src/interface/queries/sentiment/get-self-sentiment-report.query';
import { GetTeamMetricsAppsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-apps-summary-average-whole-team.query';
import { GetTeamMetricsAppsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-apps-summary-total-individual.query';
import { GetTeamMetricsAppsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-apps-summary-total-whole-team.query';
import { GetTeamMetricsCalendarAppTimeSeriesTotalIndividualQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-app-times-series-total-individual.query';
import { GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-app-times-series-total-whole-team.query';
import { GetTeamMetricsCalendarAppTrendsTimeSeriesQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-app-trends-time-series.query';
import { GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-average-whole-team.query';
import { GetTeamMetricsCalendarChartAllTimeAveragesIndividualQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages-individual.query';
import { GetTeamMetricsCalendarChartAllTimeAveragesQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-chart-all-time-averages.query';
import { GetTeamMetricsCalendarMeetingsSummaryIndividualQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-meetings-summary-individual.query';
import { GetTeamMetricsCalendarMeetingsSummaryQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-meetings-summary.query';
import { GetTeamMetricsCalendarViewsSummaryQuery } from '../src/interface/queries/team-metrics/calendar/get-team-metrics-calendar-views-summary.query';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-calendar-score-time-series-metrics-summary-average-whole-team.query';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-calendar-score-time-series-metrics-summary-total-individual.query';
import { GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-calendar-score-time-series-metrics-summary-total-whole-team.query';
import { GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-metrics-calendar-score-time-series-average-whole-team.query';
import { GetTeamMetricsCalendarScoreTimeSeriesIndividualQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-metrics-calendar-score-time-series-individual.query';
import { GetTeamMetricsCalendarScoreTimeSeriesQuery } from '../src/interface/queries/team-metrics/calendar/scores/metrics/get-team-metrics-calendar-score-time-series.query';
import { GetTeamMetricsCallsEventsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/calls/get-team-metrics-calls-events-summary-average-whole-team.query';
import { GetTeamMetricsCallsEventsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/calls/get-team-metrics-calls-events-summary-total-individual.query';
import { GetTeamMetricsCallsEventsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/calls/get-team-metrics-calls-events-summary-total-whole-team.query';
import { GetCategoryMetricsAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/category/get-category-metrics-average-whole-team.query';
import { GetCategoryMetricsTotalIndividualQuery } from '../src/interface/queries/team-metrics/category/get-category-metrics-totals-individual.query';
import { GetCategoryMetricsTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/category/get-category-metrics-totals-whole-team.query';
import { GetAppMetricsAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/get-app-metrics-average-whole-team.query';
import { GetAppMetricsTimeSeriesAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/get-app-metrics-time-series-average-whole-team.query';
import { GetAppMetricsTimeSeriesTotalIndividualQuery } from '../src/interface/queries/team-metrics/get-app-metrics-time-series-total-individual.query';
import { GetAppMetricsTimeSeriesTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/get-app-metrics-time-series-total-whole-team.query';
import { GetAppMetricsTotalIndividualQuery } from '../src/interface/queries/team-metrics/get-app-metrics-totals-individual.query';
import { GetAppMetricsTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/get-app-metrics-totals-whole-team.query';
import { GetTeamMetricsMeetingsEventSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/meetings/get-team-metrics-meetings-event-summary-average-whole-team.query';
import { GetTeamMetricsMeetingsEventSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-individual.query';
import { GetTeamMetricsMeetingsEventSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/meetings/get-team-metrics-meetings-event-summary-total-whole-team.query';
import { GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-apps-summary-average-whole-team.query';
import { GetTeamSubscriptionMetricsAppsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-individual.query';
import { GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-apps-summary-total-whole-team.query';
import { GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-average-whole-team.query';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-individual.query';
import { GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/subscription/get-team-subscription-metrics-meetings-summary-total-whole-team.query';
import { GetAppMetricsAverageSummaryWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-app-metrics-average-summary-whole-team.query';
import { GetAppMetricsTotalSummaryIndividualQuery } from '../src/interface/queries/team-metrics/summary/get-app-metrics-total-summary-individual.query';
import { GetAppMetricsTotalSummaryWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-app-metrics-total-summary-whole-team.query';
import { GetTeamMetricsAppViewsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-app-views-summary-average-whole-team.query';
import { GetTeamMetricsAppViewsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-app-views-summary-total-individual.query';
import { GetTeamMetricsAppViewsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-app-views-summary-total-whole-team.query';
import { GetTeamMetricsMeetingsSummaryAverageWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-meetings-summary-average-whole-team.query';
import { GetTeamMetricsMeetingsSummaryTotalIndividualQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-meetings-summary-total-individual.query';
import { GetTeamMetricsMeetingsSummaryTotalWholeTeamQuery } from '../src/interface/queries/team-metrics/summary/get-team-metrics-meetings-summary-total-whole-team.query';
import { GetAccountAppCategoriesPerWorkspaceSummaryQuery } from '../src/interface/queries/top-apps/get-account-app-categories-per-workspace-summary.query';
import { GetAccountAppMetricsPerWorkspaceSummaryQuery } from '../src/interface/queries/top-apps/get-account-app-metrics-per-workspace-summary.query';
import { GetAccountTopAppsMetricsSummaryQuery } from '../src/interface/queries/top-apps/get-account-top-apps-metrics-summary.query';
import { GetSelfWorkingSessionsQuery } from '../src/interface/queries/working-sessions/get-self-working-sessions.query';
import { CubeConfigDto } from './dto/common/request/cube-config.dto';
import { TimeSeriesResponseDto } from './dto/common/response/time-series-response.dto';
import { GetAppAveragesRequestParamsDto } from './dto/goals/request/get-app-averages-request.dto';
import { GetGoalSummariesRequestParamsDto } from './dto/goals/request/get-goal-summaries-request.dto';
import { GetGoalSummaryRequestParamsDto } from './dto/goals/request/get-goal-summary-request.dto';
import { GetScoreAveragesRequestParamsDto } from './dto/goals/request/get-score-averages-request.dto';
import { AppAveragesResponseDto } from './dto/goals/response/app-averages-response.dto';
import { GoalsReportResponseDto } from './dto/goals/response/goals-report-response.dto';
import { ScoreAveragesResponseDto } from './dto/goals/response/score-averages-response.dto';
import { GetAllScoresRequestParamsDto } from './dto/scores/request/get-all-scores-request.dto';
import { GetScoreTimeSeriesV2RequestParamsDto } from './dto/scores/request/get-score-time-series-v2-request.dto';
import { AllScoresResponseDto } from './dto/scores/response/scores-response.dto';
import { FilteredTeamAppMetricsV2Result } from './dto/team-app-metrics/cube/mapped/filtered-team-app-metrics-result.dto';
import { GetFilteredTeamAppMetricsRequestParamsDto } from './dto/team-app-metrics/request/get-filtered-team-app-metrics-request.dto';
import { GetFilteredTeamSubscriptionMetricsRequestParamsDto } from './dto/team-app-metrics/request/get-filtered-team-subscription-metrics-request.dto';
import { GetTeamAppMetricsRequestParamsDto } from './dto/team-app-metrics/request/get-team-app-metrics-request.dto';
import { FilteredTeamAppMetricsV2ResponseDto } from './dto/team-app-metrics/response/filtered-team-app-metrics-response.dto';
import { FilteredTeamSubscriptionMetricsResponseDto } from './dto/team-app-metrics/response/filtered-team-subscription-metrics-response.dto';
import { TeamAppMetricsResponseDto } from './dto/team-app-metrics/response/team-app-metrics-response.dto';
import { TeamTimeSeriesAppMetricsResponseDto } from './dto/team-app-metrics/response/team-time-series-response.dto';
import { TopAppsReportV2Result } from './dto/top-apps/cube/mapped/top-apps-report-result.dto';
import { GetAppTimeSeriesRequestParamsDto } from './dto/top-apps/request/get-app-time-series-request.dto';
import { GetTopAppsReportRequestParamsDto } from './dto/top-apps/request/get-top-apps-report-request.dto';
import { TopAppsReportV2ResponseDto } from './dto/top-apps/response/top-apps-report-response.dto';
import { QueryErrorMessages } from './enum/cube-error-message.enum';
import {
  AppGoalsCubeMetric,
  CubeTimeDimensionGranularity,
  Weekdays,
} from './enum/cube-schema.enum';
import { CoreScoreNames } from './enum/score-name-cube.enum';
import { ITeamAnalyticsActivityV2Enum } from './enum/team-analytics-activity/team-analytics-activity.enum';
import { getFirstMondayString } from './helpers/datetime.helper';
import {
  calculateDeviation,
  fillEmptyDatesForTimeSeries,
  formatDuration,
} from './helpers/duration.helper';
import { hydrateReforgedHeatmaps } from './helpers/heatmaps/heat-map-timestamps.helper';
import { ICubeRepository } from './interface/cube-repository.interface';
import {
  HeatMap,
  HeatmapIntervalSegment,
} from './interface/heat-maps/heat-map.interface';
import { mapToAppAveragesResponse } from './mappers/app-averages-result.mapper';
import { mapToAppTimeSeriesDetailedResults } from './mappers/app-cumulative-avg-result.mapper';
import { mapAppTimeSeriesResult } from './mappers/app-time-series-result.mapper';
import { HeatmapReforgeMapper } from './mappers/heatmap/heatmap.mapper';
import { mapToScoreTimeSeriesDetailedResultsV2 } from './mappers/scores/score-time-series-detailed-result.mapper';
import { mapTimeSeriesResultV2 } from './mappers/scores/score-time-series-result.mapper';
import { mapAllScoresResultsToScore } from './mappers/scores/scores-result.mapper';
import {
  mapDailyFilteredTeamAppMetricsV2,
  mapDailyFilteredTeamSubscriptionMetrics,
  mapTeamAppMetrics,
} from './mappers/team-analytics-activity.mapper';
import { TopAppsReportMapperV2 } from './mappers/top-apps-report.mapper';
import { CubeRepository } from './repos/cube.repository';
import {
  GetTeamHeatmapProps,
  GetUserHeatmapProps,
} from './repos/heatmap/heatmap-repository.interface';
import { HeatmapRepository } from './repos/heatmap/heatmap.repository';
import { AllScoresRepositoryV2 } from './repos/scores.repository-v2';
import { TeamAnalyticsActivityRepositoryV2 } from './repos/team-analytics-activity-v2.repository';
import { TopAppsReportRepositoryV2 } from './repos/top-apps-report-v2.repository';
import { AccountMeetingHoursBySizeTimeSeriesAggregateRootMapper } from '../src/infrastructure/mappers/org-metrics/meetings/account-meeting-hours-by-size-time-series.aggregate-root.mapper';
import { WorkingHoursByRangeDistributionResponseDto } from '../src/interface/dtos/working-hours/working-hours-by-range-distribution.response.dto';
import { WorkingHoursByRangeDistributionRequestDto } from '../src/interface/dtos/working-hours/working-hours-by-range-distribution.request.dto';
import { WorkingHoursByRangeDistibutionAggregateRootMapper } from '../src/infrastructure/mappers/working-hours/working-hours-by-range-distribution.mapper';
import { WorkingHoursByRangeDistributionDomainService } from '../src/core/domain/services/working-hours/working-hours-by-range-distribution.domain.service';
import { WorkingHoursByRangeDistributionApplicationService } from '../src/core/application/services/working-hours/working-hours-by-range-distribution.service';
import { WorkingHoursByRangeDistributionQuery } from '../src/interface/queries/working-hours/working-hours-by-range-distribution.query';
import { WorkingHoursByRangeDistributionController } from '../src/interface/controllers/working-hours/working-hours-by-range-distribution.controller';
import { GetUserWorkingSessionsRequestDto } from '../src/interface/dtos/working-sessions/user/get-user-working-sessions.request.dto';
import { GetUserWorkingSessionsResponseDto } from '../src/interface/dtos/working-sessions/user/get-user-working-sessions.response.dto';
import { GetUserWorkingSessionsApplicationService } from '../src/core/application/services/working-sessions/get-user-working-sessions.application-service';
import { GetUserWorkingSessionsController } from '../src/interface/controllers/working-sessions/get-user-working-sessions.controller';
import { GetUserWorkingSessionsQuery } from '../src/interface/queries/working-sessions/get-user-working-sessions.query';
import { OrgWorkingSessionsEntityMapper } from '../src/infrastructure/mappers/working-sessions/org-working-sessions.entity.mapper';

export interface IReforgedCubeHelperMethods {
  getAccountAverageWorkingHoursPerPersonTimeSeries(
    params: GetAccountAverageWorkingHoursPerPersonTimeSeriesRequestDto
  ): Promise<GetAccountAverageWorkingHoursPerPersonTimeSeriesResponseDto>;
  getSelfMetricsInsightsChallengeTargetStreak(
    params: GetSelfMetricsInsightsChallengeTargetStreakRequestDto
  ): Promise<GetSelfMetricsInsightsChallengeTargetStreakResponseDto>;
  getAccountMetricsInsightsFocusV1Summary(
    params: GetAccountMetricsInsightsFocusV1SummaryRequestDto
  ): Promise<GetAccountMetricsInsightsFocusV1SummaryResponseDto>;
  getAccountMetricsInsightsSentimentSummary(
    params: GetAccountMetricsInsightsSentimentSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsSentimentSummaryResponseDto>;
  getAccountMetricsInsightsTimeInCallsSummary(
    params: GetAccountMetricsInsightsTimeInCallsSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInCallsSummaryResponseDto>;
  getAccountMetricsInsightsTimeInMeetingsSummary(
    params: GetAccountMetricsInsightsTimeInMeetingsSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInMeetingsSummaryResponseDto>;
  getAccountMetricsInsightsTimeInAppSummary(
    params: GetAccountMetricsInsightsTimeInAppSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInAppSummaryResponseDto>;
  getAccountMetricsInsightsTimeInCategorySummary(
    params: GetAccountMetricsInsightsTimeInCategorySummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInCategorySummaryResponseDto>;
  getAccountMetricsInsightsDigitalIntensitySummary(
    params: GetAccountMetricsInsightsDigitalIntensitySummaryRequestDto
  ): Promise<GetAccountMetricsInsightsDigitalIntensitySummaryResponseDto>;
  getAccountMetricsInsightsDigitalWorkingHoursSummary(
    params: GetAccountMetricsInsightsDigitalWorkingHoursSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsDigitalWorkingHoursSummaryResponseDto>;
  getChallengeInsightProgressSummary(
    params: GetChallengeInsightProgressSummaryRequestDto
  ): Promise<GetChallengeInsightProgressSummaryResponseDto>;
  getAccountMetricsInsightsChartData(
    params: GetAccountMetricsInsightsChartDataRequestDto
  ): Promise<GetAccountMetricsInsightsChartDataResponseDto>;
  getAccountMetricsInsightsAverages(
    params: GetAccountMetricsInsightsAveragesRequestDto
  ): Promise<GetAccountMetricsInsightsAveragesResponseDto>;
  getAccountMetricsInsightsTotals(
    params: GetAccountMetricsInsightsTotalsRequestDto
  ): Promise<GetAccountMetricsInsightsTotalsResponseDto>;
  listAccountMetricsInsightsSummaries(
    params: ListAccountMetricsInsightsSummariesRequestDto
  ): Promise<ListAccountMetricsInsightsSummariesResponseDto>;
  getAccountAppCategoryMetricsPerApp(
    params: GetAccountAppCategoryMetricsPerAppRequestDto
  ): Promise<GetAccountAppCategoryMetricsPerAppResponseDto>;
  getAccountAppTotalsPerCategorySummary(
    params: GetAccountAppTotalsPerCategorySummaryRequestDto
  ): Promise<GetAccountAppTotalsPerCategorySummaryResponseDto>;
  getCategoryMetricsTotalIndividual(
    params: GetCategoryMetricsTotalsIndividualRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto>;
  getCategoryMetricsTotalWholeTeam(
    params: GetCategoryMetricsTotalsWholeTeamRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto>;
  getCategoryMetricsAverageWholeTeam(
    params: GetCategoryMetricsAverageWholeTeamRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto>;
  getSelfAppAveragesSummary(
    params: GetSelfAppAveragesSummaryRequestDto
  ): Promise<GetSelfAppAveragesSummaryResponseDto>;
  getSelfAppCategoryAveragesSummary(
    params: GetSelfAppCategoryAveragesSummaryRequestDto
  ): Promise<GetSelfAppCategoryAveragesSummaryResponseDto>;
  getSelfAppCategoryTimeSeries(
    params: GetSelfAppCategoryTimeSeriesRequestDto
  ): Promise<GetSelfAppCategoryTimeSeriesResponseDto>;
  getAccountAverageWorkingHoursPerPersonRangesSummary(
    params: GetAccountAverageWorkingHoursPerPersonRangesRequestDto
  ): Promise<GetAccountAverageWorkingHoursPerPersonRangesResponseDto>;
  getAccountAverageWorkingHoursPerPersonDistribution(
    params: GetAccountAverageWorkingHoursPerPersonDistributionRequestDto
  ): Promise<GetAccountAverageWorkingHoursPerPersonDistributionResponseDto>;
  expandAccountCallTimeSeries(
    params: ExpandAccountCallEventsTimeSeriesRequestDto
  ): GetAccountCallEventsTimeSeriesResponseDto[];
  getCallsPerWorkspaceTimeSeries(
    params: GetAccountAverageCallsPerWorkspaceTimeseriesRequestDto
  ): Promise<GetAccountAverageCallsPerWorkspaceTimeseriesResponseDto>;
  getAccountCallsAveragePerPersonSummary(
    params: GetAccountAverageCallsPerPersonSummaryRequestDto
  ): Promise<GetAccountAverageCallsPerPersonSummaryResponseDto>;
  getAccountMeetingsAveragePerPersonSummary(
    params: GetAccountMeetingsAveragePerPersonSummaryRequestDto
  ): Promise<GetAccountMeetingsAveragePerPersonSummaryResponseDto>;
  getAccountAverageWorkingHoursTimeSeries(
    params: GetAccountAverageWorkingHoursTimeSeriesRequestDto
  ): Promise<GetAccountAverageWorkingHoursTimeSeriesResponseDto>;
  getAccountAverageWorkingHoursSummary(
    params: GetAccountAverageWorkingHoursSummaryRequestDto
  ): Promise<GetAccountAverageWorkingHoursSummaryResponseDto>;
  getSelfSentimentReport(
    params: GetSelfSentimentReportRequestDto
  ): Promise<GetSelfSentimentReportResponseDto>;
  getAccountSentimentReportAverageTotals(
    params: GetAccountSentimentReportAverageTotalsRequestDto
  ): Promise<GetAccountSentimentReportAverageTotalsResponseDto>;
  getAccountSentimentReportAverageSummary(
    params: GetAccountSentimentReportAverageSummaryRequestDto
  ): Promise<GetAccountSentimentReportAverageSummaryResponseDto>;
  getAccountSentimentReportPercentilesAverage(
    params: GetAccountSentimentReportPercentilesAverageRequestDto
  ): Promise<GetAccountSentimentReportPercentilesAverageResponseDto>;
  getAccountSentimentReportTimeSeries(
    params: GetAccountSentimentReportTimeSeriesRequestDto
  ): Promise<GetAccountSentimentReportTimeSeriesResponseDto>;
  getAccountSentimentReportAverageTimeSeries(
    params: GetAccountSentimentReportAverageTimeSeriesRequestDto
  ): Promise<GetAccountSentimentReportAverageTimeSeriesResponseDto>;
  getSelfSentimentReportTimeSeries(
    params: GetSelfSentimentReportTimeSeriesRequestDto
  ): Promise<GetSelfSentimentReportTimeSeriesResponseDto>;
  getAppMetricsTotalsIndividual(
    params: GetAppMetricsTotalsIndividualRequestDto
  ): Promise<GetAppMetricsTotalsIndividualResponseDto>;
  getAppMetricsTotalsWholeTeam(
    params: GetAppMetricsTotalsWholeTeamRequestDto
  ): Promise<GetAppMetricsTotalsWholeTeamResponseDto>;
  getAppMetricsTotalSummaryWholeTeam(
    params: GetAppMetricsTotalSummaryWholeTeamRequestDto
  ): Promise<GetAppMetricsTotalSummaryWholeTeamResponseArray>;
  getAppMetricsAverageWholeTeam(
    params: GetAppMetricsAverageWholeTeamRequestDto
  ): Promise<GetAppMetricsAverageWholeTeamResponseDto>;
  getAppMetricsTimeSeriesTotalsIndividual(
    params: GetAppMetricsTimeSeriesTotalsIndividualRequestDto
  ): Promise<GetAppMetricsTimeSeriesTotalsIndividualResponseDto[]>;
  getAppMetricsTimeSeriesTotalsWholeTeam(
    params: GetAppMetricsTimeSeriesTotalsWholeTeamRequestDto
  ): Promise<GetAppMetricsTimeSeriesTotalsWholeTeamResponseDto[]>;
  getAppMetricsTimeSeriesAverageWholeTeam(
    params: GetAppMetricsTimeSeriesAverageWholeTeamRequestDto
  ): Promise<GetAppMetricsTimeSeriesAverageWholeTeamResponseDto[]>;
  getAppMetricsAverageSummaryWholeTeam(
    params: GetAppMetricsAverageSummaryWholeTeamRequestDto
  ): Promise<GetAppMetricsAverageSummaryWholeTeamResponseArray>;
  getAppMetricsTotalSummaryIndividual(
    params: GetAppMetricsTotalSummaryIndividualRequestDto
  ): Promise<GetAppMetricsTotalSummaryIndividualResponseArray>;
  getSelfAllScores(
    params: GetSelfAllScoresRequestDto
  ): Promise<SelfGetAllScoresResponseDto>;
  getSelfCalendarChartAllTimeAverages(
    params: GetCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetCalendarChartAllTimeAveragesResponseDto>;
  getAccountCallsSummary(
    params: GetAccountCallsSummaryRequestDto
  ): Promise<GetAccountCallsSummaryResponseDto>;
  getAccountMeetingSummary(
    params: GetAccountMeetingEventsSummaryRequestDto
  ): Promise<GetAccountMeetingEventsSummaryResponseDto>;
  getAccountCallsPerWorkspaceSummary(
    params: GetAccountCallsPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountCallsPerWorkspaceSummaryResponseDto>;
  getAccountTopAppsMetricsSummary(
    params: GetAccountTopAppsMetricsSummaryRequestDto
  ): Promise<GetAccountTopAppsMetricsSummaryResponseDto>;
  getAccountAppMetricsPerWorkspaceSummary(
    params: GetAccountAppMetricsPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountAppMetricsPerWorkspaceSummaryResponseDto>;
  getAccountMeetingTimeSeries(
    params: GetAccountMeetingEventsTimeSeriesRequestDto
  ): Promise<GetAccountMeetingEventsTimeSeriesResponseDto[]>;
  getAccountCallTimeSeries(
    params: GetAccountCallEventsTimeSeriesRequestDto
  ): Promise<GetAccountCallEventsTimeSeriesResponseDto[]>;
  getMeetingHoursBySizeSummary(
    params: GetAccountMeetingHoursBySizeSummaryRequestDto
  ): Promise<GetAccountMeetingHoursBySizeSummaryResponseDto[]>;
  getMeetingHoursBySizeTimeSeries(
    params: GetAccountMeetingHoursBySizeTimeSeriesRequestDto
  ): Promise<GetAccountMeetingHoursBySizeTimeSeriesResponseDto[]>;
  getAccountMeetingsPerWorkspaceSummary(
    params: GetAccountMeetingsPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountMeetingsPerWorkspaceSummaryResponseDto>;
  getAccountMeetingsPerWorkspaceTimeSeries(
    params: GetAccountMeetingsPerWorkspaceTimeSeriesRequestDto
  ): Promise<GetAccountMeetingsPerWorkspaceTimeSeriesResponseDto>;
  getAccountSentimentReportPercentilesSummary(
    params: GetAccountSentimentReportPercentilesSummaryRequestDto
  ): Promise<GetAccountSentimentReportPercentilesSummaryResponseDto>;
  getAccountAppCategoriesPerWorkspaceSummary(
    params: GetAccountAppCategoriesPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountAppCategoriesPerWorkspaceSummaryResponseDto>;
  getAccountAppCategoriesSummary(
    params: GetAccountAppCategoriesSummaryRequestDto
  ): Promise<GetAccountAppCategoriesSummaryResponseDto>;
  getSelfTopAppCategories(
    params: GetSelfTopAppCategoriesRequestDto
  ): Promise<GetSelfTopAppCategoriesResponseDto>;
  getAccountTotalAppCategoriesSummary(
    params: GetAccountTotalAppCategoriesSummaryRequestDto
  ): Promise<GetAccountTotalAppCategoriesSummaryResponseDto>;
  getAccountAppCategoriesTimeSeries(
    params: GetAccountAppCategoriesTimeSeriesRequestDto
  ): Promise<GetAccountAppCategoriesTimeSeriesResponseDto>;
  getWorkspaceCategoryMetricsUsageTotals(
    params: GetWorkspaceCategoryMetricsUsageTotalsRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsageTotalsResponseDto>;
  getWorkspaceCategoryMetricsUsageWholeTeamTimeSeries(
    params: GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesResponseDto>;

  getTeamMetricsAppsSummaryTotalWholeTeam(
    params: GetTeamMetricsAppsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppsSummaryResponseDto>;
  getTeamMetricsAppsSummaryTotalIndividual(
    params: GetTeamMetricsAppsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsAppsSummaryResponseDto>;
  getTeamSubscriptionMetricsAppsSummaryAverageWholeTeam(
    params: GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto>;
  getTeamSubscriptionMetricsAppsSummaryTotalWholeTeam(
    params: GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto>;
  getTeamSubscriptionMetricsAppsSummaryTotalIndividual(
    params: GetTeamSubscriptionMetricsAppsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto>;
  getTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeam(
    params: GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto>;
  getTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeam(
    params: GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto>;
  getTeamSubscriptionMetricsMeetingsSummaryTotalIndividual(
    params: GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto>;
  getTeamMetricsMeetingsSummaryTotalWholeTeam(
    params: GetTeamMetricsMeetingsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto>;
  getTeamMetricsMeetingsSummaryAverageWholeTeam(
    params: GetTeamMetricsMeetingsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto>;
  getTeamMetricsMeetingsSummaryTotalsIndividual(
    params: GetTeamMetricsMeetingsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto>;
  getTeamMetricsMeetingsEventSummaryTotalWholeTeam(
    params: GetTeamMetricsMeetingsEventSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]>;
  getTeamMetricsMeetingsEventSummaryAverageWholeTeam(
    params: GetTeamMetricsMeetingsEventSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]>;
  getTeamMetricsMeetingsEventSummaryTotalIndividual(
    params: GetTeamMetricsMeetingsEventSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]>;
  getTeamMetricsCalendarAppTimeSeriesTotalWholeTeam(
    params: GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsCalendarAppTimeSeriesWholeTeamResponseDto[]>;
  getTeamMetricsCalendarAppTimeSeriesTotalIndividual(
    params: GetTeamMetricsCalendarAppTimeSeriesTotalIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarAppTimeSeriesResponseDto[]>;
  getMySessions(
    params: GetSelfWorkingSessionsRequestDto
  ): Promise<GetSelfWorkingSessionsResponseDto[]>;
  getTeamMetricsAppViewsSummaryTotalsWholeTeam(
    params: GetTeamMetricsAppViewsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto>;
  getTeamMetricsAppViewsSummaryAverageWholeTeam(
    params: GetTeamMetricsAppViewsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto>;
  getTeamMetricsAppViewsSummaryTotalsIndividual(
    params: GetTeamMetricsAppViewsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto>;
  getTeamMetricsCalendarMeetingsSummary(
    params: GetTeamMetricsCalendarMeetingsSummaryRequestDto
  ): Promise<GetTeamMetricsCalendarMeetingsSummaryResponseDto>;
  getTeamMetricsCalendarMeetingsSummaryIndividual(
    params: GetTeamMetricsCalendarMeetingsSummaryIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarMeetingsSummaryResponseDto>;
  getTeamMetricsCalendarViewsSummary(
    params: GetTeamMetricsCalendarViewsSummaryRequestDto
  ): Promise<GetTeamMetricsCalendarViewsSummaryResponseDto>;
  getSelfAppCategoryScoreTimeSeriesMetricsSummary(
    params: GetSelfAppCategoryScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetSelfAppCategoryScoreTimeSeriesMetricsSummaryResponseDto>;
  getWorkspaceCategoryMetricsUsagePerIndividualTimeSeries(
    params: GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesResponseDto>;
  getTeamMetricsCalendarAppTrendsTimeSeries(
    params: GetTeamMetricsCalendarAppTrendsTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarAppTrendsTimeSeriesResponseDto[]>;
  getSelfAppCategoryChartAllTimeAverage(
    params: GetSelfAppCategoryChartAllTimeAverageRequestDto
  ): Promise<GetSelfAppCategoryChartAllTimeAverageResponseDto>;
  getTeamCalendarScoreTimeSeriesMetricsSummary(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto>;
  getTeamCalendarScoreTimeSeries(
    params: GetTeamMetricsCalendarScoreTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto>;
  getTeamCalendarChartAllTimeAverages(
    params: GetTeamMetricsCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto>;
  getTeamCalendarChartAllTimeAveragesAverageWholeTeam(
    params: GetTeamMetricsCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto>;
  getTeamCalendarScoreTimeSeriesMetricsSummaryIndividual(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto>;
  getTeamCalendarScoreTimeSeriesIndividual(
    params: GetTeamMetricsCalendarScoreTimeSeriesIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto>;
  getTeamCalendarChartAllTimeAveragesIndividual(
    params: GetTeamMetricsCalendarChartAllTimeAveragesIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto>;
  getTeamCalendarScoreTimeSeriesMetricsSummaryAverage(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto>;
  getTeamCalendarScoreTimeSeriesAverageWholeTeam(
    params: GetTeamMetricsCalendarScoreTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto>;
  getUserSessions(
    params: GetUserWorkingSessionsRequestDto
  ): Promise<GetUserWorkingSessionsResponseDto[]>;
}

export interface ICubeQueryHelper extends IReforgedCubeHelperMethods {
  //used for self heatmaps
  getSelfHeatmap(props: GetUserHeatmapProps): Promise<HeatMap>;
  //used for workspace heatmap
  getWorkspaceHeatmapV2(props: GetTeamHeatmapProps): Promise<HeatMap>;

  //used on top apps dashboard component
  getTopAppsReportV2(
    params: GetTopAppsReportRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TopAppsReportV2ResponseDto[]>;

  //used on team apps metrics component
  getTeamAppMetricsV2(
    params: GetTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamAppMetricsResponseDto>;
  //used on team app timeseries component
  getTeamTimeSeriesAppMetricsV2(
    params: GetTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesAppMetricsResponseDto>;

  //@deprecated
  //used in useFilteredTeamMetricsLazyQueryDeprecated
  getFilteredTeamAppMetricsV2(
    params: GetFilteredTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamAppMetricsV2ResponseDto>;

  //used in useFilteredTeamMetricsSubscriptionsLazyQuery
  getFilteredTeamSubscriptionMetricsV2(
    params: GetFilteredTeamSubscriptionMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamSubscriptionMetricsResponseDto[]>;

  getAllScoresV2(
    params: GetAllScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllScoresResponseDto>;

  //used in score modal

  getScoreTimeSeriesV2(
    params: GetScoreTimeSeriesV2RequestParamsDto,
    config: CubeConfigDto
  ): Promise<TimeSeriesResponseDto>;

  // used in metrics chart wrapper
  getAppTimeSeriesV2(
    params: GetAppTimeSeriesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TimeSeriesResponseDto>;

  // used in score averages wrapper
  getScoreAveragesV2(
    params: GetScoreAveragesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<ScoreAveragesResponseDto>;

  //used in goal summary query
  getGoalSummaryV3(
    params: GetGoalSummaryRequestParamsDto,
    config: CubeConfigDto
  ): Promise<GoalsReportResponseDto>;

  //used in goal summaries query

  getGoalSummariesV3(
    params: GetGoalSummariesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<GoalsReportResponseDto[]>;

  // used in edit goals modal
  getAppAveragesV2(
    params: GetAppAveragesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AppAveragesResponseDto>;
}

@WithSentryClassDecorator
export class CubeQueryHelper implements ICubeQueryHelper {
  cubeRepository: ICubeRepository;
  adapter: CubeAdapter;
  logManager: LogManager;
  useLogger: boolean;

  constructor(deps: {
    endpoint: string;
    debug?: boolean;
    poolConfig?: {
      maxConnections?: number;
      idleTimeout?: number;
      connectionTimeout?: number;
      retryAttempts?: number;
      retryDelay?: number;
    };
    sentry?: {
      dsn: string;
      environment: string;
      tracesSampleRate: number;
    };
  }) {
    this.useLogger = deps.debug ? deps.debug : false;
    this.cubeRepository = new CubeRepository({
      endpoint: deps.endpoint,
      debug: this.useLogger,
    });
    this.logManager = new LogManager({ debug: this.useLogger });
    this.adapter = new CubeAdapter(
      this.logManager,
      deps.endpoint,
      deps.poolConfig
    );
    StaticEnvironmentAdapter.cubejs_api_endpoint = deps.endpoint;
    if (deps.sentry && deps.sentry.dsn !== '') {
      Sentry.init({
        environment:
          deps.sentry.environment !== '' ? deps.sentry.environment : 'local',
        dsn: deps.sentry.dsn,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: deps.sentry.tracesSampleRate,
      });
    }
  }
  async getAccountAverageWorkingHoursPerPersonTimeSeries(
    params: GetAccountAverageWorkingHoursPerPersonTimeSeriesRequestDto
  ): Promise<GetAccountAverageWorkingHoursPerPersonTimeSeriesResponseDto> {
    try {
      this.logManager.debug(
        'called getAccountAverageWorkingHoursPerPersonTimeSeries'
      );

      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountAverageWorkingHoursPerPersonTimeSeriesDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAverageWorkingHoursPerPersonTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageWorkingHoursPerPersonTimeSeriesQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageWorkingHoursPerPersonTimeSeriesAggregateRootMapper();
      const controller =
        new GetAccountAverageWorkingHoursPerPersonTimeSeriesController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountAverageWorkingHoursPerPersonTimeSeries encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsFocusV1Summary(
    params: GetAccountMetricsInsightsFocusV1SummaryRequestDto
  ): Promise<GetAccountMetricsInsightsFocusV1SummaryResponseDto> {
    try {
      this.logManager.debug('called getAccountMetricsInsightsFocusV1Summary');
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountMetricsInsightsFocusV1SummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsFocusV1SummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsFocusV1SummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountMetricsInsightsFocusV1SummaryEntityMapper();
      const controller = new GetAccountMetricsInsightsFocusV1SummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsFocusV1Summary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getSelfMetricsInsightsChallengeTargetStreak(
    params: GetSelfMetricsInsightsChallengeTargetStreakRequestDto
  ): Promise<GetSelfMetricsInsightsChallengeTargetStreakResponseDto> {
    try {
      this.logManager.debug(
        'called getSelfMetricsInsightsChallengeTargetStreak'
      );
      const port = new ChallengeTargetResultPort(this.logManager, this.adapter);
      const domainService =
        new GetSelfMetricsInsightsChallengeTargetStreakDomainService(
          this.logManager
        );
      const applicationService =
        new GetSelfMetricsInsightsChallengeTargetStreakApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetSelfMetricsInsightsChallengeTargetStreakQuery(
        this.logManager,
        applicationService
      );
      const mapper = new SelfChallengeTargetStreakMapper();
      const controller =
        new GetSelfMetricsInsightsChallengeTargetStreakController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfMetricsInsightsChallengeTargetStreak encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsSentimentSummary(
    params: GetAccountMetricsInsightsSentimentSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsSentimentSummaryResponseDto> {
    try {
      this.logManager.debug('called getAccountMetricsInsightsSentimentSummary');
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountMetricsInsightsSentimentSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsSentimentSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsSentimentSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountMetricsInsightsSentimentSummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsSentimentSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsSentimentSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsTimeInCallsSummary(
    params: GetAccountMetricsInsightsTimeInCallsSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInCallsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getAccountMetricsInsightsTimeInCallsSummary'
      );
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountMetricsInsightTimeInCallsSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsTimeInCallsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsTimeInCallsSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountMetricsInsightsTimeInCallsSummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsTimeInCallsSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsTimeInCallsSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsTimeInMeetingsSummary(
    params: GetAccountMetricsInsightsTimeInMeetingsSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInMeetingsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getAccountMetricsInsightsTimeInMeetingsSummary'
      );
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountMetricsInsightsTimeInMeetingsSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsTimeInMeetingsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsTimeInMeetingsSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountMetricsInsightsTimeInMeetingSummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsTimeInMeetingsSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsTimeInMeetingsSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsTimeInAppSummary(
    params: GetAccountMetricsInsightsTimeInAppSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInAppSummaryResponseDto> {
    try {
      this.logManager.debug('called GetAccountMetricsInsightsTimeInAppSummary');
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountMetricsInsightsTimeInAppSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsTimeInAppSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsTimeInAppSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountMetricsInsightsTimeInAppSummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsTimeInAppSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMetricsInsightsTimeInAppSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsTimeInCategorySummary(
    params: GetAccountMetricsInsightsTimeInCategorySummaryRequestDto
  ): Promise<GetAccountMetricsInsightsTimeInCategorySummaryResponseDto> {
    try {
      this.logManager.debug(
        'called GetAccountMetricsInsightsTimeInCategorySummary'
      );
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountMetricsInsightsTimeInCategorySummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsTimeInCategorySummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsTimeInCategorySummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountMetricsInsightsTimeInCategorySummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsTimeInCategorySummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMetricsInsightsTimeInCategorySummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsDigitalIntensitySummary(
    params: GetAccountMetricsInsightsDigitalIntensitySummaryRequestDto
  ): Promise<GetAccountMetricsInsightsDigitalIntensitySummaryResponseDto> {
    try {
      this.logManager.debug(
        'called GetAccountMetricsInsightsDigitalIntensitySummary'
      );
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountMetricsInsightsDigitalIntensitySummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsDigitalIntensitySummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMetricsInsightsDigitalIntensitySummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountMetricsInsightsDigitalIntensitySummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsDigitalIntensitySummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMetricsInsightsDigitalIntensitySummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsDigitalWorkingHoursSummary(
    params: GetAccountMetricsInsightsDigitalWorkingHoursSummaryRequestDto
  ): Promise<GetAccountMetricsInsightsDigitalWorkingHoursSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called GetAccountMetricsInsightsDigitalWorkingHoursSummary'
      );
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountMetricsInsightsDigitalWorkingHoursSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountMetricsInsightsDigitalWorkingHoursSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetAccountMetricsInsightsDigitalWorkingHoursSummaryQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new AccountMetricsInsightsDigitalWorkingHoursSummaryEntityMapper();
      const controller =
        new GetAccountMetricsInsightsDigitalWorkingHoursSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMetricsInsightsDigitalWorkingHoursSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }

  async getChallengeInsightProgressSummary(
    params: GetChallengeInsightProgressSummaryRequestDto
  ): Promise<GetChallengeInsightProgressSummaryResponseDto> {
    try {
      this.logManager.debug('called getChallengeInsightProgressSummary');

      //ports
      const orgAnalyticsActivityPort = new OrgAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );
      const organizationAnalyticsEventsPort =
        new OrganizationAnalyticsEventsPort(this.logManager, this.adapter);
      const orgWorkingSessionsPort = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const orgSentimentReportPort = new OrgSentimentReportPort(
        this.logManager,
        this.adapter
      );
      const orgWorkingSessionsReportPort = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );

      const orgChallengeTargetResultPort = new OrgChallengeTargetResultPort(
        this.logManager,
        this.adapter
      );

      const successPercentageDomainService =
        new GetChallengeInsightProgressChallengeTargetSuccessDomainService(
          this.logManager
        );
      const successPercentageApplicationService =
        new GetChallengeInsightProgressChallengeTargetSuccessApplicationService(
          this.logManager,
          orgChallengeTargetResultPort,
          successPercentageDomainService
        );
      const successPercentageQuery =
        new GetChallengeInsightProgressChallengeTargetSuccessQuery(
          this.logManager,
          successPercentageApplicationService
        );

      const timeInCategoryDomainService =
        new GetChallengeInsightProgressTimeInCategorySummaryDomainService(
          this.logManager
        );
      const timeInCategoryApplicationService =
        new GetChallengeInsightProgressTimeInCategorySummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          timeInCategoryDomainService
        );
      const timeInCategoryQuery =
        new GetChallengeInsightProgressTimeInCategorySummaryQuery(
          this.logManager,
          timeInCategoryApplicationService
        );
      const timeInAppDomainService =
        new GetChallengeInsightProgressTimeInAppSummaryDomainService(
          this.logManager
        );
      const timeInAppApplicationService =
        new GetChallengeInsightProgressTimeInAppSummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          timeInAppDomainService
        );
      const timeInAppQuery =
        new GetChallengeInsightProgressTimeInAppSummaryQuery(
          this.logManager,
          timeInAppApplicationService
        );

      //digital working hours
      const digitalWorkingHourDomainService =
        new GetChallengeInsightProgressDigitalWorkingHoursSummaryDomainService(
          this.logManager
        );

      const digitalWorkingHoursApplicationService =
        new GetChallengeInsightProgressDigitalWorkingHoursSummaryApplicationService(
          this.logManager,
          orgWorkingSessionsPort,
          digitalWorkingHourDomainService
        );

      const digitalWorkingHoursQuery =
        new GetChallengeInsightProgressDigitalWorkingHoursSummaryQuery(
          this.logManager,
          digitalWorkingHoursApplicationService
        );

      const timeInCallsDomainService =
        new GetChallengeInsightProgressTimeInCallsSummaryDomainService(
          this.logManager
        );
      const timeInCallsApplicationService =
        new GetChallengeInsightProgressTimeInCallsSummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          timeInCallsDomainService
        );
      const timeInCallsQuery =
        new GetChallengeInsightProgressTimeInCallsSummaryQuery(
          this.logManager,
          timeInCallsApplicationService
        );

      const timeInMeetingsDomainService =
        new GetChallengeInsightProgressTimeInMeetingsSummaryDomainService(
          this.logManager
        );
      const timeInMeetingsApplicationService =
        new GetChallengeInsightProgressTimeInMeetingsSummaryApplicationService(
          this.logManager,
          organizationAnalyticsEventsPort,
          timeInMeetingsDomainService
        );

      const timeInMeetingsQuery =
        new GetChallengeInsightProgressTimeInMeetingsSummaryQuery(
          this.logManager,
          timeInMeetingsApplicationService
        );

      const focusV1DomainService =
        new GetChallengeInsightProgressFocusV1SummaryDomainService(
          this.logManager
        );
      const focusV1ApplicationService =
        new GetChallengeInsightProgressFocusV1SummaryApplicationService(
          this.logManager,
          orgWorkingSessionsPort,
          focusV1DomainService
        );
      const focusV1Query = new GetChallengeInsightProgressFocusV1SummaryQuery(
        this.logManager,
        focusV1ApplicationService
      );
      const sentimentDomainService =
        new GetChallengeInsightProgressSentimentSummaryDomainService(
          this.logManager
        );
      const sentimentApplicationService =
        new GetChallengeInsightProgressSentimentSummaryApplicationService(
          this.logManager,
          orgSentimentReportPort,
          sentimentDomainService
        );
      const sentimentQuery =
        new GetChallengeInsightProgressSentimentSummaryQuery(
          this.logManager,
          sentimentApplicationService
        );

      const digitalIntensityDomainService =
        new GetChallengeInsightProgressDigitalIntensitySummaryDomainService(
          this.logManager
        );
      const digitalIntensityApplicationService =
        new GetChallengeInsightProgressDigitalIntensitySummaryApplicationService(
          this.logManager,
          orgWorkingSessionsReportPort,
          digitalIntensityDomainService
        );
      const digitalIntensityQuery =
        new GetChallengeInsightProgressDigitalIntensitySummaryQuery(
          this.logManager,
          digitalIntensityApplicationService
        );

      const selfReportDomainService =
        new GetChallengeInsightProgressSelfReportSummaryDomainService(
          this.logManager
        );
      const selfReportApplicationService =
        new GetChallengeInsightProgressSelfReportSummaryApplicationService(
          this.logManager,
          orgChallengeTargetResultPort,
          selfReportDomainService
        );
      const selfReportQuery =
        new GetChallengeInsightProgressSelfReportSummaryQuery(
          this.logManager,
          selfReportApplicationService
        );
      const presenter = new GetChallengeInsightProgressSummaryPresenter();
      const controller =
        new GetChallengeInsightProgressSummaryCompositeController(
          this.logManager,
          successPercentageQuery,
          timeInCategoryQuery,
          timeInAppQuery,
          digitalWorkingHoursQuery,
          timeInCallsQuery,
          focusV1Query,
          sentimentQuery,
          timeInMeetingsQuery,
          digitalIntensityQuery,
          selfReportQuery,
          presenter
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getChallengeInsightProgressSummary encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }

  async getAccountMetricsInsightsChartData(
    params: GetAccountMetricsInsightsChartDataRequestDto
  ): Promise<GetAccountMetricsInsightsChartDataResponseDto> {
    try {
      this.logManager.debug('called getAccountMetricsInsightsChartData');
      const presenter = new GetAccountMetricsInsightsChartDataPresenter();

      const orgAnalyticsActivityPort = new OrgAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );
      const organizationAnalyticsEventsPort =
        new OrganizationAnalyticsEventsPort(this.logManager, this.adapter);
      const organizationWorkingSessionsPort =
        new OrganizationWorkingSessionsPort(this.logManager, this.adapter);
      const orgSentimentReportPort = new OrgSentimentReportPort(
        this.logManager,
        this.adapter
      );

      const orgChallengeTargetResultPort = new OrgChallengeTargetResultPort(
        this.logManager,
        this.adapter
      );

      const getAccountMetricsInsightDigitalWorkingHoursChartDataDomainService =
        new GetAccountMetricsInsightDigitalWorkingHoursChartDataDomainService(
          this.logManager
        );
      const getAccountMetricsInsightFocusV1ChartDataDomainService =
        new GetAccountMetricsInsightFocusV1ChartDataDomainService(
          this.logManager
        );
      const getAccountMetricsInsightFocusV1ChartDataApplicationService =
        new GetAccountMetricsInsightFocusV1ChartDataApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightFocusV1ChartDataDomainService
        );
      const getAccountMetricsInsightFocusV1ChartDataQuery =
        new GetAccountMetricsInsightFocusV1ChartDataQuery(
          this.logManager,
          getAccountMetricsInsightFocusV1ChartDataApplicationService
        );
      const getAccountMetricsInsightDigitalWorkingHoursChartDataApplicationService =
        new GetAccountMetricsInsightDigitalWorkingHoursChartDataApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalWorkingHoursChartDataDomainService
        );

      const getAccountMetricsInsightTimeInMeetingsChartDataDomainService =
        new GetAccountMetricsInsightTimeInMeetingsChartDataDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInMeetingsChartDataApplicationService =
        new GetAccountMetricsInsightTimeInMeetingsChartDataApplicationService(
          this.logManager,
          organizationAnalyticsEventsPort,
          getAccountMetricsInsightTimeInMeetingsChartDataDomainService
        );
      const getAccountMetricsInsightTimeInMeetingsChartDataQuery =
        new GetAccountMetricsInsightTimeInMeetingsChartDataQuery(
          this.logManager,
          getAccountMetricsInsightTimeInMeetingsChartDataApplicationService
        );

      const getAccountMetricsInsightSentimentChartDataDomainService =
        new GetAccountMetricsInsightSentimentChartDataDomainService(
          this.logManager
        );
      const getAccountMetricsInsightSentimentChartDataApplicationService =
        new GetAccountMetricsInsightSentimentChartDataApplicationService(
          this.logManager,
          orgSentimentReportPort,
          getAccountMetricsInsightSentimentChartDataDomainService
        );
      const getAccountMetricsInsightSentimentChartDataQuery =
        new GetAccountMetricsInsightSentimentChartDataQuery(
          this.logManager,
          getAccountMetricsInsightSentimentChartDataApplicationService
        );
      const getAccountMetricsInsightDigitalWorkingHoursChartDataQuery =
        new GetAccountMetricsInsightDigitalWorkingHoursChartDataQuery(
          this.logManager,
          getAccountMetricsInsightDigitalWorkingHoursChartDataApplicationService
        );

      const getAccountMetricsInsightTimeInAppsChartDataDomainService =
        new GetAccountMetricsInsightTimeInAppsChartDataDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInAppsChartDataApplicationService =
        new GetAccountMetricsInsightTimeInAppsChartDataApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInAppsChartDataDomainService
        );
      const getAccountMetricsInsightTimeInCategoriesChartDataApplicationService =
        new GetAccountMetricsInsightTimeInCategoriesChartDataApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInAppsChartDataDomainService
        );
      const getAccountMetricsInsightTimeInAppsChartDataQuery =
        new GetAccountMetricsInsightTimeInAppsChartDataQuery(
          this.logManager,
          getAccountMetricsInsightTimeInAppsChartDataApplicationService
        );
      const getAccountMetricsInsightTimeInCategoriesChartDataQuery =
        new GetAccountMetricsInsightTimeInCategoriesChartDataQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCategoriesChartDataApplicationService
        );

      const getAccountMetricsInsightTimeInCallsChartDataDomainService =
        new GetAccountMetricsInsightTimeInCallsChartDataDomainService(
          this.logManager
        );

      const getAccountMetricsInsightTimeInCallsChartDataApplicationService =
        new GetAccountMetricsInsightTimeInCallsChartDataApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInCallsChartDataDomainService
        );

      const getAccountMetricsInsightTimeInCallsChartDataQuery =
        new GetAccountMetricsInsightTimeInCallsChartDataQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCallsChartDataApplicationService
        );

      const getAccountMetricsInsightDigitalIntensityChartDataDomainService =
        new GetAccountMetricsInsightDigitalIntensityChartDataDomainService(
          this.logManager
        );

      const getAccountMetricsInsightDigitalIntensityChartDataApplicationService =
        new GetAccountMetricsInsightDigitalIntensityChartDataApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalIntensityChartDataDomainService
        );

      const getAccountMetricsInsightDigitalIntensityChartDataQuery =
        new GetAccountMetricsInsightDigitalIntensityChartDataQuery(
          this.logManager,
          getAccountMetricsInsightDigitalIntensityChartDataApplicationService
        );

      //self report
      const getAccountMetricsInsightSelfReportChartDataDomainService =
        new GetAccountMetricsInsightSelfReportChartDataDomainService(
          this.logManager
        );

      const getAccountMetricsInsightSelfReportChartDataApplicationService =
        new GetAccountMetricsInsightSelfReportChartDataApplicationService(
          this.logManager,
          orgChallengeTargetResultPort,
          getAccountMetricsInsightSelfReportChartDataDomainService
        );

      const getAccountMetricsInsightSelfReportChartDataQuery =
        new GetAccountMetricsInsightSelfReportChartDataQuery(
          this.logManager,
          getAccountMetricsInsightSelfReportChartDataApplicationService
        );

      const compositeController =
        new GetAccountMetricsInsightsChartDataCompositeController(
          this.logManager,
          getAccountMetricsInsightFocusV1ChartDataQuery,
          getAccountMetricsInsightDigitalWorkingHoursChartDataQuery,
          getAccountMetricsInsightTimeInAppsChartDataQuery,
          getAccountMetricsInsightTimeInCategoriesChartDataQuery,
          getAccountMetricsInsightTimeInMeetingsChartDataQuery,
          getAccountMetricsInsightSentimentChartDataQuery,
          getAccountMetricsInsightTimeInCallsChartDataQuery,
          getAccountMetricsInsightDigitalIntensityChartDataQuery,
          getAccountMetricsInsightSelfReportChartDataQuery,
          presenter
        );
      return await compositeController.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsChartData encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }
  async getAccountMetricsInsightsTotals(
    params: GetAccountMetricsInsightsTotalsRequestDto
  ): Promise<GetAccountMetricsInsightsTotalsResponseDto> {
    try {
      this.logManager.debug('called getAccountMetricsInsightsTotals');
      const presenter = new GetAccountMetricsInsightsAveragesPresenter(
        this.logManager
      );

      // port
      const organizationWorkingSessionsPort =
        new OrganizationWorkingSessionsPort(this.logManager, this.adapter);

      const organizationAnalyticsEventsPort =
        new OrganizationAnalyticsEventsPort(this.logManager, this.adapter);

      const orgAnalyticsActivityPort = new OrgAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );
      const orgChallengeTargetResultPort = new OrgChallengeTargetResultPort(
        this.logManager,
        this.adapter
      );

      const orgSentimentReportPort = new OrgSentimentReportPort(
        this.logManager,
        this.adapter
      );

      // domain service
      const getAccountMetricsInsightFocusV1AverageDomainService =
        new GetAccountMetricsInsightFocusV1TotalDomainService(this.logManager);
      const getAccountMetricsInsightDigitalWorkingHoursAverageDomainService =
        new GetAccountMetricsInsightDigitalWorkingHoursTotalDomainService(
          this.logManager
        );
      const getAccountMetricsInsightDigitalIntensityAverageDomainService =
        new GetAccountMetricsInsightDigitalIntensityTotalDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInCallsAverageDomainService =
        new GetAccountMetricsInsightTimeInCallsTotalDomainService(
          this.logManager
        );

      const getAccountMetricsInsightTimeInAppsAverageDomainService =
        new GetAccountMetricsInsightTimeInAppsTotalDomainService(
          this.logManager
        );

      const getAccountMetricsInsightTimeInCategoriesAverageDomainService =
        new GetAccountMetricsInsightTimeInCategoriesTotalDomainService(
          this.logManager
        );
      const getAccountMetricsInsightSelfReportAverageDomainService =
        new GetAccountMetricsInsightSelfReportTotalDomainService(
          this.logManager
        );

      // application service
      const getAccountMetricsInsightFocusV1AverageApplicationService =
        new GetAccountMetricsInsightFocusV1TotalApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightFocusV1AverageDomainService
        );
      const getAccountMetricsInsightDigitalWorkingHoursAverageApplicationService =
        new GetAccountMetricsInsightDigitalWorkingHoursTotalApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalWorkingHoursAverageDomainService
        );
      const getAccountMetricsInsightDigitalIntensityAverageApplicationService =
        new GetAccountMetricsInsightDigitalIntensityTotalApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalIntensityAverageDomainService
        );
      const getAccountMetricsInsightSentimentAverageDomainService =
        new GetAccountMetricsInsightSentimentTotalDomainService(
          this.logManager
        );
      const getAccountMetricsInsightSentimentAverageApplicationService =
        new GetAccountMetricsInsightSentimentTotalApplicationService(
          this.logManager,
          orgSentimentReportPort,
          getAccountMetricsInsightSentimentAverageDomainService
        );
      const getAccountMetricsInsightSentimentAverageQuery =
        new GetAccountMetricsInsightSentimentTotalQuery(
          this.logManager,
          getAccountMetricsInsightSentimentAverageApplicationService
        );
      const getAccountMetricsInsightTimeInMeetingsAverageDomainService =
        new GetAccountMetricsInsightTimeInMeetingsTotalDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInMeetingsAverageApplicationService =
        new GetAccountMetricsInsightTimeInMeetingsTotalApplicationService(
          this.logManager,
          organizationAnalyticsEventsPort,
          getAccountMetricsInsightTimeInMeetingsAverageDomainService
        );
      const getAccountMetricsInsightTimeInCallsAverageApplicationService =
        new GetAccountMetricsInsightTimeInCallsTotalApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInCallsAverageDomainService
        );

      const getAccountMetricsInsightTimeInAppsAverageApplicationService =
        new GetAccountMetricsInsightTimeInAppsTotalApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInAppsAverageDomainService
        );

      const getAccountMetricsInsightTimeInCategoriesAverageApplicationService =
        new GetAccountMetricsInsightTimeInCategoriesTotalApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInCategoriesAverageDomainService
        );
      const getAccountMetricsInsightSelfReportAverageApplicationService =
        new GetAccountMetricsInsightSelfReportTotalApplicationService(
          this.logManager,
          orgChallengeTargetResultPort,
          getAccountMetricsInsightSelfReportAverageDomainService
        );

      // query
      const getAccountMetricsInsightFocusV1AverageQuery =
        new GetAccountMetricsInsightFocusV1TotalQuery(
          this.logManager,
          getAccountMetricsInsightFocusV1AverageApplicationService
        );
      const getAccountMetricsInsightDigitalWorkingHoursAverageQuery =
        new GetAccountMetricsInsightDigitalWorkingHoursTotalQuery(
          this.logManager,
          getAccountMetricsInsightDigitalWorkingHoursAverageApplicationService
        );
      const getAccountMetricsInsightDigitalIntensityAverageQuery =
        new GetAccountMetricsInsightDigitalIntensityTotalQuery(
          this.logManager,
          getAccountMetricsInsightDigitalIntensityAverageApplicationService
        );
      const getAccountMetricsInsightTimeInMeetingsAverageQuery =
        new GetAccountMetricsInsightTimeInMeetingsTotalQuery(
          this.logManager,
          getAccountMetricsInsightTimeInMeetingsAverageApplicationService
        );
      const getAccountMetricsInsightTimeInCallsAverageQuery =
        new GetAccountMetricsInsightTimeInCallsTotalQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCallsAverageApplicationService
        );

      const getAccountMetricsInsightsTimeInAppsAverageQuery =
        new GetAccountMetricsInsightTimeInAppsTotalQuery(
          this.logManager,
          getAccountMetricsInsightTimeInAppsAverageApplicationService
        );
      const getAccountMetricsInsightsTimeInCategoriesAverageQuery =
        new GetAccountMetricsInsightTimeInCategoriesTotalQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCategoriesAverageApplicationService
        );
      const getAccountMetricsInsightsSelfReportAverageQuery =
        new GetAccountMetricsInsightSelfReportTotalQuery(
          this.logManager,
          getAccountMetricsInsightSelfReportAverageApplicationService
        );

      const compositeController =
        new GetAccountMetricsInsightsTotalsCompositeController(
          this.logManager,
          getAccountMetricsInsightDigitalWorkingHoursAverageQuery,
          getAccountMetricsInsightFocusV1AverageQuery,
          getAccountMetricsInsightDigitalIntensityAverageQuery,
          getAccountMetricsInsightSentimentAverageQuery,
          getAccountMetricsInsightTimeInMeetingsAverageQuery,
          getAccountMetricsInsightTimeInCallsAverageQuery,
          getAccountMetricsInsightsTimeInAppsAverageQuery,
          getAccountMetricsInsightsTimeInCategoriesAverageQuery,
          getAccountMetricsInsightsSelfReportAverageQuery,
          presenter
        );
      return await compositeController.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsTotals encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }

  async getAccountMetricsInsightsAverages(
    params: GetAccountMetricsInsightsAveragesRequestDto
  ): Promise<GetAccountMetricsInsightsAveragesResponseDto> {
    try {
      this.logManager.debug('called getAccountMetricsInsightsAverages');
      const presenter = new GetAccountMetricsInsightsAveragesPresenter(
        this.logManager
      );

      // port
      const organizationWorkingSessionsPort =
        new OrganizationWorkingSessionsPort(this.logManager, this.adapter);

      const organizationAnalyticsEventsPort =
        new OrganizationAnalyticsEventsPort(this.logManager, this.adapter);

      const orgAnalyticsActivityPort = new OrgAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );
      const orgChallengeTargetResultPort = new OrgChallengeTargetResultPort(
        this.logManager,
        this.adapter
      );

      const orgSentimentReportPort = new OrgSentimentReportPort(
        this.logManager,
        this.adapter
      );

      // domain service
      const getAccountMetricsInsightFocusV1AverageDomainService =
        new GetAccountMetricsInsightFocusV1AverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightDigitalWorkingHoursAverageDomainService =
        new GetAccountMetricsInsightDigitalWorkingHoursAverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightDigitalIntensityAverageDomainService =
        new GetAccountMetricsInsightDigitalIntensityAverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInCallsAverageDomainService =
        new GetAccountMetricsInsightTimeInCallsAverageDomainService(
          this.logManager
        );

      const getAccountMetricsInsightTimeInAppsAverageDomainService =
        new GetAccountMetricsInsightTimeInAppsAverageDomainService(
          this.logManager
        );

      const getAccountMetricsInsightTimeInCategoriesAverageDomainService =
        new GetAccountMetricsInsightTimeInCategoriesAverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightSelfReportAverageDomainService =
        new GetAccountMetricsInsightSelfReportAverageDomainService(
          this.logManager
        );

      // application service
      const getAccountMetricsInsightFocusV1AverageApplicationService =
        new GetAccountMetricsInsightFocusV1AverageApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightFocusV1AverageDomainService
        );
      const getAccountMetricsInsightDigitalWorkingHoursAverageApplicationService =
        new GetAccountMetricsInsightDigitalWorkingHoursAverageApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalWorkingHoursAverageDomainService
        );
      const getAccountMetricsInsightDigitalIntensityAverageApplicationService =
        new GetAccountMetricsInsightDigitalIntensityAverageApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          getAccountMetricsInsightDigitalIntensityAverageDomainService
        );
      const getAccountMetricsInsightSentimentAverageDomainService =
        new GetAccountMetricsInsightSentimentAverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightSentimentAverageApplicationService =
        new GetAccountMetricsInsightSentimentAverageApplicationService(
          this.logManager,
          orgSentimentReportPort,
          getAccountMetricsInsightSentimentAverageDomainService
        );
      const getAccountMetricsInsightSentimentAverageQuery =
        new GetAccountMetricsInsightSentimentAverageQuery(
          this.logManager,
          getAccountMetricsInsightSentimentAverageApplicationService
        );
      const getAccountMetricsInsightTimeInMeetingsAverageDomainService =
        new GetAccountMetricsInsightTimeInMeetingsAverageDomainService(
          this.logManager
        );
      const getAccountMetricsInsightTimeInMeetingsAverageApplicationService =
        new GetAccountMetricsInsightTimeInMeetingsAverageApplicationService(
          this.logManager,
          organizationAnalyticsEventsPort,
          getAccountMetricsInsightTimeInMeetingsAverageDomainService
        );
      const getAccountMetricsInsightTimeInCallsAverageApplicationService =
        new GetAccountMetricsInsightTimeInCallsAverageApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInCallsAverageDomainService
        );

      const getAccountMetricsInsightTimeInAppsAverageApplicationService =
        new GetAccountMetricsInsightTimeInAppsAverageApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInAppsAverageDomainService
        );

      const getAccountMetricsInsightTimeInCategoriesAverageApplicationService =
        new GetAccountMetricsInsightTimeInCategoriesAverageApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          getAccountMetricsInsightTimeInCategoriesAverageDomainService
        );
      const getAccountMetricsInsightSelfReportAverageApplicationService =
        new GetAccountMetricsInsightSelfReportAverageApplicationService(
          this.logManager,
          orgChallengeTargetResultPort,
          getAccountMetricsInsightSelfReportAverageDomainService
        );

      // query
      const getAccountMetricsInsightFocusV1AverageQuery =
        new GetAccountMetricsInsightFocusV1AverageQuery(
          this.logManager,
          getAccountMetricsInsightFocusV1AverageApplicationService
        );
      const getAccountMetricsInsightDigitalWorkingHoursAverageQuery =
        new GetAccountMetricsInsightDigitalWorkingHoursAverageQuery(
          this.logManager,
          getAccountMetricsInsightDigitalWorkingHoursAverageApplicationService
        );
      const getAccountMetricsInsightDigitalIntensityAverageQuery =
        new GetAccountMetricsInsightDigitalIntensityAverageQuery(
          this.logManager,
          getAccountMetricsInsightDigitalIntensityAverageApplicationService
        );
      const getAccountMetricsInsightTimeInMeetingsAverageQuery =
        new GetAccountMetricsInsightTimeInMeetingsAverageQuery(
          this.logManager,
          getAccountMetricsInsightTimeInMeetingsAverageApplicationService
        );
      const getAccountMetricsInsightTimeInCallsAverageQuery =
        new GetAccountMetricsInsightTimeInCallsAverageQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCallsAverageApplicationService
        );

      const getAccountMetricsInsightsTimeInAppsAverageQuery =
        new GetAccountMetricsInsightTimeInAppsAverageQuery(
          this.logManager,
          getAccountMetricsInsightTimeInAppsAverageApplicationService
        );
      const getAccountMetricsInsightsTimeInCategoriesAverageQuery =
        new GetAccountMetricsInsightTimeInCategoriesAverageQuery(
          this.logManager,
          getAccountMetricsInsightTimeInCategoriesAverageApplicationService
        );
      const getAccountMetricsInsightsSelfReportAverageQuery =
        new GetAccountMetricsInsightSelfReportAverageQuery(
          this.logManager,
          getAccountMetricsInsightSelfReportAverageApplicationService
        );

      const compositeController =
        new GetAccountMetricsInsightsAveragesCompositeController(
          this.logManager,
          getAccountMetricsInsightDigitalWorkingHoursAverageQuery,
          getAccountMetricsInsightFocusV1AverageQuery,
          getAccountMetricsInsightDigitalIntensityAverageQuery,
          getAccountMetricsInsightSentimentAverageQuery,
          getAccountMetricsInsightTimeInMeetingsAverageQuery,
          getAccountMetricsInsightTimeInCallsAverageQuery,
          getAccountMetricsInsightsTimeInAppsAverageQuery,
          getAccountMetricsInsightsTimeInCategoriesAverageQuery,
          getAccountMetricsInsightsSelfReportAverageQuery,
          presenter
        );
      return await compositeController.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMetricsInsightsAverages encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }

  async listAccountMetricsInsightsSummaries(
    params: ListAccountMetricsInsightsSummariesRequestDto
  ): Promise<ListAccountMetricsInsightsSummariesResponseDto> {
    try {
      this.logManager.debug('called listAccountMetricsInsightsSummaries');

      // Ports
      const organizationWorkingSessionsPort =
        new OrganizationWorkingSessionsPort(this.logManager, this.adapter);

      const orgAnalyticsActivityPort = new OrgAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );

      // Domain services
      const organizationAnalyticsEventsPort =
        new OrganizationAnalyticsEventsPort(this.logManager, this.adapter);
      const focusV1DomainService =
        new ListAccountMetricsInsightFocusV1SummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightDigitalWorkingHoursSummaryDomainService =
        new ListAccountMetricsInsightDigitalWorkingHoursSummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightTimeInCallsSummaryDomainService =
        new ListAccountMetricsInsightTimeInCallsSummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightDigitalIntensitySummaryDomainService =
        new ListAccountMetricsInsightDigitalIntensitySummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightTimeInAppsSummaryDomainService =
        new ListAccountMetricsInsightTimeInAppsSummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightTimeInCategorySummaryDomainService =
        new ListAccountMetricsInsightTimeInCategorySummaryDomainService(
          this.logManager
        );

      // Application services
      const focusV1ApplicationService =
        new ListAccountMetricsInsightFocusV1SummaryApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          focusV1DomainService
        );

      const listAccountMetricsInsightDigitalWorkingHoursSummaryApplicationService =
        new ListAccountMetricsInsightDigitalWorkingHoursSummaryApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          listAccountMetricsInsightDigitalWorkingHoursSummaryDomainService
        );

      const listAccountMetricsInsightTimeInCallsSummaryApplicationService =
        new ListAccountMetricsInsightTimeInCallsSummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          listAccountMetricsInsightTimeInCallsSummaryDomainService
        );

      const listAccountMetricsInsightDigitalIntensitySummaryApplicationService =
        new ListAccountMetricsInsightDigitalIntensitySummaryApplicationService(
          this.logManager,
          organizationWorkingSessionsPort,
          listAccountMetricsInsightDigitalIntensitySummaryDomainService
        );

      const listAccountMetricsInsightTimeInAppApplicationService =
        new ListAccountMetricsInsightTimeInAppSummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          listAccountMetricsInsightTimeInAppsSummaryDomainService
        );

      const listAccountMetricsTimeInCategoryApplicationService =
        new ListAccountMetricsInsightTimeInCategoriesSummaryApplicationService(
          this.logManager,
          orgAnalyticsActivityPort,
          listAccountMetricsInsightTimeInCategorySummaryDomainService
        );

      // Queries
      const focusV1Query = new ListAccountMetricsInsightFocusV1SummaryQuery(
        this.logManager,
        focusV1ApplicationService
      );

      const listAccountMetricsInsightDigitalWorkingHoursSummaryQuery =
        new ListAccountMetricsInsightDigitalWorkingHoursSummaryQuery(
          this.logManager,
          listAccountMetricsInsightDigitalWorkingHoursSummaryApplicationService
        );

      const listAccountMetricsInsightDigitalIntensitySummaryQuery =
        new ListAccountMetricsInsightDigitalIntensitySummaryQuery(
          this.logManager,
          listAccountMetricsInsightDigitalIntensitySummaryApplicationService
        );

      const listAccountMetricsInsightTimeInCallsSummaryQuery =
        new ListAccountMetricsInsightTimeInCallsSummaryQuery(
          this.logManager,
          listAccountMetricsInsightTimeInCallsSummaryApplicationService
        );

      const listAccountMetricsInsightTimeInMeetingsSummaryDomainService =
        new ListAccountMetricsInsightTimeInMeetingsSummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightTimeInMeetingsSummaryApplicationService =
        new ListAccountMetricsInsightTimeInMeetingsSummaryApplicationService(
          this.logManager,
          organizationAnalyticsEventsPort,
          listAccountMetricsInsightTimeInMeetingsSummaryDomainService
        );

      const listAccountMetricsInsightTimeInMeetingsSummaryQuery =
        new ListAccountMetricsInsightTimeInMeetingsSummaryQuery(
          this.logManager,
          listAccountMetricsInsightTimeInMeetingsSummaryApplicationService
        );

      const listAccountMetricsInsightTimeInAppQuery =
        new ListAccountMetricsInsightTimeInAppQuery(
          this.logManager,
          listAccountMetricsInsightTimeInAppApplicationService
        );

      const listAccountMetricsTimeInCategoryQuery =
        new ListAccountMetricsInsightTimeInCategoriesQuery(
          this.logManager,
          listAccountMetricsTimeInCategoryApplicationService
        );

      const orgSentimentReportPort = new OrgSentimentReportPort(
        this.logManager,
        this.adapter
      );

      const listAccountMetricsInsightSentimentSummaryDomainService =
        new ListAccountMetricsInsightSentimentSummaryDomainService(
          this.logManager
        );

      const listAccountMetricsInsightSentimentSummaryApplicationService =
        new ListAccountMetricsInsightSentimentSummaryApplicationService(
          this.logManager,
          orgSentimentReportPort,
          listAccountMetricsInsightSentimentSummaryDomainService
        );

      const listAccountMetricsInsightSentimentSummaryQuery =
        new ListAccountMetricsInsightSentimentSummaryQuery(
          this.logManager,
          listAccountMetricsInsightSentimentSummaryApplicationService
        );

      const presenter = new ListAccountMetricsInsightsSummariesPresenter(
        this.logManager
      );

      const compositeController =
        new ListAccountMetricsInsightsSummariesCompositeController(
          this.logManager,
          focusV1Query,
          listAccountMetricsInsightDigitalWorkingHoursSummaryQuery,
          listAccountMetricsInsightDigitalIntensitySummaryQuery,
          listAccountMetricsInsightTimeInCallsSummaryQuery,
          listAccountMetricsInsightTimeInMeetingsSummaryQuery,
          listAccountMetricsInsightTimeInAppQuery,
          listAccountMetricsInsightSentimentSummaryQuery,
          listAccountMetricsTimeInCategoryQuery,
          presenter
        );
      return await compositeController.get(params);
    } catch (error) {
      this.logManager.error(
        'listAccountMetricsInsightsSummaries encountered an error',
        error
      );
      throw new InternalServerErrorException(error);
    }
  }

  async getTeamMetricsCalendarAppTrendsTimeSeries(
    params: GetTeamMetricsCalendarAppTrendsTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarAppTrendsTimeSeriesResponseDto[]> {
    try {
      this.logManager.debug('called getTeamMetricsCalendarAppTrendsTimeSeries');

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarAppTrendsTimeSeriesDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarAppTrendsTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarAppTrendsTimeSeriesQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsCalendarAppTrendsTimeSeriesAggregateRootMapper();

      const controller =
        new GetTeamMetricsCalendarAppTrendsTimeSeriesController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarAppTrendsTimeSeries encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCalendarAppTimeSeriesTotalWholeTeam(
    params: GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsCalendarAppTimeSeriesWholeTeamResponseDto[]> {
    try {
      this.logManager.debug(
        'called getTeamMetricsCalendarAppTimeSeriesTotalWholeTeam'
      );

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsCalendarAppTimeSeriesTotalWholeTeamAggregateRootMapper();

      const controller =
        new GetTeamMetricsCalendarAppTimeSeriesTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarAppTimeSeriesTotalWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCalendarAppTimeSeriesTotalIndividual(
    params: GetTeamMetricsCalendarAppTimeSeriesTotalIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarAppTimeSeriesResponseDto[]> {
    try {
      this.logManager.debug(
        'called getTeamMetricsCalendarAppTimeSeriesTotalIndividual'
      );

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarAppTimeSeriesTotalIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarAppTimeSeriesTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarAppTimeSeriesTotalIndividualQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsCalendarAppTimeSeriesTotalIndividualAggregateRootMapper();

      const controller =
        new GetTeamMetricsCalendarAppTimeSeriesTotalIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarAppTimeSeriesTotalIndividual encountered an error',
        error
      );
    }
  }

  async getWorkspaceCategoryMetricsUsageTotals(
    params: GetWorkspaceCategoryMetricsUsageTotalsRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsageTotalsResponseDto> {
    this.logManager.debug('called getWorkspaceCategoryMetricsUsageTotals');
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetWorkspaceCategoryMetricsUsageTotalsDomainService(
          this.logManager
        );
      const applicationService =
        new GetWorkspaceCategoryMetricsUsageTotalsApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetWorkspaceCategoryMetricsUsageTotalsQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new WorkspaceCategoryMetricsUsageTotalsAggregateRootMapper();
      const controller = new GetWorkspaceCategoryMetricsUsageTotalsController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getWorkspaceCategoryMetricsUsageTotals encountered an error',
        error
      );
    }
  }

  async getWorkspaceCategoryMetricsUsageWholeTeamTimeSeries(
    params: GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesResponseDto> {
    this.logManager.debug(
      'called getWorkspaceCategoryMetricsUsageWholeTeamTimeSeries'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesDomainService(
          this.logManager
        );
      const applicationService =
        new GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new WorkspaceCategoryMetricsUsageWholeTeamTimeSeriesAggregateRootMapper();
      const controller =
        new GetWorkspaceCategoryMetricsUsageWholeTeamTimeSeriesController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getWorkspaceCategoryMetricsUsageWholeTeamTimeSeries encountered an error',
        error
      );
    }
  }

  async getAccountAppCategoryMetricsPerApp(
    params: GetAccountAppCategoryMetricsPerAppRequestDto
  ): Promise<GetAccountAppCategoryMetricsPerAppResponseDto> {
    this.logManager.debug('called GetAccountAppCategoryMetricsPerApp');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountAppCategoryMetricsPerAppDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountAppCategoryMetricsPerAppApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppCategoryMetricsPerAppQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountAppCategoryMetricsPerAppAggregateRootMapper();
      const controller = new GetAccountAppCategoryMetricsPerAppController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountAppCategoryMetricsPerApp encountered an error',
        error
      );
    }
  }

  async getAccountAppTotalsPerCategorySummary(
    params: GetAccountAppTotalsPerCategorySummaryRequestDto
  ): Promise<GetAccountAppTotalsPerCategorySummaryResponseDto> {
    this.logManager.debug('called getAccountAppTotalsPerCategorySummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountAppTotalsPerCategorySummaryDomainService(this.logManager);
      const applicationService =
        new GetAccountAppTotalsPerCategorySummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppTotalsPerCategorySummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAppTotalsPerCategorySummaryAggregateRootMapper();
      const controller = new GetAccountAppTotalsPerCategorySummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountAppTotalsPerCategorySummary encountered an error',
        error
      );
    }
  }

  async getAccountAppCategoriesTimeSeries(
    params: GetAccountAppCategoriesTimeSeriesRequestDto
  ): Promise<GetAccountAppCategoriesTimeSeriesResponseDto> {
    this.logManager.debug('called getAccountAppCategoriesTimeSeries');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountAppCategoriesTimeSeriesDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountAppCategoriesTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppCategoriesTimeSeriesQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountAppCategoriesTimeSeriesAggregateRootMapper();
      const controller = new GetAccountAppCategoriesTimeSeriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountAppCategoriesTimeSeries encountered an error',
        error
      );
    }
  }

  async getAccountTotalAppCategoriesSummary(
    params: GetAccountTotalAppCategoriesSummaryRequestDto
  ): Promise<GetAccountTotalAppCategoriesSummaryResponseDto> {
    this.logManager.debug('called getAccountTotalAppCategoriesSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountTotalAppCategoriesSummaryDomainService(this.logManager);
      const applicationService =
        new GetAccountTotalAppCategoriesSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountTotalAppCategoriesSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountTotalAppCategoriesSummaryAggregateRootMapper();
      const controller = new GetAccountTotalAppCategoriesSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountTotalAppCategoriesSummary encountered an error',
        error
      );
    }
  }

  async getSelfTopAppCategoryApps(
    params: GetSelfTopAppCategoryAppsRequestDto
  ): Promise<GetSelfTopAppCategoryAppsResponseDto> {
    this.logManager.debug('called getSelfTopAppCategoryApps');
    try {
      const port = new AnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetSelfTopAppCategoryAppsDomainService(
        this.logManager
      );
      const applicationService =
        new GetSelfTopAppCategoryAppsApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetSelfTopAppCategoryAppsQuery(
        this.logManager,
        applicationService
      );
      const mapper = new SelfTopAppCategoryAppsAggregateRootMapper();
      const controller = new GetSelfTopAppCategoryAppsController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfTopAppCategoryApps encountered an error',
        error
      );
    }
  }

  async getSelfTopAppCategories(
    params: GetSelfTopAppCategoriesRequestDto
  ): Promise<GetSelfTopAppCategoriesResponseDto> {
    this.logManager.debug('called getSelfTopAppCategories');
    try {
      const port = new AnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetSelfTopAppCategoriesDomainService(
        this.logManager
      );
      const applicationService = new GetSelfTopAppCategoriesApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetSelfTopAppCategoriesQuery(
        this.logManager,
        applicationService
      );
      const mapper = new SelfTopAppCategoriesAggregateRootMapper();
      const controller = new GetSelfTopAppCategoriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfTopAppCategories encountered an error',
        error
      );
    }
  }

  async getAccountAppCategoriesSummary(
    params: GetAccountAppCategoriesSummaryRequestDto
  ): Promise<GetAccountAppCategoriesSummaryResponseDto> {
    this.logManager.debug('called getAccountAppCategoriesSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountAppCategoriesSummaryDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountAppCategoriesSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppCategoriesSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountAppCategoriesSummaryAggregateRootMapper();
      const controller = new GetAccountAppCategoriesSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      const message = `getAccountAppCategoriesSummary encountered an error ${error.message}`;
      this.logManager.error(message);

      throw new Error(message);
    }
  }

  async getAccountAppCategoriesPerWorkspaceSummary(
    params: GetAccountAppCategoriesPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountAppCategoriesPerWorkspaceSummaryResponseDto> {
    this.logManager.debug('called getAccountAppCategoriesPerWorkspaceSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountAppCategoriesPerWorkspaceSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAppCategoriesPerWorkspaceSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppCategoriesPerWorkspaceSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAppCategoriesPerWorkspaceSummaryAggregateRootMapper();
      const controller =
        new GetAccountAppCategoriesPerWorkspaceSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountAppCategoriesPerWorkspaceSummary encountered an error',
        error
      );
    }
  }

  async getSelfAppAveragesSummary(
    params: GetSelfAppAveragesSummaryRequestDto
  ): Promise<GetSelfAppAveragesSummaryResponseDto> {
    this.logManager.debug('called getSelfAppAveragesSummary');
    try {
      const appPort = new AnalyticsActivityPort(this.logManager, this.adapter);
      const calendarEventsPort = new AnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const appsDomainService = new GetSelfAppAveragesSummaryDomainService(
        this.logManager
      );
      const calendarEventsDomainService =
        new GetSelfAppCalendarEventsAveragesSummaryDomainService(
          this.logManager
        );
      const appsApplicationService =
        new GetSelfAppAveragesSummaryApplicationService(
          this.logManager,
          appPort,
          appsDomainService
        );
      const calendarEventsApplicationService =
        new GetSelfAppCalendarEventsAveragesSummaryApplicationService(
          this.logManager,
          calendarEventsPort,
          calendarEventsDomainService
        );
      const appsQuery = new GetSelfAppAveragesSummaryQuery(
        this.logManager,
        appsApplicationService
      );
      const calendarEventsQuery =
        new GetSelfAppCalendarEventsAveragesSummaryQuery(
          this.logManager,
          calendarEventsApplicationService
        );
      const appsMapper = new SelfAppAverageSummaryEntityMapper();
      const calendarEventsMapper =
        new SelfAppCalendarEventsAverageSummaryEntityMapper();
      const presenter = new GetSelfAppAveragesSummaryPresenter(
        this.logManager,
        appsMapper,
        calendarEventsMapper
      );
      const controller = new GetSelfAppAveragesSummaryCompositeController(
        this.logManager,
        appsQuery,
        calendarEventsQuery,
        presenter
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'ScoresController.getSelfAppAveragesSummary encountered an error',
        error
      );
    }
  }

  async getSelfAppCategoryAveragesSummary(
    params: GetSelfAppCategoryAveragesSummaryRequestDto
  ): Promise<GetSelfAppCategoryAveragesSummaryResponseDto> {
    this.logManager.debug('called getSelfAppCategoryAveragesSummary');
    try {
      const appPort = new AnalyticsActivityPort(this.logManager, this.adapter);
      const appsDomainService =
        new GetSelfAppCategoryAveragesSummaryDomainService(this.logManager);
      const appsApplicationService =
        new GetSelfAppCategoryAveragesSummaryApplicationService(
          this.logManager,
          appPort,
          appsDomainService
        );
      const query = new GetSelfAppCategoryAveragesSummaryQuery(
        this.logManager,
        appsApplicationService
      );
      const mapper = new SelfAppCategoryAveragesSummaryEntityMapper();
      const controller = new GetSelfAppCategoryAveragesSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfAppCategoryAveragesSummary encountered an error',
        error
      );
    }
  }

  async getSelfAppCategoryTimeSeries(
    params: GetSelfAppCategoryTimeSeriesRequestDto
  ): Promise<GetSelfAppCategoryTimeSeriesResponseDto> {
    this.logManager.debug('called getSelfAppCategoryTimeSeries');
    try {
      const appPort = new AnalyticsActivityPort(this.logManager, this.adapter);
      const appsDomainService = new GetSelfAppCategoryTimeSeriesDomainService(
        this.logManager
      );
      const appsApplicationService =
        new GetSelfAppCategoryTimeSeriesApplicationService(
          this.logManager,
          appPort,
          appsDomainService
        );
      const query = new GetSelfAppCategoryTimeSeriesQuery(
        this.logManager,
        appsApplicationService
      );
      const mapper = new SelfAppCategoryTimeSeriesEntityMapper();
      const controller = new GetSelfAppCategoryTimeSeriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfAppCategoryTimeSeries encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCallsEventsSummaryTotalWholeTeam(
    params: GetTeamMetricsCallsEventsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsCallsEventsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamMetricsCallsEventsSummaryTotalWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCallsEventsSummaryTotalWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsCallsEventsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsCallsEventsSummaryTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsCallsEventsSummaryTotalWholeTeamParentAggregateRootMapper();
      const controller =
        new GetTeamMetricsCallsEventsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCallsEventsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCallsEventsSummaryTotalIndividual(
    params: GetTeamMetricsCallsEventsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsCallsEventsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamMetricsCallsEventsSummaryTotalIndividual'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCallsEventsSummaryTotalIndividualDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsCallsEventsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsCallsEventsSummaryTotalIndividualQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsCallsEventsSummaryTotalIndividualParentAggregateRootMapper();
      const controller =
        new GetTeamMetricsCallsEventsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCallsEventsSummaryTotalIndividual encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCallsEventsSummaryAverageWholeTeam(
    params: GetTeamMetricsCallsEventsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsCallsEventsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamMetricsCallsEventsSummaryAverageWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCallsEventsSummaryAverageWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsCallsEventsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsCallsEventsSummaryAverageWholeTeamQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsCallsEventsSummaryAverageWholeTeamParentAggregateRootMapper();
      const controller =
        new GetTeamMetricsCallsEventsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCallsEventsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsAppsSummaryTotalWholeTeam(
    params: GetTeamMetricsAppsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppsSummaryResponseDto> {
    this.logManager.debug('called getTeamMetricsAppsSummaryTotalWholeTeam');
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsAppsSummaryTotalWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsAppsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsAppsSummaryTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsAppsSummaryTotalWholeTeamParentAggregateRootMapper();
      const controller = new GetTeamMetricsAppsSummaryTotalWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }
  async getTeamMetricsAppsSummaryTotalIndividual(
    params: GetTeamMetricsAppsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsAppsSummaryResponseDto> {
    this.logManager.debug('called getTeamMetricsAppsSummaryTotalIndividual');
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsAppsSummaryTotalIndividualDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsAppsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsAppsSummaryTotalIndividualQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsAppsSummaryTotalIndividualParentAggregateRootMapper();
      const controller = new GetTeamMetricsAppsSummaryTotalIndividualController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppsSummaryTotalIndividual encountered an error',
        error
      );
    }
  }
  async getTeamMetricsAppsSummaryAverageWholeTeam(
    params: GetTeamMetricsAppsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppsSummaryResponseDto> {
    this.logManager.debug('called getTeamMetricsAppsSummaryAverageWholeTeam');
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsAppsSummaryAverageWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamMetricsAppsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetTeamMetricsAppsSummaryAverageWholeTeamQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new TeamMetricsAppsSummaryAverageWholeTeamParentAggregateRootMapper();
      const controller =
        new GetTeamMetricsAppsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamSubscriptionMetricsAppsSummaryTotalWholeTeam(
    params: GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsAppsSummaryTotalWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsAppsSummaryTotalWholeTeamAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsAppsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsAppsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }
  async getTeamSubscriptionMetricsAppsSummaryTotalIndividual(
    params: GetTeamSubscriptionMetricsAppsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsAppsSummaryTotalIndividual'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsAppsSummaryTotalIndividualDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsAppsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsAppsSummaryTotalIndividualQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsAppsSummaryTotalIndividualAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsAppsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsAppsSummaryTotalIndividual encountered an error',
        error
      );
    }
  }
  async getTeamSubscriptionMetricsAppsSummaryAverageWholeTeam(
    params: GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsAppsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsAppsSummaryAverageWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsAppsSummaryAverageWholeTeamAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsAppsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsAppsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeam(
    params: GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsMeetingsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }
  async getTeamSubscriptionMetricsMeetingsSummaryTotalIndividual(
    params: GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsMeetingsSummaryTotalIndividual'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsMeetingsSummaryTotalIndividualAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsMeetingsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsMeetingsSummaryTotalIndividual encountered an error',
        error
      );
    }
  }
  async getTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeam(
    params: GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamSubscriptionMetricsMeetingsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeam'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamDomainService(
          this.logManager
        );
      const applicationService =
        new GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query =
        new GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamQuery(
          this.logManager,
          applicationService
        );
      const mapper =
        new TeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamAggregateRootMapper();
      const controller =
        new GetTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamSubscriptionMetricsMeetingsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountAverageWorkingHoursPerPersonRangesSummary(
    params: GetAccountAverageWorkingHoursPerPersonRangesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageWorkingHoursPerPersonRangesResponseDto> {
    this.logManager.debug(
      'called getAccountAverageWorkingHoursPerPersonRangesSummary'
    );
    try {
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountAverageWorkingHoursPerPersonRangesDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAverageWorkingHoursPerPersonRangesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageWorkingHoursPerPersonRangesQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageWorkingHoursPerPersonRangesAggregateRootMapper();
      const controller =
        new GetAccountAverageWorkingHoursPerPersonRangesController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'OrganizationWorkingHoursContoller.getAccountAverageWorkingHoursPerPersonRangesSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountAverageWorkingHoursPerPersonDistribution(
    params: GetAccountAverageWorkingHoursPerPersonDistributionRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageWorkingHoursPerPersonDistributionResponseDto> {
    this.logManager.debug(
      'called getAccountAverageWorkingHoursPerPersonDistribution'
    );
    try {
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountWorkingHoursAveragePerPersonDistributionDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountWorkingHoursAveragePerPersonDistributionApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageWorkingHoursPerPersonDistributionQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageWorkingHoursPerPersonDistributionRangeMapper();
      const controller =
        new GetAccountAverageWorkingHoursPerPersonDistributionController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'OrganizationWorkingHoursContoller.getAccountAverageWorkingHoursPerPersonDistribution encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getCallsPerWorkspaceTimeSeries(
    params: GetAccountAverageCallsPerWorkspaceTimeseriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageCallsPerWorkspaceTimeseriesResponseDto> {
    this.logManager.debug('called getCallsPerWorkspaceTimeSeries');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountAverageCallsPerWorkspaceTimeSeriesDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAverageCallsPerWorkspaceTimeseriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageCallsPerWorkspaceTimeseriesQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageCallsPerWorkspaceTimeseriesAggregateRootMapper();
      const controller =
        new GetAccountAverageCallsPerWorkspaceTimeseriesController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountCallsAveragePerWorkspaceTimeseries encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountMeetingsPerWorkspaceTimeSeries(
    params: GetAccountMeetingsPerWorkspaceTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountMeetingsPerWorkspaceTimeSeriesResponseDto> {
    this.logManager.debug('called getAccountMeetingsPerWorkspaceTimeSeries');
    try {
      const mapper =
        new AccountMeetingsPerWorkspaceTimeSeriesAggregateRootMapper();
      const domainService =
        new GetAccountMeetingsPerWorkspaceTimeSeriesDomainService(
          this.logManager
        );
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const service =
        new GetAccountMeetingsPerWorkspaceTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMeetingsPerWorkspaceTimeSeriesQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountMeetingsPerWorkspaceTimeSeriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'CallsController.getAccountMeetingsPerWorkspaceTimeSeries encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountCallsPerWorkspaceSummary(
    input: GetAccountCallsPerWorkspaceSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountCallsPerWorkspaceSummaryResponseDto> {
    this.logManager.debug('called getAccountCallsPerWorkspaceSummary');
    try {
      const mapper = new AccountCallsPerWorkspaceSummaryAggregateRootMapper();
      const domainService = new GetAccountCallsPerWorkspaceSummaryDomainService(
        this.logManager
      );
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const service = new GetAccountCallsPerWorkspaceSummaryApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetAccountCallsPerWorkspaceSummaryQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountCallsPerWorkspaceSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'CallsController.getAccountCallsPerWorkspaceSummary encountered an error',
        error
      );
    }
  }

  async getAccountMeetingsPerWorkspaceSummary(
    input: GetAccountMeetingsPerWorkspaceSummaryRequestDto
  ): Promise<GetAccountMeetingsPerWorkspaceSummaryResponseDto> {
    this.logManager.debug('called getAccountMeetingsPerWorkspaceSummary');
    try {
      const mapper =
        new AccountMeetingsPerWorkspaceSummaryAggregateRootMapper();
      const domainService =
        new GetAccountMeetingsPerWorkspaceSummaryDomainService(this.logManager);
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const service =
        new GetAccountMeetingsPerWorkspaceSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMeetingsPerWorkspaceSummaryQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountMeetingsPerWorkspaceSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'getAccountMeetingsPerWorkspaceSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountCallsAveragePerPersonSummary(
    params: GetAccountAverageCallsPerPersonSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageCallsPerPersonSummaryResponseDto> {
    this.logManager.debug('called getAccountCallsAveragePerPersonSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountAverageCallsPerPersonSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAverageCallsPerPersonSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageCallsPerPersonSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageCallsPerPersonSummaryAggregateRootMapper();
      const controller = new GetAccountAverageCallsPerPersonSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountCallsAveragePerPersonSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountMeetingsAveragePerPersonSummary(
    params: GetAccountMeetingsAveragePerPersonSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountMeetingsAveragePerPersonSummaryResponseDto> {
    this.logManager.debug('called getAccountMeetingsAveragePerPersonSummary');
    try {
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountAverageMeetingsPerPersonSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAverageMeetingsPerPersonSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageMeetingsPerPersonSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageMeetingsPerPersonSummaryAggregateRootMapper();
      const controller =
        new GetAccountAverageMeetingsPerPersonSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountMeetingsAveragePerPersonSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountTopAppsMetricsSummary(
    input: GetAccountTopAppsMetricsSummaryRequestDto & UserCountInDateRangeProps
  ): Promise<GetAccountTopAppsMetricsSummaryResponseDto> {
    this.logManager.debug('called getAccountTopAppsMetricsSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountTopAppsMetricsSummaryDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountTopAppsMetricsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountTopAppsMetricsSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountTopAppsMetricsSummaryAggregateRootMapper();
      const controller = new GetAccountTopAppsMetricsSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'TopAppsController.getAccountTopAppsMetricsSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountAppMetricsPerWorkspaceSummary(
    input: GetAccountAppMetricsPerWorkspaceSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAppMetricsPerWorkspaceSummaryResponseDto> {
    this.logManager.debug('called getAccountAppMetricsPerWorkspaceSummary');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountAppMetricsPerWorkspaceSummaryDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountAppMetricsPerWorkspaceSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAppMetricsPerWorkspaceSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAppMetricsPerWorkspaceSummaryAggregateRootMapper();
      const controller = new GetAccountAppMetricsPerWorkspaceSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'AppsController.getAccountAppMetricsPerWorkspaceSummary encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountAverageWorkingHoursTimeSeries(
    params: GetAccountAverageWorkingHoursTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageWorkingHoursTimeSeriesResponseDto> {
    this.logManager.debug('called getAccountAverageWorkingHoursTimeSeries');
    try {
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountWorkingHoursAverageTimeSeriesDomainService(
          this.logManager
        );
      const applicationService =
        new GetAccountWorkingHoursAverageTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageWorkingHoursTimeSeriesQuery(
        this.logManager,
        applicationService
      );
      const mapper =
        new AccountAverageWorkingHoursTimeSeriesAggregateRootMapper();
      const controller = new GetAccountAverageWorkingHoursTimeSeriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getAccountAverageWorkingHoursTimeSeries encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountAverageWorkingHoursSummary(
    params: GetAccountAverageWorkingHoursSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountAverageWorkingHoursSummaryResponseDto> {
    this.logManager.debug('called getAccountAverageWorkingHoursSummary');
    try {
      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const domainService =
        new GetAccountWorkingHoursAverageSummaryDomainService(this.logManager);
      const applicationService =
        new GetAccountWorkingHoursAverageSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountAverageWorkingHoursSummaryQuery(
        this.logManager,
        applicationService
      );
      const mapper = new AccountAverageWorkingHoursSummaryAggregateRootMapper();
      const controller = new GetAccountAverageWorkingHoursSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'OrganizationWorkingHoursContoller.getAccountAverageWorkingHoursSummary encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportAverageSummary(
    params: GetAccountSentimentReportAverageSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportAverageSummaryResponseDto> {
    this.logManager.debug('called getAccountSentimentReportAverageSummary');
    try {
      const mapper =
        new AccountSentimentReportAverageSummaryAggregateRootMapper();
      const domainService =
        new GetAccountSentimentReportAverageSummaryDomainService(
          this.logManager
        );
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const service =
        new GetAccountSentimentReportAverageSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountSentimentReportAverageSummaryQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountSentimentReportAverageSummaryController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentController.getAccountSentimentReportAverageSummary encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportPercentilesSummary(
    params: GetAccountSentimentReportPercentilesSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportPercentilesSummaryResponseDto> {
    this.logManager.debug('called getAccountSentimentReportPercentilesSummary');
    try {
      const mapper =
        new AccountSentimentReportPercentilesSummaryAggregateRootMapper();
      const domainService =
        new GetAccountSentimentReportPercentilesSummaryDomainService(
          this.logManager
        );
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const service =
        new GetAccountSentimentReportPercentilesSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountSentimentReportPercentilesSummaryQuery(
        this.logManager,
        service
      );
      const controller =
        new GetAccountSentimentReportPercentilesSummaryController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentController.getAccountSentimentReportPercentilesSummary encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportAverageTimeSeries(
    params: GetAccountSentimentReportAverageTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportAverageTimeSeriesResponseDto> {
    this.logManager.debug('called getAccountSentimentReportAverageTimeSeries');
    try {
      const rootMapper =
        new AccountSentimentReportAverageTimeSeriesAggregateRootMapper();
      const domainService =
        new GetAccountSentimentReportAverageTimeSeriesDomainService(
          this.logManager
        );
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const service =
        new GetAccountSentimentReportAverageTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountSentimentReportAverageTimeSeriesQuery(
        this.logManager,
        service
      );
      const controller =
        new GetAccountSentimentReportAverageTimeSeriesController(
          this.logManager,
          query,
          rootMapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentReportController.getAccountSentimentReportAverageTimeSeries encountered an error',
        error
      );
    }
  }
  async getSelfSentimentReport(
    params: GetSelfSentimentReportRequestDto
  ): Promise<GetSelfSentimentReportResponseDto> {
    this.logManager.debug('called getSelfSentimentReport');
    try {
      const mapper = new SelfSentimentReportMapper();
      const port = new SentimentReportPort(this.logManager, this.adapter);
      const service = new GetSelfSentimentReportApplicationService(
        this.logManager,
        port
      );
      const query = new GetSelfSentimentReportQuery(this.logManager, service);
      const controller = new GetSelfSentimentReportController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentReportController.getSelfSentimentReport encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportAverageTotals(
    params: GetAccountSentimentReportAverageTotalsRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportAverageTotalsResponseDto> {
    this.logManager.debug('called getAccountSentimentReportAverageTotals');
    try {
      const mapper =
        new AccountSentimentReportAverageTotalsAggregateRootMapper();

      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountSentimentReportAverageTotalsDomainService(
          this.logManager
        );
      const service =
        new GetAccountSentimentReportAverageTotalsApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountSentimentReportAverageTotalsMetricsQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountSentimentReportAverageTotalsController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentController.getAccountSentimentReportAverageTotals encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportPercentilesAverage(
    params: GetAccountSentimentReportPercentilesAverageRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportPercentilesAverageResponseDto> {
    this.logManager.debug('called getAccountSentimentReportPercentilesAverage');
    try {
      const mapper =
        new AccountSentimentReportPercentilesAverageAggregateRootMapper();
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const domainService =
        new GetAccountSentimentReportPercentilesAverageDomainService(
          this.logManager
        );
      const service =
        new GetAccountSentimentReportPercentilesAverageApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountSentimentReportPercentilesAverageMetricsQuery(
        this.logManager,
        service
      );
      const controller =
        new GetAccountSentimentReportPercentilesAverageController(
          this.logManager,
          query,
          mapper
        );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentController.getAccountSentimentReportPercentilesAverage encountered an error',
        error
      );
    }
  }
  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountSentimentReportTimeSeries(
    params: GetAccountSentimentReportTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountSentimentReportTimeSeriesResponseDto> {
    this.logManager.debug('called getAccountSentimentReportTimeSeries');
    try {
      const rootMapper =
        new AccountSentimentReportTimeSeriesAggregateRootMapper();
      const domainService =
        new GetAccountSentimentReportTimeSeriesDomainService(this.logManager);
      const port = new OrgSentimentReportPort(this.logManager, this.adapter);
      const service = new GetAccountSentimentReportTimeSeriesApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetAccountSentimentReportTimeSeriesQuery(
        this.logManager,
        service
      );
      const controller = new GetAccountSentimentReportTimeSeriesController(
        this.logManager,
        query,
        rootMapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentReportController.getAccountSentimentReportTimeSeries encountered an error',
        error
      );
    }
  }
  async getSelfSentimentReportTimeSeries(
    params: GetSelfSentimentReportTimeSeriesRequestDto
  ): Promise<GetSelfSentimentReportTimeSeriesResponseDto> {
    this.logManager.debug('called getSelfSentimentReportTimeSeries');
    try {
      const mapper = new SelfSentimentReportTimeSeriesMapper();
      const port = new SentimentReportPort(this.logManager, this.adapter);
      const domainService = new GetSelfSentimentReportTimeSeriesDomainService(
        this.logManager
      );
      const service = new GetSelfSentimentReportTimeSeriesApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetSelfSentimentReportTimeSeriesQuery(
        this.logManager,
        service
      );
      const controller = new GetSelfSentimentReportTimeSeriesController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'SentimentReportController.getSelfSentimentReportTimeSeries encountered an error',
        error
      );
    }
  }

  async getSelfAllScores(
    params: GetSelfAllScoresRequestDto
  ): Promise<SelfGetAllScoresResponseDto> {
    this.logManager.debug('called getSelfAllScores');
    try {
      // entity mappers
      const focusMapper = new SelfFocusScoreMapper();
      const averageFocusMapper = new SelfAverageFocusScoreMapper();
      const digitalWorkingHoursMapper =
        new SelfDigitalWorkingHoursScoreMapper();
      const averageDigitalWorkingHoursMapper =
        new SelfAverageDigitalWorkingHoursScoreMapper();
      const timeInCallsMapper = new SelfTimeInCallsScoreMapper();
      const averageTimeInCallsMapper = new SelfAverageTimeInCallsScoreMapper();
      const screentimeMapper = new SelfScreentimeScoreMapper();
      const averageScreentimeMapper = new SelfAverageScreentimeScoreMapper();
      const digitalIntensityMapper = new SelfDigitalIntensityScoreMapper();
      const averageDigitalIntensityMapper =
        new SelfAverageDigitalIntensityScoreMapper();

      // aggregate root mappers

      const calendarAggregateRootMapper =
        new SelfCalendarScoreAggregateRootMapper();
      const focusAggregateRootMapper = new SelfFocusScoreAggregateRootMapper();
      const digitalWorkingHoursAggregateRootMapper =
        new SelfDigitalWorkingHoursScoreAggregateRootMapper();
      const timeInCallsAggregateRootMapper =
        new SelfTimeInCallsScoreAggregateRootMapper();
      const screentimeAggregateRootMapper =
        new SelfScreentimeScoreAggregateRootMapper();
      const digitalIntensityAggregateRootMapper =
        new SelfDigitalIntensityScoreAggregateRootMapper();

      // ports

      const analyticsEventPort = new AnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      // domain service
      const calendarDomainService = new GetSelfCalendarScoreDomainService(
        this.logManager
      );

      // application services

      const calendarScoreService = new GetSelfCalendarScoreApplicationService(
        this.logManager,
        analyticsEventPort,
        calendarDomainService
      );

      const focusScoreService = new GetSelfFocusScoreService(
        this.logManager,
        this.adapter,
        focusMapper,
        averageFocusMapper
      );
      const timeInCallsScoreService = new GetSelfTimeInCallsScoreService(
        this.logManager,
        this.adapter,
        timeInCallsMapper,
        averageTimeInCallsMapper
      );
      const screentimeScoreService = new GetSelfScreentimeScoreService(
        this.logManager,
        this.adapter,
        screentimeMapper,
        averageScreentimeMapper
      );
      const digitalIntensityScoreService =
        new GetSelfDigitalIntensityScoreService(
          this.logManager,
          this.adapter,
          digitalIntensityMapper,
          averageDigitalIntensityMapper
        );
      const digitalWorkingHoursScoreService =
        new GetSelfDigitalWorkingHoursScoreService(
          this.logManager,
          this.adapter,
          digitalWorkingHoursMapper,
          averageDigitalWorkingHoursMapper
        );

      const getSelfCalendarScoreQuery = new GetSelfCalendarScoreQuery(
        this.logManager,
        calendarScoreService
      );
      const getSelfFocusScoreQuery = new GetSelfFocusScoreQuery(
        this.logManager,
        focusScoreService
      );
      const getSelfTimeInCallsScoreQuery = new GetSelfTimeInCallsScoreQuery(
        this.logManager,
        timeInCallsScoreService
      );
      const getSelfScreentimeScoreQuery = new GetSelfScreentimeScoreQuery(
        this.logManager,
        screentimeScoreService
      );
      const getSelfDigitalIntensityScoreQuery =
        new GetSelfDigitalIntensityScoreQuery(
          this.logManager,
          digitalIntensityScoreService
        );
      const getSelfDigitalWorkingHoursScoreQuery =
        new GetSelfDigitalWorkingHoursScoreQuery(
          this.logManager,
          digitalWorkingHoursScoreService
        );

      const presenter = new GetSelfAllScoresPresenter(
        this.logManager,
        digitalIntensityAggregateRootMapper,
        digitalWorkingHoursAggregateRootMapper,
        focusAggregateRootMapper,
        screentimeAggregateRootMapper,
        calendarAggregateRootMapper,
        timeInCallsAggregateRootMapper
      );

      const controller = new GetSelfAllScoresCompositeController(
        this.logManager,
        getSelfCalendarScoreQuery,
        getSelfFocusScoreQuery,
        getSelfDigitalWorkingHoursScoreQuery,
        getSelfTimeInCallsScoreQuery,
        getSelfScreentimeScoreQuery,
        getSelfDigitalIntensityScoreQuery,
        presenter
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error('getSelfAllScores encountered an error', error);
    }
  }

  async getSelfCalendarAppTimeSeries(
    params: GetSelfCalendarAppTimeSeriesRequestDto
  ): Promise<GetSelfCalendarScoreTimeSeriesResponseDto> {
    this.logManager.debug('called getSelfCalendarAppTimeSeries');
    try {
      const entityMapper = new SelfCalendarScoreTimeSeriesMapper();
      const aggregateMapper =
        new SelfCalendarScoreTimeSeriesAggregateRootMapper();

      const weekdayService = new GetSelfCalendarAppTimeSeriesWeekdayService(
        this.logManager,
        this.adapter,
        entityMapper
      );
      const weekdayQuery = new GetSelfCalendarAppTimeSeriesWeekdayQuery(
        this.logManager,
        weekdayService
      );

      const granularityService = new GetSelfCalendarAppTimeSeriesService(
        this.logManager,
        this.adapter,
        entityMapper
      );
      const granularityQuery = new GetSelfCalendarAppTimeSeriesQuery(
        this.logManager,
        granularityService
      );

      const controller = new GetSelfCalendarAppTimeSeriesController(
        this.logManager,
        granularityQuery,
        weekdayQuery,
        aggregateMapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'CalendarGoalController.getSelfCalendarScoreTimeSeriesApp encountered an error',
        error
      );
    }
  }

  async getSelfCalendarScoreTimeSeries(
    params: GetSelfCalendarScoreTimeSeriesRequestDto
  ): Promise<GetSelfCalendarScoreTimeSeriesResponseDto> {
    this.logManager.debug('called getSelfCalendarScoreTimeSeries');
    try {
      const entityMapper = new SelfCalendarScoreTimeSeriesMapper();
      const aggregateMapper =
        new SelfCalendarScoreTimeSeriesAggregateRootMapper();

      const granularityService = new GetSelfCalendarScoreTimeSeriesService(
        this.logManager,
        this.adapter,
        entityMapper
      );
      const granularityQuery = new GetSelfCalendarScoreTimeSeriesQuery(
        this.logManager,
        granularityService
      );
      const weekdayService = new GetSelfCalendarScoreTimeSeriesWeekdayService(
        this.logManager,
        this.adapter,
        entityMapper
      );
      const weekdayQuery = new GetSelfCalendarScoreTimeSeriesWeekdayQuery(
        this.logManager,
        weekdayService
      );
      const controller = new GetSelfCalendarScoreTimeSeriesController(
        this.logManager,
        granularityQuery,
        weekdayQuery,
        aggregateMapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'CalendarGoalController.getSelfCalendarScoreTimeSeries encountered an error',
        error
      );
    }
  }

  async getSelfCalendarChartAllTimeAverages(
    params: GetCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetCalendarChartAllTimeAveragesResponseDto> {
    this.logManager.debug('called getSelfCalendarChartAllTimeAverages');
    try {
      const entityMapper = new CalendarChartAllTimeAveragesMapper();
      const aggregateRootMapper =
        new CalendarChartAllTimeAveragesAggregateRootMapper();
      const service = new GetCalendarChartAllTimeAveragesService(
        this.logManager,
        this.adapter,
        entityMapper
      );
      const query = new GetCalendarChartAllTimeAveragesQuery(
        this.logManager,
        service
      );
      const controller = new GetCalendarChartAllTimeAveragesController(
        this.logManager,
        query,
        aggregateRootMapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetCalendarChartAllTimeAveragesController encountered an error',
        error
      );
    }
  }

  async getSelfCalendarAppChartAllTimeAverages(
    params: GetCalendarAppChartAllTimeAveragesRequestDto
  ): Promise<GetCalendarChartAllTimeAveragesResponseDto> {
    this.logManager.debug('called getSelfCalendarAppChartAllTimeAverages');
    try {
      const analyticsEventPort = new AnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const entityMapper = new CalendarChartAllTimeAveragesMapper();
      const aggregateRootMapper =
        new CalendarChartAllTimeAveragesAggregateRootMapper();
      const service = new GetCalendarAppChartAllTimeAveragesService(
        this.logManager,
        analyticsEventPort,
        entityMapper
      );
      const query = new GetCalendarAppChartAllTimeAveragesQuery(
        this.logManager,
        service
      );
      const controller = new GetCalendarAppChartAllTimeAveragesController(
        this.logManager,
        query,
        aggregateRootMapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetSelfCalendarAppChartAllTimeAverages encountered an error',
        error
      );
    }
  }

  async getSelfCalendarAppTimeSeriesMetricsSummary(
    params: GetCalendarAppTimeSeriesMetricsRequestDto
  ): Promise<GetSelfCalendarAppTimeSeriesMetricsSummaryResponseDto> {
    this.logManager.debug('called getSelfCalendarAppTimeSeriesMetricsSummary');
    try {
      const entityMapper = new CalendarAppTimeSeriesMetricsSummaryMapper();
      const mapper = new CalendarTimeSeriesMetricsSummaryAggregateRootMapper();
      const service = new GetCalendarAppTimeSeriesMetricsSummaryService(
        this.logManager,
        this.adapter,
        entityMapper
      );

      const query = new GetCalendarAppTimeSeriesMetricsSummaryQuery(
        this.logManager,
        service
      );

      const controller = new GetCalendarAppTimeSeriesMetricsSummaryController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetCalendarAppTimeSeriesMetricsController encountered an error',
        error
      );
    }
  }

  async getSelfCalendarScoreTimeSeriesMetricsSummary(
    props: GetSelfCalendarScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetSelfCalendarScoreTimeSeriesMetricsSummaryResponseDto> {
    this.logManager.debug(
      'called getSelfCalendarScoreTimeSeriesMetricsSummary'
    );
    try {
      const entityMapper =
        new SelfCalendarScoreTimeSeriesMetricsSummaryMapper();
      const aggregateMapper =
        new CalendarTimeSeriesMetricsSummaryAggregateRootMapper();

      const granularityService =
        new GetSelfCalendarScoreTimeSeriesMetricsSummaryService(
          this.logManager,
          this.adapter,
          entityMapper
        );
      const granularityQuery =
        new GetSelfCalendarScoreTimeSeriesMetricsSummaryQuery(
          this.logManager,
          granularityService
        );

      const controller =
        new GetSelfCalendarScoreTimeSeriesMetricsSummaryController(
          this.logManager,
          granularityQuery,
          aggregateMapper
        );

      return await controller.get(props);
    } catch (error) {
      this.logManager.error(
        'GetSelfCalendarScoreTimeSeriesMetricsSummaryController encountered an error',
        error
      );
    }
  }

  async getAppMetricsTotalsIndividual(
    params: GetAppMetricsTotalsIndividualRequestDto
  ): Promise<GetAppMetricsTotalsIndividualResponseDto> {
    this.logManager.debug('called getAppMetricsTotalsIndividual');
    try {
      const mapper = new AppMetricsTotalIndividualMapper();
      const service = new GetAppMetricsTotalIndividualService(
        this.logManager,
        this.adapter,
        mapper
      );
      const query = new GetAppMetricsTotalIndividualQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsTotalIndividualController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTotalsIndividual encountered an error',
        error
      );
    }
  }
  async getAppMetricsTotalsWholeTeam(
    params: GetAppMetricsTotalsWholeTeamRequestDto
  ): Promise<GetAppMetricsTotalsWholeTeamResponseDto> {
    this.logManager.debug('called getAppMetricsTotalsWholeTeam');
    try {
      const mapper = new AppMetricsTotalWholeTeamMapper();
      const service = new GetAppMetricsTotalWholeTeamService(
        this.logManager,
        this.adapter,
        mapper
      );
      const query = new GetAppMetricsTotalWholeTeamQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsTotalWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTotalsWholeTeam encountered an error',
        error
      );
    }
  }

  async getCategoryMetricsAverageWholeTeam(
    params: GetCategoryMetricsAverageWholeTeamRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto> {
    this.logManager.debug('called getCategoryMetricsTotalWholeTeam');
    try {
      const mapper =
        new CategoryMetricsAverageWholeTeamParentAggregateRootMapper();
      const domainService = new GetCategoryMetricsAverageWholeTeamDomainService(
        this.logManager
      );
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const service = new GetCategoryMetricsAverageWholeTeamApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetCategoryMetricsAverageWholeTeamQuery(
        this.logManager,
        service
      );
      const controller = new GetCategoryMetricsAverageWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getCategoryMetricsAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getCategoryMetricsTotalWholeTeam(
    params: GetCategoryMetricsTotalsWholeTeamRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto> {
    this.logManager.debug('called getCategoryMetricsTotalWholeTeam');
    try {
      const mapper =
        new CategoryMetricsTotalWholeTeamParentAggregateRootMapper();
      const domainService = new GetCategoryMetricsTotalWholeTeamDomainService(
        this.logManager
      );
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const service = new GetCategoryMetricsTotalWholeTeamApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetCategoryMetricsTotalWholeTeamQuery(
        this.logManager,
        service
      );
      const controller = new GetCategoryMetricsTotalWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getCategoryMetricsTotalWholeTeam encountered an error',
        error
      );
    }
  }
  async getCategoryMetricsTotalIndividual(
    params: GetCategoryMetricsTotalsIndividualRequestDto
  ): Promise<GetCategoryMetricsTotalsResponseDto> {
    this.logManager.debug('called getCategoryMetricsTotalIndividual');
    try {
      const mapper =
        new CategoryMetricsTotalIndividualParentAggregateRootMapper();
      const domainService = new GetCategoryMetricsTotalIndividualDomainService(
        this.logManager
      );
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const service = new GetCategoryMetricsTotalIndividualApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetCategoryMetricsTotalIndividualQuery(
        this.logManager,
        service
      );
      const controller = new GetCategoryMetricsTotalIndividualController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getCategoryMetricsTotalIndividual encountered an error',
        error
      );
    }
  }
  async getAppMetricsAverageWholeTeam(
    params: GetAppMetricsAverageWholeTeamRequestDto
  ): Promise<GetAppMetricsAverageWholeTeamResponseDto> {
    this.logManager.debug('called getAppMetricsAveragesWholeTeam');
    try {
      const mapper = new AppMetricsAverageWholeTeamAggregateRootMapper();
      const domainService = new GetAppMetricsAverageWholeTeamDomainService(
        this.logManager
      );
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);
      const service = new GetAppMetricsAverageWholeTeamApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetAppMetricsAverageWholeTeamQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsAverageWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsAveragesWholeTeam encountered an error',
        error
      );
    }
  }
  async getAppMetricsTimeSeriesTotalsIndividual(
    params: GetAppMetricsTimeSeriesTotalsIndividualRequestDto
  ): Promise<GetAppMetricsTimeSeriesTotalsIndividualResponseDto[]> {
    this.logManager.debug('called getAppMetricsTimeSeriesTotalsIndividual');
    try {
      const mapper = new AppMetricsTimeSeriesTotalIndividualMapper();
      const service = new GetAppMetricsTimeSeriesTotalIndividualService(
        this.logManager,
        this.adapter,
        mapper
      );
      const query = new GetAppMetricsTimeSeriesTotalIndividualQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsTimeSeriesTotalIndividualController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTimeSeriesTotalsIndividual encountered an error',
        error
      );
    }
  }
  async getAppMetricsTimeSeriesTotalsWholeTeam(
    params: GetAppMetricsTimeSeriesTotalsWholeTeamRequestDto
  ): Promise<GetAppMetricsTimeSeriesTotalsWholeTeamResponseDto[]> {
    this.logManager.debug('called getAppMetricsTimeSeriesTotalsWholeTeam');
    try {
      const mapper = new AppMetricsTimeSeriesTotalWholeTeamMapper();

      const configService = new ConfigService();
      configService.set(StaticEnvironmentKeysEnum.DEBUG, this.useLogger);
      configService.set(
        StaticEnvironmentKeysEnum.CUBEJS_API_ENDPOINT,
        StaticEnvironmentAdapter.cubejs_api_endpoint
      );

      const diLogManager = new DiLogManager(configService);
      const diCubeAdapter = new DiCubeAdapter(diLogManager, configService);

      const teamAnalyticsActivityPort = new TeamAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );

      const orgWorkingDaysPort = new OrgWorkingDaysPort(
        diLogManager,
        diCubeAdapter
      );

      const domainService =
        new GetAppMetricsTimeSeriesTotalWholeTeamDomainService(this.logManager);

      const service =
        new GetAppMetricsTimeSeriesTotalWholeTeamApplicationService(
          this.logManager,
          teamAnalyticsActivityPort,
          orgWorkingDaysPort,
          domainService
        );

      const query = new GetAppMetricsTimeSeriesTotalWholeTeamQuery(
        this.logManager,
        service
      );

      const controller = new GetAppMetricsTimeSeriesTotalWholeTeamController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTimeSeriesTotalsWholeTeam encountered an error',
        error
      );
    }
  }

  async getAppMetricsTimeSeriesAverageWholeTeam(
    params: GetAppMetricsTimeSeriesAverageWholeTeamRequestDto
  ): Promise<GetAppMetricsTimeSeriesAverageWholeTeamResponseDto[]> {
    this.logManager.debug('called getAppMetricsTimeSeriesAveragesWholeTeam');
    try {
      const mapper =
        new AppMetricsTimeSeriesAverageWholeTeamAggregateRootMapper();

      const configService = new ConfigService();
      configService.set(StaticEnvironmentKeysEnum.DEBUG, this.useLogger);
      configService.set(
        StaticEnvironmentKeysEnum.CUBEJS_API_ENDPOINT,
        StaticEnvironmentAdapter.cubejs_api_endpoint
      );

      const diLogManager = new DiLogManager(configService);
      const diCubeAdapter = new DiCubeAdapter(diLogManager, configService);

      const teamAnalyticsActivityPort = new TeamAnalyticsActivityPort(
        this.logManager,
        this.adapter
      );

      const orgWorkingDaysPort = new OrgWorkingDaysPort(
        diLogManager,
        diCubeAdapter
      );

      const domainService =
        new GetAppMetricsTimeSeriesAverageWholeTeamDomainService(
          this.logManager
        );

      const service =
        new GetAppMetricsTimeSeriesAverageWholeTeamApplicationService(
          this.logManager,
          teamAnalyticsActivityPort,
          orgWorkingDaysPort,
          domainService
        );

      const query = new GetAppMetricsTimeSeriesAverageWholeTeamQuery(
        this.logManager,
        service
      );

      const controller = new GetAppMetricsTimeSeriesAverageWholeTeamController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTimeSeriesAveragesWholeTeam encountered an error',
        error
      );
    }
  }

  async getAppMetricsTotalSummaryWholeTeam(
    params: GetAppMetricsTotalSummaryWholeTeamRequestDto
  ): Promise<GetAppMetricsTotalSummaryWholeTeamResponseArray> {
    this.logManager.debug('called getAppMetricsTotalSummaryWholeTeam');
    try {
      const mapper = new AppMetricsTotalSummaryWholeTeamMapper();
      const service = new GetAppMetricsTotalSummaryWholeTeamService(
        this.logManager,
        this.adapter,
        mapper
      );
      const query = new GetAppMetricsTotalSummaryWholeTeamQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsTotalSummaryWholeTeamController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTotalSummaryWholeTeam encountered an error',
        error
      );
    }
  }

  async getAppMetricsAverageSummaryWholeTeam(
    params: GetAppMetricsAverageSummaryWholeTeamRequestDto
  ): Promise<GetAppMetricsAverageSummaryWholeTeamResponseArray> {
    this.logManager.debug('called getAppMetricsAverageSummaryWholeTeam');
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetAppMetricsAverageSummaryWholeTeamDomainService(this.logManager);

      const service =
        new GetAppMetricsAverageSummaryWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetAppMetricsAverageSummaryWholeTeamQuery(
        this.logManager,
        service
      );

      const mapper = new AppMetricsAverageSummaryWholeTeamAggregateRootMapper();

      const controller = new GetAppMetricsAverageSummaryWholeTeamController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsAverageSummaryWholeTeam encountered an error',
        error
      );
    }
  }

  async getAppMetricsTotalSummaryIndividual(
    params: GetAppMetricsTotalSummaryIndividualRequestDto
  ): Promise<GetAppMetricsTotalSummaryIndividualResponseArray> {
    this.logManager.debug('called getAppMetricsTotalSummaryIndividual');
    try {
      const mapper = new AppMetricsTotalSummaryIndividualMapper();
      const service = new GetAppMetricsTotalSummaryIndividualService(
        this.logManager,
        this.adapter,
        mapper
      );
      const query = new GetAppMetricsTotalSummaryIndividualQuery(
        this.logManager,
        service
      );
      const controller = new GetAppMetricsTotalSummaryIndividualController(
        this.logManager,
        query,
        mapper
      );
      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'TeamMetricsController.getAppMetricsTotalSummaryIndividual encountered an error',
        error
      );
    }
  }

  async getSelfHeatmap(props: GetUserHeatmapProps): Promise<HeatMap> {
    this.logManager.debug('called getSelfHeatmap');
    try {
      const heatmapsReforgeRepository = new HeatmapRepository({
        repository: this.cubeRepository,
      });
      const result = await heatmapsReforgeRepository.getUserHeatmap(props);
      const hydratedResult = hydrateReforgedHeatmaps(result);
      const response: HeatMap = [];
      const userIds = [...new Set(hydratedResult.map((r) => r.userId))];
      userIds.map((userId) => {
        const matchingHeatmaps = hydratedResult.filter(
          (r) => r.userId === userId
        );
        const segments: HeatmapIntervalSegment[] = matchingHeatmaps
          .map((heatmap) =>
            HeatmapReforgeMapper.interfaceToPresenter({
              ...HeatmapReforgeMapper.domainToInterface(heatmap),
              startDate: new Date(props.params.startDate),
              endDate: new Date(props.params.endDate),
              timezone: props.params.timezone,
            })
          )
          .sort((a, b) => (a.localSegment > b.localSegment ? 1 : -1));
        response.push({ userId, segments });
      });
      return response;
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryHeatMapsError}: ${error}`);
    }
  }

  async getWorkspaceHeatmapV2(props: GetTeamHeatmapProps): Promise<HeatMap> {
    this.logManager.debug('called getWorkspaceHeatmapV2');
    try {
      const heatmapsReforgeRepository = new HeatmapRepository({
        repository: this.cubeRepository,
      });
      const result = await heatmapsReforgeRepository.getWorkspaceHeatmapV2(
        props
      );
      const hydratedResult = hydrateReforgedHeatmaps(
        result,
        props.params.userIds
      );
      const response: HeatMap = [];
      const userIds = [...new Set(hydratedResult.map((r) => r.userId))];
      userIds.map((userId) => {
        const matchingHeatmaps = hydratedResult.filter(
          (r) => r.userId === userId
        );
        const segments: HeatmapIntervalSegment[] = matchingHeatmaps
          .map((heatmap) =>
            HeatmapReforgeMapper.interfaceToPresenter({
              ...HeatmapReforgeMapper.domainToInterface(heatmap),
              startDate: new Date(props.params.startDate),
              endDate: new Date(props.params.endDate),
              timezone: props.params.timezone,
            })
          )
          .sort((a, b) => (a.localSegment > b.localSegment ? 1 : -1));
        response.push({ userId, segments });
      });
      return response;
    } catch (error) {
      throw new Error(`${QueryErrorMessages.QueryHeatMapsError}: ${error}`);
    }
  }

  async getTopAppsReportV2(
    params: GetTopAppsReportRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TopAppsReportV2ResponseDto[]> {
    this.logManager.debug('called getTopAppsReportV2');
    try {
      const topAppsRepositoryV2 = new TopAppsReportRepositoryV2({
        repository: this.cubeRepository,
      });
      const topAppsReport = await topAppsRepositoryV2.queryTopAppsReport(
        params,
        config
      );
      if (topAppsReport.length < 1) {
        this.logManager.debug('No reports to retrieve, returning early');
        return [];
      }
      const rollingAverages =
        await topAppsRepositoryV2.queryCumulativeAvgDurations(
          {
            appIds: topAppsReport.map((report) => report.appId),
            startTime: params.startDate,
            endTime: params.endDate,
            granularity: params.granularity,
          },
          config
        );
      return topAppsReport.map((report) => {
        let cumulativeResult = 0;
        const rollingAverageFilter = rollingAverages.filter(
          (avg) => avg.appId == report.appId
        );
        if (rollingAverageFilter.length > 0) {
          const rollingAverage = rollingAverageFilter[0];
          const rollingAverageValue =
            rollingAverage.totalDuration / rollingAverage.dayCount;
          cumulativeResult = report.totalDuration - rollingAverageValue;
        }
        const resultToMap: TopAppsReportV2Result = {
          ...report,
          cumulativeAverageDurationDifference: cumulativeResult,
        };
        return TopAppsReportMapperV2.domainToRest(resultToMap);
      });
    } catch (error) {
      this.logManager.debug('Caught error in getTopAppsReport', error);
      throw new Error(
        `${QueryErrorMessages.QuerygetTopAppsReportError}: ${error}`
      );
    }
  }

  /**
   * This function returns a variety of current and historical singular metrics for a given team and app
   * @param params
   * @param params.startDate {string} beginning of the period for latest metrics
   * @param params.endDate {string} ending of period for latest metrics
   * @param params.timezone {string} timezone of user
   * @param params.teamId {string} ID of team to query
   * @param params.appId {string} ID of app to query
   * @param params.frequency {CubeTimeDimensionGranularity | Weekdays} one of day, week, monday, tuesday, wednesday, thursday, friday, saturday, sunday
   * @param config
   * @param config.token {string} a jwt token
   * @returns a variety of current and historical singular metrics and trends
   */
  async getTeamAppMetricsV2(
    params: GetTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamAppMetricsResponseDto> {
    this.logManager.debug('called getTeamAppMetricsV2');
    const teamAnalyticsActivityRepo = new TeamAnalyticsActivityRepositoryV2({
      repository: this.cubeRepository,
    });

    if (params.frequency === CubeTimeDimensionGranularity.Week) {
      const [
        latestMetrics,
        allTimeAverageMetrics,
        weeklyMaxDuration,
        weeklyMaxCallDuration,
        weeklyMaxInteractions,
        weeklyMaxCallInteractions,
      ] = await Promise.all([
        teamAnalyticsActivityRepo.queryLatestTeamAppMetrics(params, config),
        teamAnalyticsActivityRepo.queryAllTimeAverageTeamAppMetrics(
          params,
          config
        ),
        teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
          params,
          config,
          ITeamAnalyticsActivityV2Enum.TotalDuration
        ),
        teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
          params,
          config,
          ITeamAnalyticsActivityV2Enum.TotalCallDuration
        ),
        teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
          params,
          config,
          ITeamAnalyticsActivityV2Enum.Count
        ),
        teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
          params,
          config,
          ITeamAnalyticsActivityV2Enum.CallCount
        ),
      ]);
      const weeklyMaxAvgInteractionDuration =
        weeklyMaxDuration > 0 && weeklyMaxInteractions > 0
          ? Math.round(weeklyMaxDuration / weeklyMaxInteractions)
          : 0;

      const weeklyMaxAvgCallInteractionDuration =
        weeklyMaxCallDuration > 0 && weeklyMaxCallInteractions > 0
          ? Math.round(weeklyMaxCallDuration / weeklyMaxCallInteractions)
          : 0;
      return mapTeamAppMetrics(
        latestMetrics,
        allTimeAverageMetrics,
        weeklyMaxDuration,
        weeklyMaxCallDuration,
        weeklyMaxInteractions,
        weeklyMaxCallInteractions,
        weeklyMaxAvgInteractionDuration,
        weeklyMaxAvgCallInteractionDuration
      );
    }

    const [
      dailyLatestMetrics,
      dailyAllTimeAverageMetrics,
      dailyWeekdayAverageMetrics,
      dailyMaxDuration,
      dailyMaxCallDuration,
      dailyMaxInteractions,
      dailyMaxCallInteractions,
    ] = await Promise.all([
      teamAnalyticsActivityRepo.queryLatestTeamAppMetrics(params, config),
      teamAnalyticsActivityRepo.queryAllTimeAverageTeamAppMetrics(
        params,
        config
      ),
      teamAnalyticsActivityRepo.queryAllTimeWeekdayAverageTeamAppMetrics(
        params,
        config
      ),
      teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
        params,
        config,
        ITeamAnalyticsActivityV2Enum.TotalDuration
      ),
      teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
        params,
        config,
        ITeamAnalyticsActivityV2Enum.TotalCallDuration
      ),
      teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
        params,
        config,
        ITeamAnalyticsActivityV2Enum.Count
      ),
      teamAnalyticsActivityRepo.queryAllTimeMaxAppMetric(
        params,
        config,
        ITeamAnalyticsActivityV2Enum.CallCount
      ),
    ]);

    const dailyMaxAvgInteractionDuration =
      dailyMaxDuration > 0 && dailyMaxInteractions > 0
        ? Math.round(dailyMaxDuration / dailyMaxInteractions)
        : 0;

    const dailyMaxAvgCallInteractionDuration =
      dailyMaxCallDuration > 0 && dailyMaxCallInteractions > 0
        ? Math.round(dailyMaxCallDuration / dailyMaxCallInteractions)
        : 0;

    return mapTeamAppMetrics(
      dailyLatestMetrics,
      dailyAllTimeAverageMetrics,
      dailyMaxDuration,
      dailyMaxCallDuration,
      dailyMaxInteractions,
      dailyMaxCallInteractions,
      dailyMaxAvgInteractionDuration,
      dailyMaxAvgCallInteractionDuration,
      dailyWeekdayAverageMetrics
    );
  }

  async getFilteredTeamAppMetricsV2(
    params: GetFilteredTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamAppMetricsV2ResponseDto> {
    this.logManager.debug('called getFilteredTeamAppMetricsV2');
    try {
      const teamAnalyticsActivityRepoV2 = new TeamAnalyticsActivityRepositoryV2(
        {
          repository: this.cubeRepository,
        }
      );
      const [latestMetrics, userCounts, totals] = await Promise.all([
        teamAnalyticsActivityRepoV2.queryLatestFilteredTeamAppMetrics(
          params,
          config
        ),
        teamAnalyticsActivityRepoV2.queryUserCount(params, config),
        teamAnalyticsActivityRepoV2.queryTeamTotalMetrics(params, config),
      ]);
      const completeMetrics: FilteredTeamAppMetricsV2Result[] =
        latestMetrics.map((partialApp) => {
          const userCountMatch = userCounts.find((userCountObject) => {
            const appIdsMatch = userCountObject.appId === partialApp.appId;

            const datesMatch = userCountObject.date === partialApp.date;

            return appIdsMatch && datesMatch;
          });
          return {
            ...partialApp,
            userCount: Number(userCountMatch.userCount),
          };
        });

      const metricsArray = completeMetrics.map((app) =>
        mapDailyFilteredTeamAppMetricsV2(app)
      );

      return {
        ...totals,
        appMetrics: metricsArray,
      };
    } catch (error) {
      this.logManager.debug('Caught error in filteredTeamMetrics', error);
      throw new Error(
        `${QueryErrorMessages.QueryFilteredTeamAppMetricsError}: ${error}`
      );
    }
  }

  async getFilteredTeamSubscriptionMetricsV2(
    params: GetFilteredTeamSubscriptionMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<FilteredTeamSubscriptionMetricsResponseDto[]> {
    this.logManager.debug('called getFilteredTeamSubscriptionMetricsV2');
    try {
      const teamAnalyticsActivityRepo = new TeamAnalyticsActivityRepositoryV2({
        repository: this.cubeRepository,
      });
      const latestMetrics =
        await teamAnalyticsActivityRepo.queryLatestFilteredTeamSubscriptionMetrics(
          params,
          config
        );
      const metricsArray = latestMetrics.map((app) =>
        mapDailyFilteredTeamSubscriptionMetrics(app)
      );

      return metricsArray;
    } catch (error) {
      this.logManager.debug(
        'Caught error in filteredTeamSubscriptionMetrics',
        error
      );
      throw new Error(
        `${QueryErrorMessages.QueryFilteredTeamSubscriptionMetricsError}: ${error}`
      );
    }
  }

  async getAllScoresV2(
    params: GetAllScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AllScoresResponseDto> {
    this.logManager.debug('called getAllScoresV2');
    try {
      const allScoresRepositoryV2 = new AllScoresRepositoryV2({
        repository: this.cubeRepository,
        debug: this.useLogger,
      });
      let promises = [];
      const basicAllScores = allScoresRepositoryV2.queryUserAllScores(
        params,
        config
      );
      const allTimeAverages = allScoresRepositoryV2.queryUserAvgScores(
        params,
        config
      );
      const weekdayAverages =
        allScoresRepositoryV2.queryPerWeekdayAverageAllScores(params, config);
      const allTimeMaxes = allScoresRepositoryV2.queryUserMaxScores(
        params,
        config
      );
      promises = [
        basicAllScores,
        allTimeAverages,
        weekdayAverages,
        allTimeMaxes,
      ];
      const results = await Promise.all(promises);
      if (results[0].length < 1) {
        this.logManager.debug('scores returned no results');
        return { hasData: false };
      }
      if (
        ![
          'screenTime',
          'focus',
          'digitalIntensity',
          'workingHours',
          'timeInCalls',
        ].every((v) => Object.keys(results[0][0]).includes(v))
      ) {
        this.logManager.debug('At least one of the scores is missing data');
        return { hasData: false };
      }
      const mappedReponse = mapAllScoresResultsToScore(
        results[0][0],
        results[1],
        results[2],
        results[3][0]
      );
      this.logManager.debug('getAllScoresV2 response', mappedReponse);

      return mappedReponse;
    } catch (error) {
      this.logManager.debug('Caught error in getAllScoresV2', error);
      throw new Error(`${QueryErrorMessages.QueryGetAllScoresError}: ${error}`);
    }
  }

  /**
   * This function returns a variety of current and historical singular metrics for a given team and app
   * @param params
   * @param params.startDate {string} beginning of the period for latest metrics
   * @param params.endDate {string} ending of period for latest metrics
   * @param params.timezone {string} timezone of user
   * @param params.teamId {string} ID of team to query
   * @param params.appId {string} ID of app to query
   * @param params.frequency {CubeTimeDimensionGranularity | Weekdays} one of day, week, monday, tuesday, wednesday, thursday, friday, saturday, sunday
   * @param params.userIds [string[]] an optional list of userIds to filter the per-user metrics by
   * @param config
   * @param config.token {string} a jwt token
   * @returns a variety of current and historical singular metrics and trends
   */

  async getTeamTimeSeriesAppMetricsV2(
    params: GetTeamAppMetricsRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TeamTimeSeriesAppMetricsResponseDto> {
    this.logManager.debug('called getTeamTimeSeriesAppMetricsV2');
    const teamAnalyticsActivityRepo = new TeamAnalyticsActivityRepositoryV2({
      repository: this.cubeRepository,
    });
    const [userData, teamAverageData, wholeTeamData, allTimeAverage] =
      await Promise.all([
        teamAnalyticsActivityRepo.queryTeamUserTimeSeriesAppMetrics(
          params,
          config
        ),
        teamAnalyticsActivityRepo.queryTeamAverageTimeSeriesAppMetrics(
          params,
          config
        ),
        teamAnalyticsActivityRepo.queryTeamTimeSeriesAppMetrics(params, config),
        teamAnalyticsActivityRepo.queryTeamTimeSeriesAllTimeAverageAppMetrics(
          params,
          config
        ),
      ]);

    return {
      userData,
      teamAverageData,
      wholeTeamData,
      allTimeAverage,
    };
  }

  async getScoreTimeSeriesV2(
    params: GetScoreTimeSeriesV2RequestParamsDto,
    config: CubeConfigDto
  ): Promise<TimeSeriesResponseDto> {
    try {
      const allScoresRepositoryV2 = new AllScoresRepositoryV2({
        repository: this.cubeRepository,
        debug: this.useLogger,
      });

      let granularity, weekday, metricsStartDate;
      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        granularity = CubeTimeDimensionGranularity.Day;
        weekday = params.frequency;
      } else {
        granularity = params.frequency;
        weekday = undefined;
      }

      if (
        params.frequency &&
        params.frequency === CubeTimeDimensionGranularity.Week
      ) {
        metricsStartDate = getFirstMondayString(params.endDate);
      } else {
        metricsStartDate = params.endDate;
      }

      const [
        timeSeriesData,
        scoreCumulativeAvg,
        scoreCumulativeWeekdayAvg,
        scoreMetrics,
        scoreMetricsMax,
      ] = await Promise.all([
        allScoresRepositoryV2.queryScoreTimeSeries(
          { ...params, granularity, weekday },
          config
        ),
        allScoresRepositoryV2.queryUserAvgScores(
          {
            startDate: params.startDate,
            endDate: params.endDate,
            granularity,
          },
          config
        ),
        allScoresRepositoryV2.queryPerWeekdayAverageAllScores(
          {
            startDate: params.startDate,
            endDate: params.endDate,
            granularity,
            weekday,
          },
          config
        ),
        allScoresRepositoryV2.queryUserAllScores(
          {
            startDate: metricsStartDate,
            endDate: params.endDate,
            granularity,
          },
          config
        ),
        allScoresRepositoryV2.queryUserMaxScores({ granularity }, config),
      ]);

      let validatedMetrics =
        scoreMetrics.length < 1
          ? {
              screenTime: 0,
              focus: 0,
              digitalIntensity: 0,
              workingHours: 0,
              timeInCalls: 0,
            }
          : scoreMetrics[0];

      const mappedReponse = mapTimeSeriesResultV2(timeSeriesData, params.score);
      this.logManager.debug('getScoreTimeSeriesV2 response', mappedReponse);

      const filledResponse = fillEmptyDatesForTimeSeries(
        params.startDate,
        params.endDate,
        granularity,
        mappedReponse,
        weekday
      );

      const response = mapToScoreTimeSeriesDetailedResultsV2(
        scoreCumulativeAvg,
        scoreCumulativeWeekdayAvg,
        validatedMetrics,
        scoreMetricsMax[0],
        filledResponse,
        params.score
      );

      return response;
    } catch (error) {
      this.logManager.debug('Caught error in getScoreTimeSeriesV2', error);
      throw new Error(`${QueryErrorMessages.QueryGetAllScoresError}: ${error}`);
    }
  }

  async getAppTimeSeriesV2(
    params: GetAppTimeSeriesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<TimeSeriesResponseDto> {
    this.logManager.debug('called getAppTimeSeriesV2');
    try {
      const topAppsRepositoryV2 = new TopAppsReportRepositoryV2({
        repository: this.cubeRepository,
      });

      let granularity, weekday;
      if (Object.values<string>(Weekdays).includes(params.frequency)) {
        granularity = CubeTimeDimensionGranularity.Day;
        weekday = params.frequency;
      } else {
        granularity = params.frequency;
        weekday = undefined;
      }

      const timeSeriesData = await topAppsRepositoryV2.queryAppTimeSeries(
        { ...params, granularity, weekday },
        config
      );

      const appCumulativeAvg =
        await topAppsRepositoryV2.queryCumulativeAvgDurations(
          {
            appIds: [params.appId],
            startTime: params.startDate,
            endTime: params.endDate,
            granularity,
          },
          config
        );

      const appCumulativeWeekdayAvg =
        await topAppsRepositoryV2.queryCumulativeAvgDurations(
          {
            appIds: [params.appId],
            startTime: params.startDate,
            endTime: params.endDate,
            granularity,
            currentWeekday: true,
          },
          config
        );

      const appMetrics = await topAppsRepositoryV2.queryAppMetrics(
        {
          appIds: [params.appId],
          endTime: params.endDate,
          granularity,
        },
        config
      );

      const appMetricsMax = await topAppsRepositoryV2.queryUserMaxAppMetrics(
        { appIds: [params.appId], granularity },
        config
      );

      const filledResponse = fillEmptyDatesForTimeSeries(
        params.startDate,
        params.endDate,
        granularity,
        mapAppTimeSeriesResult(timeSeriesData, params.metric),
        weekday
      );

      const response = mapToAppTimeSeriesDetailedResults(
        appCumulativeAvg[0],
        appCumulativeWeekdayAvg[0],
        appMetrics[0],
        appMetricsMax,
        filledResponse,
        params.metric,
        granularity
      );
      this.logManager.debug(
        'mapToAppTimeSeriesDetailedResults response',
        response
      );
      return response;
    } catch (error) {
      this.logManager.debug('Caught error in getAppTimeSeries', error);
      throw new Error(`${QueryErrorMessages.QueryTimeSeriesError}: ${error}`);
    }
  }

  async getScoreAveragesV2(
    params: GetScoreAveragesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<ScoreAveragesResponseDto> {
    this.logManager.debug('called getScoreAveragesV2');
    try {
      const allScoresRepositoryV2 = new AllScoresRepositoryV2({
        repository: this.cubeRepository,
        debug: this.useLogger,
      });
      const allTimeDailyAverages =
        await allScoresRepositoryV2.queryUserAvgScores(
          {
            startDate: params.endDate,
            endDate: params.endDate,
            score: params.score,
            granularity: CubeTimeDimensionGranularity.Day,
          },
          config
        );
      const allTimeWeeklyAverages =
        await allScoresRepositoryV2.queryUserAvgScores(
          {
            startDate: params.endDate,
            endDate: params.endDate,
            score: params.score,
            granularity: CubeTimeDimensionGranularity.Week,
          },
          config
        );
      const dailyAverage = allTimeDailyAverages[params.score] || 0;
      const weeklyAverage = allTimeWeeklyAverages[params.score] || 0;
      const durationScoreList = [
        CoreScoreNames['Digital Working hours'],
        CoreScoreNames.Screentime,
        CoreScoreNames['Time in Calls'],
      ];
      const formattedDailyAverage = durationScoreList.includes(params.score)
        ? formatDuration(dailyAverage)
        : dailyAverage.toFixed(0);
      const formattedWeeklyAverage = durationScoreList.includes(params.score)
        ? formatDuration(weeklyAverage)
        : weeklyAverage.toFixed(0);

      return {
        dailyAverage: Math.round(dailyAverage),
        weeklyAverage: Math.round(weeklyAverage),
        formattedDailyAverage: formattedDailyAverage,
        formattedWeeklyAverage: formattedWeeklyAverage,
      };
    } catch (error) {
      this.logManager.debug('Caught error in getScoreAveragesV2', error);
      throw new Error(
        `${QueryErrorMessages.QueryGetScoreAveragesError}: ${error}`
      );
    }
  }

  async getAppAveragesV2(
    params: GetAppAveragesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<AppAveragesResponseDto> {
    this.logManager.debug('called getAppAveragesV2');
    try {
      const topAppsRepositoryV2 = new TopAppsReportRepositoryV2({
        repository: this.cubeRepository,
      });
      const [cumulativeValues] =
        await topAppsRepositoryV2.queryCumulativeAvgDurations(
          {
            appIds: [params.appId],
            startTime: params.endDate,
            endTime: params.endDate,
          },
          config
        );

      return mapToAppAveragesResponse(cumulativeValues);
    } catch (error) {
      this.logManager.debug('Caught error in getAppAverages', error);
      throw new Error(
        `${QueryErrorMessages.QueryGetAppAveragesError}: ${error}`
      );
    }
  }

  // if appId is provided, scoreName is not provided
  async getGoalSummaryV3(
    params: GetGoalSummaryRequestParamsDto,
    config: CubeConfigDto
  ): Promise<GoalsReportResponseDto> {
    this.logManager.debug('called getGoalSummaryV3');
    try {
      let currentValue, isDuration, startDate;
      startDate =
        params.goal.period === CubeTimeDimensionGranularity.Week
          ? getFirstMondayString(params.goal.endDate)
          : params.goal.endDate;
      // if appId is provided, we need to query based on appId and calculate the deviation from the goal value
      // measure is a duration metric if it is TimeInApp or CallTimeInApp
      if (params.goal.appId) {
        const topAppsRepositoryV2 = new TopAppsReportRepositoryV2({
          repository: this.cubeRepository,
        });
        const [appMetrics] = await topAppsRepositoryV2.queryAppMetrics(
          {
            appIds: [params.goal.appId],
            granularity: params.goal.period,
            startTime: startDate,
            endTime: params.goal.endDate,
          },
          config
        );
        isDuration =
          params.goal.measure === AppGoalsCubeMetric.TimeInApp ||
          params.goal.measure === AppGoalsCubeMetric.CallTimeInApp
            ? true
            : false;
        const measureToValue = {
          [AppGoalsCubeMetric.TimeInApp]: appMetrics.duration,
          [AppGoalsCubeMetric.Interactions]: appMetrics.count,
          [AppGoalsCubeMetric.CallTimeInApp]: appMetrics.callDuration,
          [AppGoalsCubeMetric.CallInteractions]: appMetrics.callCount,
        };
        currentValue = measureToValue[params.goal.measure];
        // if scoreName is provided, we need to query the score and calculate the deviation from the goal value
      } else if (params.goal.scoreName) {
        const allScoresRepositoryV2 = new AllScoresRepositoryV2({
          repository: this.cubeRepository,
          debug: this.useLogger,
        });
        const [scoreMetrics] = await allScoresRepositoryV2.queryUserAllScores(
          {
            granularity: params.goal.period,
            startDate: startDate,
            endDate: params.goal.endDate,
          },
          config
        );
        if (!scoreMetrics) {
          currentValue = 0;
        } else {
          currentValue = scoreMetrics[params.goal.scoreName];
        }
        isDuration =
          params.goal.scoreName === CoreScoreNames.Screentime ||
          params.goal.scoreName === CoreScoreNames['Digital Working hours'] ||
          params.goal.scoreName === CoreScoreNames['Time in Calls']
            ? true
            : false;
      }
      const percentageDifference = calculateDeviation(
        currentValue,
        Number(params.goal.value)
      );
      return {
        formattedPercentageDifference:
          percentageDifference < 0
            ? `${Math.round(percentageDifference).toString()}%`
            : `+${Math.round(percentageDifference).toString()}%`,
        percentageDifference: Math.round(percentageDifference),
        currentValue: isDuration
          ? formatDuration(currentValue)
          : currentValue.toFixed(),
      };
    } catch (error) {
      this.logManager.debug('Caught error in getGoalSummaryV3', error);
      throw new Error(`${QueryErrorMessages.QueryGetGoalSummary}: ${error}`);
    }
  }

  async getGoalSummariesV3(
    params: GetGoalSummariesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<GoalsReportResponseDto[]> {
    this.logManager.debug('called getGoalSummariesV3');
    try {
      let goalsPromises: Promise<GoalsReportResponseDto>[] = [];
      for (const goal of params.goals) {
        goalsPromises.push(this.getGoalSummaryV3({ goal }, config));
      }
      const goalSummaries = await Promise.all(goalsPromises);
      return goalSummaries;
    } catch (error) {
      this.logManager.debug('Caught error in getGoalSummariesV3', error);
      throw new Error(`${QueryErrorMessages.QueryGetGoalSummary}: ${error}`);
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountMeetingSummary(
    input: GetAccountMeetingEventsSummaryRequestDto & UserCountInDateRangeProps
  ): Promise<GetAccountMeetingEventsSummaryResponseDto> {
    this.logManager.debug('called getAccountMeetingSummary');
    try {
      const aggregateRootMapper =
        new AccountMeetingSummaryAggregateRootMapper();

      const domainService = new GetAccountMeetingEventsSummaryDomainService(
        this.logManager
      );

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const applicationService =
        new GetAccountMeetingEventsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetAccountMeetingEventsSummaryQuery(
        this.logManager,
        applicationService
      );

      const controller = new GetAccountMeetingEventsSummaryController(
        this.logManager,
        query,
        aggregateRootMapper
      );
      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'getAccountMeetingSummary encountered an error',
        error
      );
    }
  }

  async getSelfGoalSummaries(
    params: GetSelfGoalSummariesRequestDto
  ): Promise<GetSelfGoalSummariesResponseDto> {
    this.logManager.debug('called getSelfGoalSummaries');
    try {
      // mappers
      const digitalIntensityScoreGoalMapper =
        new SelfDigitalIntensityScoreGoalMapper();
      const focusScoreGoalMapper = new SelfFocusScoreGoalMapper();
      const screentimeScoreGoalMapper = new SelfScreentimeScoreGoalMapper();
      const digitalWorkingHoursScoreGoalMapper =
        new SelfDigitalWorkingHoursScoreGoalMapper();
      const timeInCallsScoreGoalMapper = new SelfTimeInCallsScoreGoalMapper();
      const timeInMeetingsScoreGoalMapper =
        new SelfTimeInMeetingsScoreGoalMapper();
      const callTimeInAppGoalMapper = new SelfCallTimeInAppGoalMapper();
      const interactionsGoalMapper = new SelfInteractionsGoalMapper();
      const calendarTimeInAppGoalMapper = new SelfCalendarTimeInAppGoalMapper();
      const timeInAppGoalMapper = new SelfTimeInAppGoalMapper();

      // ports

      const analyticsEventPort = new AnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      // domain services
      const timeInMeetingsDomainService =
        new GetSelfTimeInMeetingsScoreGoalDomainService(this.logManager);

      // services
      const digitalIntensityScoreGoalService =
        new GetSelfDigitalIntensityScoreGoalService(
          this.logManager,
          this.adapter,
          digitalIntensityScoreGoalMapper
        );
      const focusScoreGoalService = new GetSelfFocusScoreGoalService(
        this.logManager,
        this.adapter,
        focusScoreGoalMapper
      );
      const screentimeScoreGoalService = new GetSelfScreentimeScoreGoalService(
        this.logManager,
        this.adapter,
        screentimeScoreGoalMapper
      );
      const digitalWorkingHoursScoreGoalService =
        new GetSelfDigitalWorkingHoursScoreGoalService(
          this.logManager,
          this.adapter,
          digitalWorkingHoursScoreGoalMapper
        );
      const timeInCallsScoreGoalService =
        new GetSelfTimeInCallsScoreGoalService(
          this.logManager,
          this.adapter,
          timeInCallsScoreGoalMapper
        );
      const timeInMeetingsScoreGoalService =
        new GetSelfTimeInMeetingsScoreGoalApplicationService(
          this.logManager,
          analyticsEventPort,
          timeInMeetingsDomainService
        );
      const callTimeInAppGoalService = new GetSelfCallTimeInAppGoalService(
        this.logManager,
        this.adapter,
        callTimeInAppGoalMapper
      );
      const interactionsGoalService = new GetSelfInteractionsGoalService(
        this.logManager,
        this.adapter,
        interactionsGoalMapper
      );
      const calendarTimeInAppGoalService =
        new GetSelfCalendarTimeInAppGoalService(
          this.logManager,
          this.adapter,
          calendarTimeInAppGoalMapper
        );
      const timeInAppGoalService = new GetSelfTimeInAppGoalService(
        this.logManager,
        this.adapter,
        timeInAppGoalMapper
      );

      // queries

      const digitalIntensityScoreGoalQuery =
        new GetSelfDigitalIntensityScoreGoalQuery(
          this.logManager,
          digitalIntensityScoreGoalService
        );
      const focusScoreGoalQuery = new GetSelfFocusScoreGoalQuery(
        this.logManager,
        focusScoreGoalService
      );
      const screentimeScoreGoalQuery = new GetSelfScreentimeScoreGoalQuery(
        this.logManager,
        screentimeScoreGoalService
      );
      const digitalWorkingHoursScoreGoalQuery =
        new GetSelfDigitalWorkingHoursScoreGoalQuery(
          this.logManager,
          digitalWorkingHoursScoreGoalService
        );
      const timeInCallsScoreGoalQuery = new GetSelfTimeInCallsScoreGoalQuery(
        this.logManager,
        timeInCallsScoreGoalService
      );
      const timeInMeetingsScoreGoalQuery =
        new GetSelfTimeInMeetingsScoreGoalQuery(
          this.logManager,
          timeInMeetingsScoreGoalService
        );
      const callTimeInAppGoalQuery = new GetSelfCallTimeInAppGoalQuery(
        this.logManager,
        callTimeInAppGoalService
      );
      const interactionsGoalQuery = new GetSelfInteractionsGoalQuery(
        this.logManager,
        interactionsGoalService
      );
      const calendarTimeInAppGoalQuery = new GetSelfCalendarTimeInAppGoalQuery(
        this.logManager,
        calendarTimeInAppGoalService
      );
      const timeInAppGoalQuery = new GetSelfTimeInAppGoalQuery(
        this.logManager,
        timeInAppGoalService
      );

      const presenter = new GetSelfGoalSummaryPresenter(
        this.logManager,
        calendarTimeInAppGoalMapper,
        callTimeInAppGoalMapper,
        digitalIntensityScoreGoalMapper,
        digitalWorkingHoursScoreGoalMapper,
        focusScoreGoalMapper,
        interactionsGoalMapper,
        screentimeScoreGoalMapper,
        timeInAppGoalMapper,
        timeInCallsScoreGoalMapper,
        timeInMeetingsScoreGoalMapper
      );

      const controller = new GetSelfGoalSummariesCompositeController(
        this.logManager,
        calendarTimeInAppGoalQuery,
        callTimeInAppGoalQuery,
        digitalIntensityScoreGoalQuery,
        digitalWorkingHoursScoreGoalQuery,
        focusScoreGoalQuery,
        interactionsGoalQuery,
        screentimeScoreGoalQuery,
        timeInAppGoalQuery,
        timeInCallsScoreGoalQuery,
        timeInMeetingsScoreGoalQuery,
        presenter
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error('getSelfGoalSummaries encountered an error', error);
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountCallsSummary(
    input: GetAccountCallsSummaryRequestDto & UserCountInDateRangeProps
  ): Promise<GetAccountCallsSummaryResponseDto> {
    this.logManager.debug('called getAccountCallsSummary');
    try {
      const aggregateRootMapper = new AccountCallsSummaryAggregateRootMapper();
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountCallsSummaryDomainService(
        this.logManager
      );
      const service = new GetAccountCallsSummaryApplicationService(
        this.logManager,
        port,
        domainService
      );
      const query = new GetAccountCallsSummaryQuery(this.logManager, service);
      const controller = new GetAccountCallsSummaryController(
        this.logManager,
        query,
        aggregateRootMapper
      );

      return await controller.get(input);
    } catch (error) {
      this.logManager.error(
        'GetAccountCallsSummaryController encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountMeetingTimeSeries(
    params: GetAccountMeetingEventsTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountMeetingEventsTimeSeriesResponseDto[]> {
    this.logManager.debug('called getAccountMeetingTimeSeries');
    try {
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );
      const domainService = new GetAccountEventsMeetingsTimeSeriesDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountMeetingEventsTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountMeetingEventsTimeSeriesQuery(
        this.logManager,
        applicationService
      );
      const aggregateRootMapper =
        new AccountMeetingTimeSeriesItemAggregateRootMapper();
      const controller = new GetAccountMeetingEventsTimeSeriesController(
        this.logManager,
        query,
        aggregateRootMapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMeetingEventsTimeSeriesController encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getAccountCallTimeSeries(
    params: GetAccountCallEventsTimeSeriesRequestDto & UserCountInDateRangeProps
  ): Promise<GetAccountCallEventsTimeSeriesResponseDto[]> {
    this.logManager.debug('called getAccountCallTimeSeries');
    try {
      const port = new OrgAnalyticsActivityPort(this.logManager, this.adapter);
      const domainService = new GetAccountEventsCallsTimeSeriesDomainService(
        this.logManager
      );
      const applicationService =
        new GetAccountCallEventsTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );
      const query = new GetAccountCallEventsTimeSeriesQuery(
        this.logManager,
        applicationService
      );
      const aggregateRootMapper =
        new AccountCallsTimeSeriesItemAggregateRootMapper();
      const controller = new GetAccountCallEventsTimeSeriesController(
        this.logManager,
        query,
        aggregateRootMapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountCallEventsTimeSeriesController encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getMeetingHoursBySizeTimeSeries(
    params: GetAccountMeetingHoursBySizeTimeSeriesRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountMeetingHoursBySizeTimeSeriesResponseDto[]> {
    this.logManager.debug('called getMeetingHoursBySizeTimeSeries');
    try {
      const aggregateRootMapper =
        new AccountMeetingHoursBySizeTimeSeriesAggregateRootMapper();

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetAccountMeetingHoursBySizeTimeSeriesDomainService(
          this.logManager
        );

      const applicationService =
        new GetAccountMeetingHoursBySizeTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetAccountMeetingHoursBySizeTimeSeriesQuery(
        this.logManager,
        applicationService
      );

      const controller = new GetAccountMeetingHoursBySizeTimeSeriesController(
        this.logManager,
        query,
        aggregateRootMapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMeetingHoursBySizeTimeSeriesController encountered an error',
        error
      );
    }
  }

  @GuardvalidateUserCount(DEFAULT_USER_COUNT_VALIDATION_VALUE)
  async getMeetingHoursBySizeSummary(
    params: GetAccountMeetingHoursBySizeSummaryRequestDto &
      UserCountInDateRangeProps
  ): Promise<GetAccountMeetingHoursBySizeSummaryResponseDto[]> {
    this.logManager.debug('called getMeetingHoursBySizeSummary');
    try {
      const aggregateRootMapper =
        new AccountMeetingHoursBySizeSummaryAggregateRootMapper();

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetAccountMeetingHoursBySizeSummaryDomainService(this.logManager);

      const applicationService =
        new GetAccountMeetingHoursBySizeSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetAccountMeetingHoursBySizeSummaryQuery(
        this.logManager,
        applicationService
      );

      const controller = new GetAccountMeetingHoursBySizeSummaryController(
        this.logManager,
        query,
        aggregateRootMapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'GetAccountMeetingHoursBySizeSummaryController encountered an error',
        error
      );
    }
  }

  expandAccountCallTimeSeries(
    params: ExpandAccountCallEventsTimeSeriesRequestDto
  ): GetAccountCallEventsTimeSeriesResponseDto[] {
    this.logManager.debug('called expandAccountCallTimeSeries');
    try {
      const domainService = new ExpandAccountCallEventsTimeSeriesDomainService(
        this.logManager
      );
      const command = new ExpandAccountCallsEventsTimeSeriesCommand(
        this.logManager,
        domainService
      );
      const aggregateRootMapper =
        new AccountCallsTimeSeriesItemAggregateRootMapper();
      const controller = new ExpandAccountCallsEventsTimeSeriesController(
        this.logManager,
        command,
        aggregateRootMapper
      );

      return controller.get(params);
    } catch (error) {
      this.logManager.error(
        'expandAccountCallTimeSeries encountered an error',
        error
      );
    }
  }

  async getSelfScoreAveragesSummary(
    params: GetSelfScoreAveragesSummaryRequestDto
  ): Promise<GetSelfScoreAveragesSummaryResponseDto> {
    this.logManager.debug('called getSelfScoreAveragesSummary');
    try {
      const workingSessionsPort = new WorkingSessionsPort(
        this.logManager,
        this.adapter
      );
      const analyticsEventPort = new AnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const timeInMeetingsDomainService =
        new GetSelfTimeInMeetingsScoreAveragesSummaryDomainService(
          this.logManager
        );

      const timeInCallsDomainService =
        new GetSelfTimeInCallsScoreAveragesSummaryDomainService(
          this.logManager
        );

      const digitalIntensityDomainService =
        new GetSelfDigitalIntensityScoreAveragesSummaryDomainService(
          this.logManager
        );

      const focusDomainService =
        new GetSelfFocusScoreAveragesSummaryDomainService(this.logManager);

      const screentimeDomainService =
        new GetSelfScreentimeScoreAveragesSummaryDomainService(this.logManager);

      const digitalWorkingHoursDomainService =
        new GetSelfDigitalWorkingHoursScoreAveragesSummaryDomainService(
          this.logManager
        );

      const timeInMeetingsApplicationService =
        new GetSelfTimeInMeetingsScoreAveragesSummaryApplicationService(
          this.logManager,
          analyticsEventPort,
          timeInMeetingsDomainService
        );

      const timeInCallsApplicationService =
        new GetSelfTimeInCallsScoreAveragesSummaryApplicationService(
          this.logManager,
          workingSessionsPort,
          timeInCallsDomainService
        );

      const digitalIntensityApplicationService =
        new GetSelfDigitalIntensityScoreAveragesSummaryApplicationService(
          this.logManager,
          workingSessionsPort,
          digitalIntensityDomainService
        );

      const focusApplicationService =
        new GetSelfFocusScoreAveragesSummaryApplicationService(
          this.logManager,
          workingSessionsPort,
          focusDomainService
        );

      const screentimeApplicationService =
        new GetSelfScreentimeScoreAveragesSummaryApplicationService(
          this.logManager,
          workingSessionsPort,
          screentimeDomainService
        );

      const digitalWorkingHoursApplicationService =
        new GetSelfDigitalWorkingHoursScoreAveragesSummaryApplicationService(
          this.logManager,
          workingSessionsPort,
          digitalWorkingHoursDomainService
        );

      const timeInMeetingsQuery =
        new GetSelfTimeInMeetingsScoreAveragesSummaryQuery(
          this.logManager,
          timeInMeetingsApplicationService
        );

      const timeInCallsQuery = new GetSelfTimeInCallsScoreAveragesSummaryQuery(
        this.logManager,
        timeInCallsApplicationService
      );

      const digitalIntensityQuery =
        new GetSelfDigitalIntensityScoreAveragesSummaryQuery(
          this.logManager,
          digitalIntensityApplicationService
        );

      const focusQuery = new GetSelfFocusScoreAveragesSummaryQuery(
        this.logManager,
        focusApplicationService
      );

      const screentimeQuery = new GetSelfScreentimeScoreAveragesSummaryQuery(
        this.logManager,
        screentimeApplicationService
      );

      const digitalWorkingHoursQuery =
        new GetSelfDigitalWorkingHoursScoreAveragesSummaryQuery(
          this.logManager,
          digitalWorkingHoursApplicationService
        );

      const timeInMeetingsScoreAveragesSummaryMapper =
        new SelfTimeInMeetingsScoreAveragesSummaryMapper();
      const timeInCallsScoreAveragesSummaryMapper =
        new SelfTimeInCallsScoreAveragesSummaryMapper();
      const digitalIntensityScoreAveragesSummaryMapper =
        new SelfDigitalIntensityScoreAveragesSummaryMapper();
      const focusScoreAveragesSummaryMapper =
        new SelfFocusScoreAveragesSummaryMapper();
      const screentimeScoreAveragesSummaryMapper =
        new SelfScreentimeScoreAveragesSummaryMapper();
      const digitalWorkingHoursScoreAveragesSummaryMapper =
        new SelfDigitalWorkingHoursScoreAveragesSummaryMapper();

      const presenter = new GetSelfScoreAveragesSummaryPresenter(
        this.logManager,
        timeInMeetingsScoreAveragesSummaryMapper,
        timeInCallsScoreAveragesSummaryMapper,
        digitalIntensityScoreAveragesSummaryMapper,
        focusScoreAveragesSummaryMapper,
        screentimeScoreAveragesSummaryMapper,
        digitalWorkingHoursScoreAveragesSummaryMapper
      );

      const controller = new GetSelfScoreAveragesSummaryCompositeController(
        this.logManager,
        timeInMeetingsQuery,
        timeInCallsQuery,
        digitalIntensityQuery,
        focusQuery,
        screentimeQuery,
        digitalWorkingHoursQuery,
        presenter
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfScoreAveragesSummary encountered an error',
        error
      );
    }
  }

  async getTeamMetricsMeetingsSummaryTotalWholeTeam(
    params: GetTeamMetricsMeetingsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto> {
    try {
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsSummaryTotalWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsSummaryTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsSummaryTotalWholeTeamAggregateRootMapper();

      const controller =
        new GetTeamMetricsMeetingsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsMeetingsSummaryAverageWholeTeam(
    params: GetTeamMetricsMeetingsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto> {
    try {
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsSummaryAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsSummaryAverageWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsSummaryAverageWholeTeamAggregateRootMapper();

      const controller =
        new GetTeamMetricsMeetingsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsMeetingsSummaryTotalsIndividual(
    params: GetTeamMetricsMeetingsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsMeetingsSummaryResponseDto> {
    try {
      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsSummaryTotalIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsSummaryTotalIndividualQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsSummaryTotalIndividualAggregateRootMapper();

      const controller =
        new GetTeamMetricsMeetingsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsSummaryTotalsIndividual encountered an error',
        error
      );
    }
  }

  async getMySessions(
    params: GetSelfWorkingSessionsRequestDto
  ): Promise<GetSelfWorkingSessionsResponseDto[]> {
    try {
      this.logManager.debug('called getMySessions');

      const port = new WorkingSessionsPort(this.logManager, this.adapter);

      const applicationService = new GetSelfWorkingSessionsApplicationService(
        this.logManager,
        port
      );

      const query = new GetSelfWorkingSessionsQuery(
        this.logManager,
        applicationService
      );

      const mapper = new SelfWorkingSessionsEntityMapper();

      const controller = new GetSelfWorkingSessionsController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error('getMySessions encountered an error', error);
      throw error;
    }
  }

  async getTeamMetricsMeetingsEventSummaryTotalWholeTeam(
    params: GetTeamMetricsMeetingsEventSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]> {
    try {
      this.logManager.debug(
        'called getTeamMetricsMeetingsEventSummaryTotalWholeTeam'
      );

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsEventSummaryTotalWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsEventSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsEventSummaryTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsEventSummaryTotalWholeTeamEntityMapper();

      const controller =
        new GetTeamMetricsMeetingsEventSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsEventSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsMeetingsEventSummaryAverageWholeTeam(
    params: GetTeamMetricsMeetingsEventSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]> {
    try {
      this.logManager.debug(
        'called getTeamMetricsMeetingsEventSummaryAverageWholeTeam'
      );

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsEventSummaryAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsEventSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsEventSummaryAverageWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsEventSummaryAverageWholeTeamEntityMapper();

      const controller =
        new GetTeamMetricsMeetingsEventSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsEventSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsMeetingsEventSummaryTotalIndividual(
    params: GetTeamMetricsMeetingsEventSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsMeetingsEventSummaryResponseDto[]> {
    try {
      this.logManager.debug(
        'called getTeamMetricsMeetingsEventSummaryTotalIndividual'
      );

      const port = new OrganizationAnalyticsEventsPort(
        this.logManager,
        this.adapter
      );

      const domainService =
        new GetTeamMetricsMeetingsEventSummaryTotalIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsMeetingsEventSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsMeetingsEventSummaryTotalIndividualQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsMeetingsEventSummaryTotalIndividualEntityMapper();

      const controller =
        new GetTeamMetricsMeetingsEventSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsMeetingsEventSummaryTotalIndividual encountered an error',
        error
      );
    }
  }

  async getTeamMetricsAppViewsSummaryTotalsWholeTeam(
    params: GetTeamMetricsAppViewsSummaryTotalWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getTeamMetricsAppViewsSummaryTotalsWholeTeam'
      );

      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsAppViewsSummaryTotalWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsAppViewsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsAppViewsSummaryTotalWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsAppViewsSummaryTotalWholeTeamAggregateRootMapper();

      const controller =
        new GetTeamMetricsAppViewsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppViewsSummaryTotalsWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsAppViewsSummaryAverageWholeTeam(
    params: GetTeamMetricsAppViewsSummaryAverageWholeTeamRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getTeamMetricsAppViewsSummaryAverageWholeTeam'
      );

      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsAppViewsSummaryAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsAppViewsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsAppViewsSummaryAverageWholeTeamQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsAppViewsSummaryAverageWholeTeamAggregateRootMapper();

      const controller =
        new GetTeamMetricsAppViewsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppViewsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamMetricsAppViewsSummaryTotalsIndividual(
    params: GetTeamMetricsAppViewsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamMetricsAppViewsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getTeamMetricsAppViewsSummaryTotalsIndividual'
      );

      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsAppViewsSummaryTotalIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsAppViewsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsAppViewsSummaryTotalIndividualQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsAppViewsSummaryTotalIndividualAggregateRootMapper();

      const controller =
        new GetTeamMetricsAppViewsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsAppViewsSummaryTotalsIndividual encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCalendarViewsSummary(
    params: GetTeamMetricsCalendarViewsSummaryRequestDto
  ): Promise<GetTeamMetricsCalendarViewsSummaryResponseDto> {
    try {
      this.logManager.debug('called getTeamMetricsCalendarViewsSummary');

      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService = new GetTeamMetricsCalendarViewsSummaryDomainService(
        this.logManager
      );

      const applicationService =
        new GetTeamMetricsCalendarViewsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarViewsSummaryQuery(
        this.logManager,
        applicationService
      );

      const mapper = new TeamMetricsCalendarViewsSummaryAggregateRootMapper();

      const controller = new GetTeamMetricsCalendarViewsSummaryController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarViewsSummary encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCalendarMeetingsSummary(
    params: GetTeamMetricsCalendarMeetingsSummaryRequestDto
  ): Promise<GetTeamMetricsCalendarMeetingsSummaryResponseDto> {
    try {
      this.logManager.debug('called getTeamMetricsCalendarMeetingsSummary');

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarMeetingsSummaryDomainService(this.logManager);

      const applicationService =
        new GetTeamMetricsCalendarMeetingsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarMeetingsSummaryQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsCalendarMeetingsSummaryAggregateRootMapper();

      const controller = new GetTeamMetricsCalendarMeetingsSummaryController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarMeetingsSummary encountered an error',
        error
      );
    }
  }

  async getTeamMetricsCalendarMeetingsSummaryIndividual(
    params: GetTeamMetricsCalendarMeetingsSummaryIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarMeetingsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getTeamMetricsCalendarMeetingsSummaryIndividual'
      );

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarMeetingsSummaryIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarMeetingsSummaryIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarMeetingsSummaryIndividualQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new TeamMetricsCalendarMeetingsSummaryAggregateRootMapper();

      const controller =
        new GetTeamMetricsCalendarMeetingsSummaryIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamMetricsCalendarMeetingsSummaryIndividual encountered an error',
        error
      );
    }
  }

  async getSelfAppCategoryScoreTimeSeriesMetricsSummary(
    params: GetSelfAppCategoryScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetSelfAppCategoryScoreTimeSeriesMetricsSummaryResponseDto> {
    try {
      this.logManager.debug(
        'called getSelfAppCategoryScoreTimeSeriesMetricsSummary'
      );

      const port = new AnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetSelfAppCategoryScoreTimeSeriesMetricsSummaryDomainService(
          this.logManager
        );

      const applicationService =
        new GetSelfAppCategoryScoreTimeSeriesMetricsSummaryApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetSelfAppCategoryScoreTimeSeriesMetricsSummaryQuery(
        this.logManager,
        applicationService
      );

      const mapper =
        new SelfAppCategoryScoreTimeSeriesMetricsSummaryAggregateRootMapper();

      const controller =
        new GetSelfAppCategoryScoreTimeSeriesMetricsSummaryController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfAppCategoryScoreTimeSeriesMetricsSummary encountered an error',
        error
      );
    }
  }

  async getWorkspaceCategoryMetricsUsagePerIndividualTimeSeries(
    params: GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesRequestDto
  ): Promise<GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesResponseDto> {
    this.logManager.debug(
      'called getWorkspaceCategoryMetricsUsagePerIndividualTimeSeries'
    );
    try {
      const port = new TeamAnalyticsActivityPort(this.logManager, this.adapter);

      const domainService =
        new GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesDomainService(
          this.logManager
        );

      const applicationService =
        new GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesQuery(
          this.logManager,
          applicationService
        );

      const mapper =
        new WorkspaceCategoryMetricsUsagePerIndividualTimeSeriesAggregateRootMapper();

      const controller =
        new GetWorkspaceCategoryMetricsUsagePerIndividualTimeSeriesController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getWorkspaceCategoryMetricsUsagePerIndividualTimeSeries encountered an error',
        error
      );
    }
  }

  async getSelfAppCategoryChartAllTimeAverage(
    params: GetSelfAppCategoryChartAllTimeAverageRequestDto
  ): Promise<GetSelfAppCategoryChartAllTimeAverageResponseDto> {
    this.logManager.debug('called getSelfAppCategoryChartAllTimeAverage');
    try {
      const mapper =
        new GetSelfAppCategoryChartAllTimeAverageAggregateRootMapper();

      const port = new AnalyticsActivityPort(this.logManager, this.adapter);
      const service =
        new GetSelfAppCategoryChartAllTimeAverageApplicationService(
          this.logManager,
          port
        );
      const query = new GetSelfAppCategoryChartAllTimeAveragesQuery(
        this.logManager,
        service
      );
      const controller = new GetSelfAppCategoryChartAllTimeAverageController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getSelfAppCategoryChartAllTimeAverage encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeriesMetricsSummary(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeam'
    );
    try {
      const mapper =
        new TeamCalendarScoreTimeSeriesMetricsSummaryAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeriesMetricsSummaryTotalWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeriesMetricsSummaryIndividual(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividual'
    );
    try {
      const mapper =
        new TeamCalendarScoreTimeSeriesMetricsSummaryAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeriesMetricsSummaryTotalIndividual encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeriesMetricsSummaryAverage(
    params: GetTeamCalendarScoreTimeSeriesMetricsSummaryRequestDto
  ): Promise<GetTeamCalendarScoreTimeSeriesMetricsSummaryResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeam'
    );
    try {
      const mapper =
        new TeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeriesMetricsSummaryAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeries(
    params: GetTeamMetricsCalendarScoreTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto> {
    this.logManager.debug('called getTeamCalendarScoreTimeSeries');
    try {
      const mapper =
        new TeamMetricsCalendarScoreTimeSeriesAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarScoreTimeSeriesDomainService(this.logManager);

      const applicationService =
        new GetTeamMetricsCalendarScoreTimeSeriesApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarScoreTimeSeriesQuery(
        this.logManager,
        applicationService
      );

      const controller = new GetTeamMetricsCalendarScoreTimeSeriesController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeries encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeriesIndividual(
    params: GetTeamMetricsCalendarScoreTimeSeriesIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto> {
    this.logManager.debug('called getTeamCalendarScoreTimeSeriesIndividual');
    try {
      const mapper =
        new TeamMetricsCalendarScoreTimeSeriesAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarScoreTimeSeriesIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarScoreTimeSeriesIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarScoreTimeSeriesIndividualQuery(
        this.logManager,
        applicationService
      );

      const controller =
        new GetTeamMetricsCalendarScoreTimeSeriesIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeriesIndividual encountered an error',
        error
      );
    }
  }

  async getTeamCalendarScoreTimeSeriesAverageWholeTeam(
    params: GetTeamMetricsCalendarScoreTimeSeriesRequestDto
  ): Promise<GetTeamMetricsCalendarScoreTimeSeriesResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarScoreTimeSeriesAverageWholeTeam'
    );
    try {
      const mapper =
        new TeamMetricsCalendarScoreTimeSeriesAverageWholeTeamAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);

      const domainService =
        new GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamMetricsCalendarScoreTimeSeriesAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarScoreTimeSeriesAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getTeamCalendarChartAllTimeAverages(
    params: GetTeamMetricsCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto> {
    this.logManager.debug('called getTeamCalendarChartAllTimeAverages');

    try {
      const mapper =
        new TeamMetricsCalendarChartAllTimeAveragesAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCalendarChartAllTimeAveragesDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarChartAllTimeAveragesApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new GetTeamMetricsCalendarChartAllTimeAveragesQuery(
        this.logManager,
        applicationService
      );

      const controller =
        new GetTeamMetricsCalendarChartAllTimeAveragesController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarChartAllTimeAverages encountered an error',
        error
      );
    }
  }

  async getTeamCalendarChartAllTimeAveragesIndividual(
    params: GetTeamMetricsCalendarChartAllTimeAveragesIndividualRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarChartAllTimeAveragesIndividual'
    );

    try {
      const mapper =
        new TeamMetricsCalendarChartAllTimeAveragesAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCalendarChartAllTimeAveragesIndividualDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarChartAllTimeAveragesIndividualApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamMetricsCalendarChartAllTimeAveragesIndividualQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamMetricsCalendarChartAllTimeAveragesIndividualController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarChartAllTimeAveragesIndividual encountered an error',
        error
      );
    }
  }

  async getTeamCalendarChartAllTimeAveragesAverageWholeTeam(
    params: GetTeamMetricsCalendarChartAllTimeAveragesRequestDto
  ): Promise<GetTeamMetricsCalendarChartAllTimeAveragesResponseDto> {
    this.logManager.debug(
      'called getTeamCalendarChartAllTimeAveragesAverageWholeTeam'
    );

    try {
      const mapper =
        new TeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamAggregateRootMapper();

      const port = new TeamAnalyticsEventsPort(this.logManager, this.adapter);
      const domainService =
        new GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamDomainService(
          this.logManager
        );

      const applicationService =
        new GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query =
        new GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamQuery(
          this.logManager,
          applicationService
        );

      const controller =
        new GetTeamMetricsCalendarChartAllTimeAveragesAverageWholeTeamController(
          this.logManager,
          query,
          mapper
        );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getTeamCalendarChartAllTimeAveragesAverageWholeTeam encountered an error',
        error
      );
    }
  }

  async getWorkingHoursByRangeDistribution(
    params: WorkingHoursByRangeDistributionRequestDto
  ): Promise<WorkingHoursByRangeDistributionResponseDto> {
    this.logManager.debug('called getWorkingHoursByRangeDistribution');

    try {
      const mapper = new WorkingHoursByRangeDistibutionAggregateRootMapper();

      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );

      const domainService = new WorkingHoursByRangeDistributionDomainService(
        this.logManager
      );

      const applicationService =
        new WorkingHoursByRangeDistributionApplicationService(
          this.logManager,
          port,
          domainService
        );

      const query = new WorkingHoursByRangeDistributionQuery(
        this.logManager,
        applicationService
      );

      const controller = new WorkingHoursByRangeDistributionController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error(
        'getWorkingHoursByRangeDistribution encountered an error',
        error
      );
    }
  }

  async getUserSessions(
    params: GetUserWorkingSessionsRequestDto
  ): Promise<GetUserWorkingSessionsResponseDto[]> {
    try {
      this.logManager.debug('called getUserSessions', JSON.stringify(params));

      const port = new OrganizationWorkingSessionsPort(
        this.logManager,
        this.adapter
      );

      const applicationService = new GetUserWorkingSessionsApplicationService(
        this.logManager,
        port
      );

      const query = new GetUserWorkingSessionsQuery(
        this.logManager,
        applicationService
      );

      const mapper = new OrgWorkingSessionsEntityMapper();

      const controller = new GetUserWorkingSessionsController(
        this.logManager,
        query,
        mapper
      );

      return await controller.get(params);
    } catch (error) {
      this.logManager.error('getMySessions encountered an error', error);
    }
  }
  /**
   * Get connection pool statistics
   */
  getPoolStats(): Record<string, any> {
    return this.adapter.getPoolStats();
  }

  /**
   * Close the connection pool
   */
  async close(): Promise<void> {
    await this.adapter.close();
  }
}
